// Package guide (the SO page's packing-plan card). Moved verbatim from
// pages/SalesOrderPage.jsx (2026-10-27); split into smaller files later.
import React, { useEffect, useState, useRef } from "react";
import { useOutlinePaths, OutlineOverlay } from '../../utils/packagingOutline.jsx';
import { assemblyCountFromPrimarySpace } from '../../utils/assemblyMath';
import SoFinalizeEmailDialog from '../../components/SoFinalizeEmailDialog.jsx';
import { desktopUi } from '../desktopUi';
import { identityUi } from './identityUi';
import { guideAllows } from './guideMode';
import { openPdfTab as openPdfTabShared } from '../../features/salesOrders/soPdf';
import { palletDisplayName as palletNameIn, palletSealState, boxActionState, boxLabel, boxContents, isTaskWipPallet, taskWipStation, taskWipHeader, holdingPalletText, isCarton, isNested, nestedBoxesOf, insideText } from '../../features/salesOrders/palletRules';
import { isHoldingPallet, planHasHoldingPallets } from '../../features/salesOrders/holdingPallets';
import { SOStagingOnWip, SOStagingAssignRow, PrepareShipment } from '../../components/salesOrders/PrepareShipment.jsx';
import {
  sealBox, sealPallet, shipPallet, stagePallet, confirmPickup, confirmPickupAll,
  reprintPalletLabels, reprintBoxLabel, reprintManifest, queueStatusLabel,
  promoteHoldingPallet, packFromHoldingPallet,
} from '../../features/salesOrders/palletActions';
import {
  shipAllReady, stageAllReady, printPartLabels, printAllPartLabels, reprintCombinedManifest, finalizeSentResult,
  postPlanEdit, manualAssign, createEmptyPallet, findPlanPallets,
  bulkReadyOffer, pickupAllOffer, finalizeBatchAvailable as finalizeBatchAvailableRule, sealedPallets, findablePallets,
} from '../../features/salesOrders/planActions';
import { loadOrderPlan } from '../../features/salesOrders/soData';
import { PartDetailModal, ProductIdLink } from '../../components/PartDetailModal.jsx';
import { planEditor, withEditor, withEditorQuery } from '../../features/salesOrders/planEdits';
import { detailToString } from '../../utils/apiError';
import { cellHeight, stackLayout, stackPhrase, zoneStackLayout } from '../../utils/stackLayout';
import PalletCheckLink from '../../features/palletCheck/PalletCheckLink.jsx';
import { useShippingSigningFlags } from '../shippingHub/signingFlags';
import LoadTruckModal from '../shippingHub/LoadTruckModal';
import { deliveryLoadsOn, deliveryWaitingPallets, loadTruckOrderFromPlan, startDeliveryQr } from '../shippingHub/deliverySigning';
import usePalletChecks from '../../features/palletCheck/usePalletChecks';
import { openProblemsFor } from '../../features/palletCheck/palletCheckData';
import { zoneUnits, zoneMoveQty, zoneMoveText } from '../../features/salesOrders/zoneUnits';

// --- Packaging Plan Inline Card ---
// Persistent per-SO packing plan (backed by jobq-api /packaging/plans/*).
// Draft → Committed; append-only revisions. Surfaces a pallet map + box
// list below the Documents row on SalesOrderPage.
const SO_PKG_COLOR_PALETTE = [
  'rgb(209, 227, 255)', 'rgb(219, 247, 209)', 'rgb(255, 222, 179)',
  'rgb(240, 204, 255)', 'rgb(255, 247, 191)', 'rgb(199, 250, 242)',
  'rgb(255, 204, 217)', 'rgb(219, 219, 235)',
];

const SO_BOX_SKU_COLOR = {
  'S-16763': '#4c6ef5',
  'S-4827':  '#12b886',
  'S-4994':  '#fd7e14',
  'S-16737': '#e64980',
  'S-4061':  '#7048e8',
};

const SO_DEFAULT_MAX_PALLET_WEIGHT = 4000;
// Default max pallet-direct stack height (inches) before the spread-layer
// packer spills onto another pallet. Mirrors packaging_guide
// DEFAULT_TARGET_STACK_HEIGHT_IN (12"): keeps low/medium-qty jobs consolidated
// on one pallet. Lower = flatter on more pallets; raise toward the 24" freight
// cap for fewer, taller pallets.
const SO_DEFAULT_TARGET_STACK_HEIGHT = 12;

// The card's prompts when the caller passes no `ui` (the SO page): the desktop's dialogs, with every identity PIN
// prompt asking (never the admin session's PIN; a face may answer) and approval prompts as typed (identityUi.js).
const DEFAULT_GUIDE_UI = identityUi(desktopUi);

// `ui` (optional): desktopUi's shape + an optional identity(why) -> Promise<token|null> (the Shipping hub's
// Working-as session). Without it, DEFAULT_GUIDE_UI.
// `mode`: 'office' (the SO page: every tool, as before) or 'operator' (the Shipping hub: the floor's actions; the
// office tools appear after "Office tools" = an Admin PIN, kept by this card only). guideMode.js lists which is which.
// `signedPickup` (the Shipping hub with GET /features shipping_signing.pickup on; operator mode only):
// ({plan, palletIds, setBusy}) -> Promise<{done, message}>: Confirm Pickup / Customer here start a pickup the customer
// signs on a phone (features/shippingHub/pickupSigning) instead of confirming it with the operator's PIN alone.
const SOPackagingPlanCard = ({ salesOrderId, apiBase, focusPalletId = null, mode = 'office', ui: uiProp = null, signedPickup = null }) => {
  const ui = uiProp || DEFAULT_GUIDE_UI;
  const isOperator = mode === 'operator';
  const [officeUnlocked, setOfficeUnlocked] = useState(false);
  const can = (action) => guideAllows(mode, officeUnlocked, action);
  const [expanded, setExpanded] = useState(isOperator);
  // true until the first read answers, so the card never flashes "No packaging plan" before it loads
  const [loading, setLoading] = useState(Boolean(salesOrderId));
  const [busy, setBusy] = useState('');
  const [plan, setPlan] = useState(null);
  // GET /features shipping_signing (2026-10-28): with `delivery` on, a delivery order's Ship pallet / Ship all open
  // Load truck (the load + its signing QR) instead of the plain ship; off = exactly as before.
  const signing = useShippingSigningFlags();
  const [loadTruck, setLoadTruck] = useState(null); // {palletIds | null}: the Load truck dialog for these pallets
  // `error` is the card's one-line MESSAGE (an action's result, success or failure); a plan re-read never clears it.
  // `loadError` is the plan read's own failure.
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [maxPalletWeightInput, setMaxPalletWeightInput] = useState(String(SO_DEFAULT_MAX_PALLET_WEIGHT));
  // Max pallet-direct stack height (inches). Caps how tall loose stacks grow
  // before spilling onto another pallet. Mirrors packing_plans.target_stack_height_in.
  const [targetStackHeightInput, setTargetStackHeightInput] = useState(String(SO_DEFAULT_TARGET_STACK_HEIGHT));
  // When false: per-pallet /ship calls skip the customer email; the office
  // sends one consolidated email at SO Finalize. Mirrors packing_plans.notify_per_pallet.
  const [notifyPerPalletInput, setNotifyPerPalletInput] = useState(true);
  const [routedJobs, setRoutedJobs] = useState([]);
  // Viability check — populated when no plan exists, surfaces the missing-part-data
  // blockers that caused the SO-creation auto-plan hook to skip.
  const [viability, setViability] = useState(null);
  // Outline cache: fetches each unique outline_svg_path once and parses
  // the inner <path d="..."> values. Passed down so cells render the
  // outline as inline non-scaling-stroke paths (holes stay visible at
  // tiny cell sizes where <image href> strokes go sub-pixel).
  const outlineCache = useOutlinePaths(plan);
  // Open problems reported on the pallet check screen ({} when the call fails: the chip just never shows), for the
  // pallet cards and Prepare shipment's holding pallet cards; read while the card is open, as before.
  const palletChecks = usePalletChecks(expanded ? plan?.plan_id : null);
  // Resolve a pallet's stable global identifier (PLT-XXXX) from its
  // pallet_id. Used in confirm prompts, toast messages, and PDF
  // filenames so the operator sees the same label they'll see on the
  // physical pallet + every manifest / slip / radio call. Falls back
  // to the legacy P# ordinal only on pre-Apr-2026 rows that lack a
  // short_id. The handlers below close over `plan` so this just reads
  // current state without an extra prop / arg threading change.
  const palletDisplayName = (palletId) => palletNameIn(plan, palletId);
  const [showJobList, setShowJobList] = useState(false);
  // When set, shows the SOMovePalletModal for consolidating boxes from this
  // pallet onto another. Cleared on close / completion.
  const [moveModalSourcePallet, setMoveModalSourcePallet] = useState(null);
  // When set ({box, planId}), shows SONestBoxModal: put this box inside another order's carton.
  const [nestModal, setNestModal] = useState(null);
  // When set, shows the PartDetailModal. Threaded as onInspectPart through
  // every product_id render site so operators can click a part name
  // anywhere on the plan card and pull up its image + dims + process.
  const [inspectedPartId, setInspectedPartId] = useState(null);
  // Edit mode: operator-driven manual edits to the plan. When true, each
  // zone/box/pallet renders extra controls (move, assign, delete, create).
  // All edits hit /packaging/plans/{plan_id}/... endpoints introduced
  // in the Apr 21 migration; guardrails enforce Building-pallet only +
  // unsealed-box only so labels/manifests stay consistent.
  const [editMode, setEditMode] = useState(false);
  // Box SKU catalog — used by the "New box" dropdown to pick a carton.
  // Fetched once when edit mode is first enabled.
  const [boxCatalog, setBoxCatalog] = useState([]);
  // SO-Finalize Email modal state. Opens when the operator clicks Finalize
  // SO Shipment / Pickup, OR after a per-pallet ship/stage auto-advances
  // the plan to its terminal state. The modal owns its own preview-fetch
  // lifecycle; this state just controls which kind/PIN/preActionBits we
  // pass in.
  const [finalizeDialog, setFinalizeDialog] = useState({
    open: false,
    kind: 'shipping',
    pin: '',
    preActionBits: [],
    // Manifest for the batch that was just shipped/staged (the document the
    // customer email attached). The dialog offers it for printing so the
    // whole-order finalize manifest is not mistaken for "today's" (SO-1112,
    // 2026-09-16).
    batchManifestUrl: null,
    batchPalletLabels: [],
  });

  const openFinalizeDialog = ({ kind, pin, preActionBits, batchManifestUrl, batchPalletLabels }) => {
    setFinalizeDialog({
      open: true,
      kind: kind === 'pickup' ? 'pickup' : 'shipping',
      pin: pin || '',
      preActionBits: preActionBits || [],
      batchManifestUrl: batchManifestUrl || null,
      batchPalletLabels: batchPalletLabels || [],
    });
  };

  const handleFinalizeDialogClose = () => {
    setFinalizeDialog((prev) => ({ ...prev, open: false }));
  };

  // What the card says after the SO-Finalize email went, and the manifest it opens (features/salesOrders/planActions).
  const handleFinalizeDialogSent = async (responseBody, { notify }) => {
    const r = finalizeSentResult({
      apiBase, plan, kind: finalizeDialog.kind, preActionBits: finalizeDialog.preActionBits, responseBody, notify,
    });
    setError(r.message);
    if (r.document) {
      try {
        // manifest_url is batch-scoped (?groups=) on a batch finalize, so
        // the tab that opens matches the customer's email attachment.
        await openPdfTab(r.document.url, r.document.filename);
      } catch (_) { /* non-fatal */ }
    }
    setFinalizeDialog((prev) => ({ ...prev, open: false }));
    await loadPlan();
  };

  const loadPlan = React.useCallback(async () => {
    if (!salesOrderId) return;
    setLoading(true);
    setLoadError('');
    try {
      setPlan(await loadOrderPlan(salesOrderId)); // null when the order has no plan (404)
    } catch (err) {
      setLoadError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [salesOrderId]);

  // Bulk-action timeout recovery (ship-all / stage-all outrunning the gateway's 30s): features/salesOrders/planActions
  // (pollBulkPalletOutcome), shared with the phone package guide.

  // When no plan exists, fetch the viability check so we can surface any
  // blockers that stopped the SO-creation auto-plan hook from running.
  const loadViability = React.useCallback(async () => {
    if (!salesOrderId) return;
    try {
      const res = await fetch(`${apiBase}/sales_orders/${salesOrderId}/plan_viability`);
      if (!res.ok) { setViability(null); return; }
      setViability(await res.json());
    } catch (_) {
      setViability(null);
    }
  }, [salesOrderId, apiBase]);

  useEffect(() => {
    setPlan(null);
    setError('');
    setLoadError('');
    setOfficeUnlocked(false);
    setViability(null);
    setMaxPalletWeightInput(String(SO_DEFAULT_MAX_PALLET_WEIGHT));
    setTargetStackHeightInput(String(SO_DEFAULT_TARGET_STACK_HEIGHT));
    setNotifyPerPalletInput(true);
    if (salesOrderId) loadPlan();
  }, [salesOrderId, loadPlan]);

  useEffect(() => {
    if (!loading && !plan && salesOrderId) {
      loadViability();
    }
  }, [loading, plan, salesOrderId, loadViability]);

  // &pallet=<id> (a beacon's /s/plt- QR, 2026-10-22): open the card on that pallet once per link,
  // scroll to it and highlight it for a few seconds.
  const [highlightPalletId, setHighlightPalletId] = useState(null);
  const palletFocusRef = useRef(null);
  useEffect(() => {
    if (!focusPalletId || !plan || !Array.isArray(plan.pallets)) return;
    const key = `${salesOrderId}:${focusPalletId}`;
    if (palletFocusRef.current === key) return;
    if (!plan.pallets.some((p) => String(p.pallet_id) === String(focusPalletId))) return;
    palletFocusRef.current = key;
    setExpanded(true);
    setHighlightPalletId(Number(focusPalletId));
  }, [focusPalletId, plan, salesOrderId]);
  useEffect(() => {
    if (!highlightPalletId || !expanded) return undefined;
    const scroll = setTimeout(() => {
      const el = document.getElementById(`so-pallet-detail-${highlightPalletId}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    const clear = setTimeout(() => setHighlightPalletId(null), 4000);
    return () => { clearTimeout(scroll); clearTimeout(clear); };
  }, [highlightPalletId, expanded]);

  // When a plan loads, surface its stored max pallet weight + stack height
  useEffect(() => {
    if (plan && plan.max_pallet_weight_lbs != null) {
      setMaxPalletWeightInput(String(Math.round(plan.max_pallet_weight_lbs)));
    }
    if (plan && plan.target_stack_height_in != null) {
      setTargetStackHeightInput(String(plan.target_stack_height_in));
    }
  }, [plan]);

  // Fetch the routed-jobs list whenever the plan_id changes. Used by the
  // "Print slips" per-job table. Small query, cheap to call up-front.
  const loadRoutedJobs = React.useCallback(async (planId) => {
    if (!planId) return;
    try {
      const res = await fetch(`${apiBase}/packaging/plans/${planId}/jobs`);
      if (!res.ok) return;
      const data = await res.json();
      setRoutedJobs(data.jobs || []);
    } catch (_) {
      setRoutedJobs([]);
    }
  }, [apiBase]);

  useEffect(() => {
    if (plan && plan.plan_id) {
      loadRoutedJobs(plan.plan_id);
    } else {
      setRoutedJobs([]);
    }
  }, [plan && plan.plan_id, loadRoutedJobs]);

  // Fetch the box SKU catalog (5 active Uline boxes, seeded in
  // packaging_boxes). Used by the "New box" picker in edit mode.
  const loadBoxCatalog = React.useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/packaging/boxes`);
      if (!res.ok) return;
      const data = await res.json();
      setBoxCatalog(Array.isArray(data) ? data : (data.boxes || []));
    } catch (_) { /* non-fatal */ }
  }, [apiBase]);

  useEffect(() => {
    if (editMode && boxCatalog.length === 0) loadBoxCatalog();
  }, [editMode, boxCatalog.length, loadBoxCatalog]);

  // Generic POST-an-edit helper. Every manual-edit endpoint returns a
  // success payload or a 4xx with {detail}; we reload the plan on success
  // and surface the detail string on failure.
  const _postPlanEdit = React.useCallback((path, body, busyKey) => postPlanEdit({
    apiBase, path, body, setBusy: (on) => setBusy(on ? busyKey : ''), setMessage: setError, reloadPlan: loadPlan,
  }), [apiBase, loadPlan]);

  const handleMoveZone = React.useCallback(async (zoneId, destBoxId, qty) => {
    if (!plan) return;
    // When `qty` is supplied AND less than the zone's current planned_qty,
    // the backend treats this as a SPLIT move (a new zone with `qty`
    // pieces is created on the destination; the source zone keeps the
    // rest). Omit `qty` for the legacy whole-zone move.
    const body = { dest_box_id: destBoxId };
    if (Number.isFinite(qty) && qty > 0) {
      body.qty = qty;
    }
    return _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/zones/${zoneId}/move`,
      body,
      `move_zone_${zoneId}`,
    );
  }, [plan, _postPlanEdit]);

  const handleEditZoneQty = React.useCallback(async (zoneId, newPlannedQty) => {
    if (!plan) return;
    // PATCH verb — same underlying helper, different method.
    const busyKey = `edit_zone_${zoneId}`;
    setBusy(busyKey); setError('');
    try {
      const res = await fetch(
        `${apiBase}/packaging/plans/${plan.plan_id}/zones/${zoneId}/quantity`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(withEditor({ planned_qty: newPlannedQty })),
        },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      await loadPlan();
      return await res.json().catch(() => ({}));
    } catch (err) {
      setError(err.message || String(err));
      return null;
    } finally {
      setBusy('');
    }
  }, [plan, apiBase, loadPlan]);

  const handleDeleteZone = React.useCallback(async (zoneId, productId) => {
    if (!plan) return;
    const label = productId || `zone #${zoneId}`;
    if (!window.confirm(`Delete this zone (${label}) from the plan? The part will disappear from the box contents; if it was the only zone in the box, the box becomes Empty and deletable.`)) return;
    const busyKey = `del_zone_${zoneId}`;
    setBusy(busyKey); setError('');
    try {
      const res = await fetch(
        `${apiBase}/packaging/plans/${plan.plan_id}/zones/${zoneId}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(withEditor({})),
        },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      await loadPlan();
      return await res.json().catch(() => ({}));
    } catch (err) {
      setError(err.message || String(err));
      return null;
    } finally {
      setBusy('');
    }
  }, [plan, apiBase, loadPlan]);

  const handleDeleteEmptyBox = React.useCallback(async (boxId) => {
    if (!plan) return;
    return _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/boxes/${boxId}/delete-if-empty`,
      {},
      `del_box_${boxId}`,
    );
  }, [plan, _postPlanEdit]);

  const handleDeleteEmptyPallet = React.useCallback(async (palletId) => {
    if (!plan) return;
    if (!window.confirm('Delete this empty pallet? The plan will show one fewer pallet on the map.')) return;
    return _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/pallets/${palletId}/delete-if-empty`,
      {},
      `del_pallet_${palletId}`,
    );
  }, [plan, _postPlanEdit]);

  // Packing takes the pieces off the laser task's WIP pallet(s): planActions.manualAssign says how many, which pallet
  // emptied (its beacon is freed) and any step the job still owes the part.
  const handleManualAssign = React.useCallback(async (body) => {
    if (!plan) return undefined;
    return manualAssign({
      apiBase, plan, body,
      setBusy: (on) => setBusy(on ? `assign_${body.part_id}` : ''), setMessage: setError, reloadPlan: loadPlan,
    });
  }, [plan, apiBase, loadPlan]);

  // Clear WIP: the escape hatch for a laser task's WIP pallet whose pieces went somewhere JobQ
  // didn't record. Every stack goes to 0, the pallet ends and its beacon is freed.
  const handleClearWip = React.useCallback(async (palletId, label) => {
    if (!plan) return;
    const reason = window.prompt(
      `Clear WIP pallet ${label}?\n\nEvery stack on it goes to 0, the pallet ends and its beacon is freed. ` +
      'Use it only when the pieces went somewhere JobQ did not record (packed by hand, scrapped). ' +
      'To pack them, assign them from the Staging queue instead.\n\nWhy? (at least 5 characters)'
    );
    if (reason == null) return;
    if (reason.trim().length < 5) { setError('Clear WIP needs a reason of at least 5 characters. Nothing was changed.'); return; }
    const res = await _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/pallets/${palletId}/wip-clear`,
      { reason: reason.trim(), edited_by: null },
      `wip_clear_${palletId}`,
    );
    if (res) {
      const freed = (res.beacons_released || []).length ? ` Beacon ${res.beacons_released.join(', ')} freed: take it off.` : '';
      setError(`WIP pallet ${label} cleared.${freed}`);
    }
    return res;
  }, [plan, _postPlanEdit]);

  // Kit boxes: loose-ship assembly consumables (bolt-on slats, hardware) as a
  // pallet-scoped box/bundle covering N sets. Contents are snapshotted server-side.
  const handleCreateKitBox = React.useCallback(async (palletId, body) => {
    if (!plan) return;
    return _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/pallets/${palletId}/kit-boxes`,
      body,
      `kit_add_${palletId}`,
    );
  }, [plan, _postPlanEdit]);

  // Purchased items: standalone purchased-component SO lines (bought finished,
  // resold — no job, no zone) packed as box contents. The line may belong to
  // another open SO of the same customer (one carton for two POs).
  const handleAddPurchasedItem = React.useCallback(async (boxId, body) => {
    if (!plan) return;
    return _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/boxes/${boxId}/purchased-items`,
      body,
      `purchased_add_${boxId}`,
    );
  }, [plan, _postPlanEdit]);

  const handleDeletePurchasedItem = React.useCallback(async (itemId) => {
    if (!plan) return;
    if (!window.confirm('Remove this purchased item from the box? The pieces go back to "not in any box".')) return;
    setBusy(`purchased_del_${itemId}`); setError('');
    try {
      const res = await fetch(withEditorQuery(`${apiBase}/packaging/plans/${plan.plan_id}/purchased-items/${itemId}`), { method: 'DELETE' });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      await loadPlan();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  }, [plan, apiBase, loadPlan]);

  const handleDeleteKitBox = React.useCallback(async (kitBoxId) => {
    if (!plan) return;
    if (!window.confirm('Remove this kit box from the pallet? The loose items go back to "not in any kit box".')) return;
    setBusy(`kit_del_${kitBoxId}`); setError('');
    try {
      const res = await fetch(withEditorQuery(`${apiBase}/packaging/plans/${plan.plan_id}/kit-boxes/${kitBoxId}`), { method: 'DELETE' });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      await loadPlan();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  }, [plan, apiBase, loadPlan]);

  // Walk a Complete plan back to Fulfilling — the escape hatch for
  // late-arriving content (typically Staging-queue parts that finished
  // their secondary process after the last pallet was picked up). The
  // plan re-completes on its own once every pallet is staged + picked
  // up / shipped; so_finalized_at is preserved server-side, so the
  // combined-manifest reprint keeps working.
  const handleReopenPlan = React.useCallback(async () => {
    if (!plan) return;
    if (!window.confirm(
      'Reopen this completed plan?\n\nIt goes back to Fulfilling so you can add pallets and assign parts. It will complete itself again once every pallet is staged and picked up / shipped. The SO-finalize record and combined manifest are preserved.'
    )) return;
    return _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/reopen`,
      { notes: null },
      'reopen_plan',
    );
  }, [plan, _postPlanEdit]);

  // Mirror of the API's _detect_so_finalize_batch: pallets staged/shipped
  // AFTER the last SO finalize form a new batch the customer hasn't gotten
  // a consolidated notice for (typical after /reopen + re-stage). Re-arms
  // the Finalize button, which is otherwise hidden once so_finalized_at is
  // set — without this, closing the auto-opened dialog after a bulk stage
  // left no way back into the batch email.
  const finalizeBatchAvailable = React.useMemo(() => finalizeBatchAvailableRule(plan), [plan]);

  // Cross-SO ride-along assign for non-laser / oversized parts not yet on a
  // box. Creates a fresh planned_box on THIS plan with shipped_pallet_id
  // pointing at another SO's pallet (host). Source SO billing/yield/qty_shipped
  // attribution stays on this plan; the host pallet's seal/ship/pickup
  // advances this SO's Shipping tasks via the COALESCE(shipped_pallet_id,
  // pallet_id) ride-along read path. Same-customer guard enforced server-side.
  const handleRideAlongAssign = React.useCallback(async (body) => {
    if (!plan) return;
    return _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/ride-along-assign`,
      body,
      `ridealong_assign_${body.part_id}`,
    );
  }, [plan, _postPlanEdit]);

  // Cross-SO target list for manual-handling ride-along destinations. Same
  // endpoint the Move dialog uses; results are shared via prop down to
  // SOPlanManualHandling so it can render a "Ship on another SO's pallet"
  // option per row. Refetches when plan_id changes.
  const [planCrossSoTargets, setPlanCrossSoTargets] = React.useState([]);
  React.useEffect(() => {
    if (!plan?.plan_id) {
      setPlanCrossSoTargets([]);
      return;
    }
    let cancelled = false;
    fetch(`${apiBase}/packaging/plans/${plan.plan_id}/cross-so-move-targets`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => {
        if (cancelled) return;
        setPlanCrossSoTargets(data?.targets || []);
      })
      .catch(() => { /* non-fatal — manual-handling falls back to in-plan dest only */ });
    return () => { cancelled = true; };
  }, [apiBase, plan?.plan_id]);

  const handleCreateEmptyBox = React.useCallback(async (palletId, asPalletDirect, boxSku) => {
    if (!plan) return;
    return _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/boxes`,
      { dest_pallet_id: palletId, as_pallet_direct: !!asPalletDirect, box_sku: boxSku || null },
      `new_box_${palletId}`,
    );
  }, [plan, _postPlanEdit]);

  // sizeChoice = "40x48" | "48x96" | "48x120" | "60x120". Defaults to 40x48.
  // Defensive: if the React onClick accidentally passes us the event object
  // (legacy single-button caller), ignore it and fall through to default.
  const handleCreateEmptyPallet = React.useCallback(async (sizeChoice) => {
    if (!plan) return undefined;
    return createEmptyPallet({
      apiBase, plan, sizeChoice, setBusy: (on) => setBusy(on ? 'new_pallet' : ''), setMessage: setError, reloadPlan: loadPlan,
    });
  }, [plan, apiBase, loadPlan]);

  // Box-only shortcut: creates a phantom pallet + one empty box of the
  // chosen SKU in a single round-trip. Skips the old "make pallet → flip
  // container_type → add box" three-step dance. Backend forces 40×48
  // dims on the phantom pallet since there's no wood pallet underneath.
  const handleCreateBoxOnly = React.useCallback(async (boxSku) => {
    if (!plan || !boxSku) return;
    return _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/pallets`,
      { container_type: 'Box', box_sku: boxSku },
      'new_box_only',
    );
  }, [plan, _postPlanEdit]);

  // Convert a Building plan-pallet between 'Pallet' (default) and 'Box'
  // (single loose carton, no pallet). Drives manifest/label/email wording
  // via shipment_pallets.container_type at seal time. PATCH endpoint
  // (different verb than _postPlanEdit's hardcoded POST), inline implementation.
  const handleConvertContainerType = React.useCallback(async (palletId, palletNum, newType) => {
    if (!plan) return;
    const labelNew = newType === 'Box' ? 'a single loose box (no pallet)' : 'a palletized shipment';
    if (!window.confirm(`Convert Pallet ${palletDisplayName(palletId)} to ${labelNew}? Manifest, labels, and the customer email will reflect the new container type at seal time.`)) {
      return;
    }
    const busyKey = `container_${palletId}`;
    setBusy(busyKey); setError('');
    try {
      const res = await fetch(
        `${apiBase}/packaging/plans/${plan.plan_id}/pallets/${palletId}/container-type`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(withEditor({ container_type: newType })),
        },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      await loadPlan();
      return await res.json().catch(() => ({}));
    } catch (err) {
      setError(err.message || String(err));
      return null;
    } finally {
      setBusy('');
    }
  }, [plan, apiBase, loadPlan]);

  const handleRepackPositions = React.useCallback(async (palletId) => {
    if (!plan) return;
    return _postPlanEdit(
      `/packaging/plans/${plan.plan_id}/pallets/${palletId}/repack-positions`,
      {},
      `repack_${palletId}`,
    );
  }, [plan, _postPlanEdit]);

  // Operator-driven manual reposition (drag-and-drop). Body is the full
  // list of box positions for this pallet (only the ones that moved
  // — the backend filters no-ops anyway, but the UI sends only dirty
  // boxes to keep the payload small). Verb is PATCH, not POST, so use
  // a local fetch (the shared _postPlanEdit helper hard-codes POST).
  const handleSaveBoxPositions = React.useCallback(async (palletId, positions) => {
    if (!plan) return;
    const busyKey = `manual_pos_${palletId}`;
    setBusy(busyKey); setError('');
    try {
      const res = await fetch(
        `${apiBase}/packaging/plans/${plan.plan_id}/pallets/${palletId}/box-positions`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(withEditor({ positions })),
        },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        const detailText = typeof b.detail === 'string'
          ? b.detail
          : (b.detail?.error || b.detail?.hint || JSON.stringify(b.detail || {}));
        throw new Error(detailText || `HTTP ${res.status}`);
      }
      const out = await res.json().catch(() => ({}));
      await loadPlan();
      return out;
    } catch (err) {
      setError(err.message || String(err));
      return null;
    } finally {
      setBusy('');
    }
  }, [plan, apiBase, loadPlan]);

  const handleCreate = async () => {
    setBusy('create'); setError('');
    const parsed = parseFloat(maxPalletWeightInput);
    const maxWeightBody = Number.isFinite(parsed) && parsed > 0
      ? { max_pallet_weight_lbs: parsed }
      : {};
    const parsedHeight = parseFloat(targetStackHeightInput);
    const stackHeightBody = Number.isFinite(parsedHeight) && parsedHeight > 0
      ? { target_stack_height_in: parsedHeight }
      : {};
    try {
      const res = await fetch(`${apiBase}/packaging/plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sales_order_id: salesOrderId,
          ...maxWeightBody,
          ...stackHeightBody,
          notify_per_pallet: notifyPerPalletInput,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      setPlan(await res.json());
      setExpanded(true);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  };

  const handleCommit = async () => {
    if (!plan) return;
    setBusy('commit'); setError('');
    try {
      const res = await fetch(`${apiBase}/packaging/plans/${plan.plan_id}/commit`, { method: 'POST' });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      setPlan(await res.json());
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  };

  const handleRefresh = async () => {
    if (!plan) return;
    setBusy('refresh'); setError('');
    try {
      const res = await fetch(`${apiBase}/packaging/plans/${plan.plan_id}/refresh`, { method: 'POST' });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setPlan(body.plan);
      if (body.summary) {
        const s = body.summary;
        if (s.changed) {
          setError(`Revision ${s.revision}: +${s.new_boxes} box(es), +${s.new_pallets} pallet(s)`);
        } else {
          setError(`Already up-to-date (revision ${s.revision}).`);
        }
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  };

  const handleRegenerate = async () => {
    if (!plan) return;
    const ok = window.confirm(
      "Regenerate this plan with a fresh layout?\n\n" +
      "• Re-packs every part/assembly from CURRENT data (use after fixing " +
      "dims, thickness, or an assembly's Shipping Profile).\n" +
      "• Completed laser jobs re-route into the new layout automatically.\n" +
      "• Pallet/box numbers and PLT-/BOX- ids are reissued — any printed " +
      "routing slips refer to the old containers; reprint them after.\n\n" +
      "Blocked if anything is sealed, shipped, riding on another SO's pallet, " +
      "or manually placed/adjusted via Edit plan (manual placements can't be " +
      "reproduced automatically)."
    );
    if (!ok) return;
    setBusy('regenerate'); setError('');
    try {
      const res = await fetch(`${apiBase}/packaging/plans/${plan.plan_id}/regenerate`, { method: 'POST' });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setPlan(body.plan);
      const routed = body.backfill?.summary?.total_qty_routed ?? body.backfill?.total_qty_routed;
      setError(
        `Plan regenerated (new plan #${body.plan_id}).` +
        (routed != null ? ` Re-routed ${routed} pcs from completed jobs.` : '') +
        ' Reprint routing slips — container numbers changed.'
      );
    } catch (err) {
      // "failed" keyword keys the banner classifier to render danger-red.
      setError(`Regenerate failed: ${err.message || String(err)}`);
    } finally {
      setBusy('');
    }
  };

  const handlePrintAllSlips = async () => {
    if (!plan) return;
    setBusy('print_all'); setError('');
    try {
      const res = await fetch(`${apiBase}/packaging/plans/${plan.plan_id}/routing-slips.pdf`);
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Open in a new tab / trigger the print dialog
      const w = window.open(url, '_blank');
      if (w) {
        // Attempt auto-print once the PDF is loaded. Browsers may block
        // this; fall back is the user prints from the opened tab.
        w.addEventListener('load', () => {
          try { w.focus(); w.print(); } catch (_) { /* noop */ }
        });
      } else {
        // Popup blocked — offer direct download
        const a = document.createElement('a');
        a.href = url;
        a.download = `Packaging_${plan.order_number || 'Plan'}_Routing_Slips.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 20000);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  };

  const handlePrintJobSlip = async (jobMasterNum, laserTaskId) => {
    if (!laserTaskId) {
      setError(`Job ${jobMasterNum} has no laser task.`);
      return;
    }
    setBusy(`print_job_${laserTaskId}`); setError('');
    try {
      const res = await fetch(`${apiBase}/tasks/${laserTaskId}/laser_report_only`);
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) {
        w.addEventListener('load', () => {
          try { w.focus(); w.print(); } catch (_) { /* noop */ }
        });
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `Packaging_Job${jobMasterNum}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 20000);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  };

  const handleBackfill = async () => {
    if (!plan) return;
    if (!window.confirm(
      `Retroactively route every already-completed laser task that could fill this plan?\n\n` +
      `This includes:\n` +
      `  • Jobs on ${plan.order_number} itself\n` +
      `  • Jobs on OTHER sales orders that have surplus allocations pointing here\n\n` +
      `Safe to re-run: already-routed tasks are skipped.`
    )) return;
    setBusy('backfill'); setError('');
    try {
      const res = await fetch(`${apiBase}/packaging/plans/${plan.plan_id}/backfill`, { method: 'POST' });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setPlan(body.plan);
      const s = body.summary || {};
      const msg = `Backfill complete. Considered ${s.tasks_considered} task(s) → ` +
        `${s.tasks_routed} routed, ${s.tasks_idempotent} already routed. ` +
        `${s.total_placements} placements, ${s.total_qty_routed} pieces. ` +
        (s.total_unplaced ? `${s.total_unplaced} unplaced. ` : '') +
        (s.total_alloc_no_plan ? `${s.total_alloc_no_plan} to SOs w/o plans. ` : '') +
        (s.errors?.length ? `${s.errors.length} error(s).` : '');
      setError(msg);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  };

  const handleDelete = async () => {
    if (!plan) return;
    if (!window.confirm(`Delete this Draft plan for ${plan.order_number}?`)) return;
    setBusy('delete'); setError('');
    try {
      const res = await fetch(`${apiBase}/packaging/plans/${plan.plan_id}`, { method: 'DELETE' });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      setPlan(null);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  };

  // Fetch a PDF as blob and open it in a new tab with auto-print (features/salesOrders/soPdf.js).
  // Used by the Seal (pallet/box labels), Ship (manifest) and reprint flows.
  const openPdfTab = openPdfTabShared;

  // The pallet actions are features/salesOrders/palletActions.js (shared with the phone's order
  // screen); the card keeps its busy key and its one-line message. A successful action reloads the plan;
  // the message stays (loadPlan reports its own failure in loadError).
  const palletIo = (btnKey) => ({
    ui,
    setBusy: (on) => setBusy(on ? btnKey : ''),
    setMessage: setError,
    reloadPlan: loadPlan,
    openFinalize: openFinalizeDialog,
  });

  // Seal a single box from the plan. Box must be Full; operator seals
  // it the moment the last part drops in, then peels the printed label
  // and tapes it on before the next task prints a slip that lands in
  // that box (which would be blocked anyway once Sealed).
  const handleSealBox = (palletId, palletNum, boxId, boxLabel) =>
    sealBox({ plan, palletId, boxId, boxLabel, ...palletIo(`seal_box_${boxId}`) });

  // Seal a pallet: materialise shipment records, flip it Ready, open the pallet + stack labels.
  const handleSealPallet = (palletId) =>
    sealPallet({ plan, palletId, ...palletIo(`seal_${palletId}`) });

  // Prepare shipment (holding pallets): "Ship this pallet as it is" turns a holding pallet into the shipping pallet.
  const handlePromoteHoldingPallet = (palletId) =>
    promoteHoldingPallet({ plan, palletId, ...palletIo(`promote_${palletId}`) });

  // Prepare shipment's Pack into (a holding-pallet card): manual-assign naming the source; a 409 shows and re-reads.
  const handlePackFromHolding = (body) =>
    packFromHoldingPallet({ plan, body, ...palletIo(`assign_${body.part_id}`) });

  // A holding pallet's Find: light its beacon and show where it is (the plan's Find list, this pallet only).
  const handleFindPlanPallet = (palletId) => findPlanPallets({ plan, palletIds: [palletId], reloadPlan: loadPlan });

  // Unseal a Ready pallet — tears down the shipment_* rows so the pallet
  // flips back to Building and the operator can add boxes / move boxes onto
  // it, then re-seal. Labels already printed become stale (server returns
  // a warning the UI surfaces). Blocked after any task has shipped_date.
  const handleUnsealPallet = async (palletId, palletNum) => {
    if (!plan) return;
    if (!window.confirm(
      `Unseal Pallet ${palletDisplayName(palletId)} for ${plan.order_number}?\n\n` +
      `This will:\n` +
      `  • Remove the pallet + its boxes from the shipment system\n` +
      `  • Flip the pallet back to Building so you can add/move more boxes\n` +
      `  • Restore each box's status to Full/Partial based on contents\n\n` +
      `⚠️ Any pallet or box labels already printed become stale — shred them.\n` +
      `Not possible once the pallet has shipped.`
    )) return;
    const btnKey = `unseal_pallet_${palletId}`;
    setBusy(btnKey); setError('');
    try {
      const res = await fetch(
        `${apiBase}/packaging/plans/${plan.plan_id}/pallets/${palletId}/unseal`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(withEditor({ notes: null })),
        },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      const body = await res.json();
      if (body.already_unsealed) {
        setError(`Pallet ${palletDisplayName(palletId)} was already Building — nothing to do.`);
      } else {
        const parts = [`Pallet ${palletDisplayName(palletId)} unsealed`];
        if (body.boxes_unsealed) parts.push(`${body.boxes_unsealed} box(es) reverted`);
        if (body.pallet_direct_unsealed) parts.push(`${body.pallet_direct_unsealed} stack(s) reverted`);
        setError(parts.join(' · ') + ' — shred any printed labels for this pallet.');
      }
      await loadPlan();
    } catch (err) {
      setError(`Unseal Pallet ${palletDisplayName(palletId)} failed: ${err.message || String(err)}`);
    } finally {
      setBusy('');
    }
  };

  // Unseal a single Sealed box on a Building pallet — use when the operator
  // sealed one box prematurely but the pallet itself hasn't been finalized.
  // For a Ready pallet, unseal the whole pallet instead.
  const handleUnsealBox = async (palletId, palletNum, boxId, boxLabel) => {
    if (!plan) return;
    if (!window.confirm(
      `Unseal ${boxLabel} on Pallet ${palletDisplayName(palletId)}?\n\n` +
      `  • Removes ${boxLabel} from the shipment system\n` +
      `  • Flips the box back to Full/Partial so zones can be edited\n` +
      `  • If this was the only sealed box on the pallet, the shipment shell is cleared too\n\n` +
      `⚠️ The box label already printed becomes stale — shred it.`
    )) return;
    const btnKey = `unseal_box_${boxId}`;
    setBusy(btnKey); setError('');
    try {
      const res = await fetch(
        `${apiBase}/packaging/plans/${plan.plan_id}/pallets/${palletId}/boxes/${boxId}/unseal`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(withEditor({ notes: null })),
        },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      const body = await res.json();
      const parts = [`${boxLabel} unsealed on ${palletDisplayName(palletId)}`];
      if (body.shell_emptied) parts.push('pallet shipment shell cleared');
      setError(parts.join(' · ') + ' — shred the old box label.');
      await loadPlan();
    } catch (err) {
      setError(`Unseal ${boxLabel} failed: ${err.message || String(err)}`);
    } finally {
      setBusy('');
    }
  };

  // One carton, two orders (jobq-api 2026-11-04): a box can sit INSIDE another same-customer order's carton on the
  // same pallet. The API answers a labels_url to reprint (the carton count on the pallet changed); it is a path under
  // the API, opened the way the Move dialog opens dest_label_url.
  const apiUrl = (u) => (/^https?:/i.test(String(u)) ? u : `${apiBase}${u}`);
  const openNestLabels = async (labelsUrl, name) => {
    if (!labelsUrl) return;
    try {
      await openPdfTab(apiUrl(labelsUrl), `${name || 'Pallet'}_Labels.pdf`);
    } catch (_) { /* labels may pop up later, don't block */ }
  };

  // Take out: DELETE /packaging/plans/{plan_id}/boxes/{box_id}/nest (plan_id = this card's plan: the API takes the
  // guest's plan or the host's). The box is a plain rider box on the same pallet again.
  const handleUnnestBox = async (nested) => {
    if (!plan || !nested) return;
    const name = nested.short_id || boxLabel(nested);
    const host = nested.inside_box_short_id || 'its carton';
    if (!window.confirm(
      `Take ${name} out of ${host}?\n\n` +
      `  • ${name} stays on the same pallet as a box of its own (its order keeps its billing and shipping credit)\n` +
      `  • It gets its own place on the pallet map and its own label again\n\n` +
      `Reprint the labels after: the pallet's box count changed.`
    )) return;
    setBusy(`unnest_${nested.box_id}`); setError('');
    try {
      const res = await fetch(
        withEditorQuery(`${apiBase}/packaging/plans/${plan.plan_id}/boxes/${nested.box_id}/nest`),
        { method: 'DELETE' },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      const out = await res.json().catch(() => ({}));
      // its own page again + its carton's + any carton renumbered (box_ids), then the pallet label (its box count)
      await openNestLabels(out.labels_url, nested.inside_pallet_short_id || host);
      await openNestLabels(out.pallet_label_url, `${nested.inside_pallet_short_id || 'Pallet'}_pallet`);
      setError(`${name} taken out of ${host}.${out.labels_url ? ' Reprint the labels that opened.' : ''}`);
      await loadPlan();
    } catch (err) {
      setError(`Take ${name} out failed: ${err.message || String(err)}`);
    } finally {
      setBusy('');
    }
  };

  // Put inside a carton...: opens SONestBoxModal for this box (`planId` = the box's own plan: an incoming rider's
  // riding_from_plan_id, else this card's plan).
  const handleOpenNestModal = (box, planId) => setNestModal({ box, planId: planId || plan?.plan_id });
  const handleNested = async (result) => {
    const name = result.short_id || 'The box';
    const bits = [`${name} is inside ${result.host_short_id || 'the carton'} on ${result.pallet_short_id || 'its pallet'}`];
    if (result.moved_pallet) bits.push('moved onto that pallet');
    if (result.sealed_now) bits.push('sealed with the carton');
    const warn = (result.warnings || []).length ? ` ⚠ ${result.warnings.join('; ')}` : '';
    setNestModal(null);
    // the carton's page (+ every carton renumbered: box_ids), the pallet label when sealed (its box count changed),
    // and the box's old pallet's renumbered pages when it rode over
    await openNestLabels(result.labels_url, result.pallet_short_id);
    await openNestLabels(result.pallet_label_url, `${result.pallet_short_id || 'Pallet'}_pallet`);
    await openNestLabels(result.src_box_labels_url, 'Old_pallet_boxes');
    setError(`${bits.join(' · ')}.${result.labels_url ? ' Reprint the labels that opened.' : ''}${warn}`);
    await loadPlan();
  };

  // Ship a sealed pallet: prompt for PIN, finalize Shipping tasks, send the
  // customer notification email with the pallet manifest PDF, flip pallet
  // Shipped. If this was the last pallet on the plan, the plan auto-advances
  // to Complete and the SO-Finalize email dialog opens with the same PIN.
  const handleShipPallet = (palletId) => (deliveryLoadsOn(plan, signing)
    ? setLoadTruck({ palletIds: [palletId] })
    : shipPallet({ plan, palletId, ...palletIo(`ship_${palletId}`) }));

  // A delivery order's pallets that shipped with no signing code (a plain ship): start one / show it again.
  const handleDeliveryQr = async () => {
    if (!plan) return;
    setError('');
    const r = await startDeliveryQr({
      apiBase, ui, planId: plan.plan_id, orderNumber: plan.order_number, customerName: plan.customer_name,
      setBusy: (on) => setBusy(on ? 'delivery_qr' : ''),
    });
    if (r.message) setError(r.message);
    if (r.outcome) await loadPlan();
  };

  // Reprint the pallet+box labels for a pallet that's already been sealed (or shipped).
  const handleReprintLabels = (shipmentGroupId, palletNum) =>
    reprintPalletLabels({ shipmentGroupId, palletNum, ...palletIo(`reprint_labels_${shipmentGroupId}`) });

  // Reprint a single box's label (shipment_box_id from load_plan): the same PDF its seal opened.
  const handleReprintBoxLabel = (shipmentGroupId, shipmentBoxId, boxLabel) =>
    reprintBoxLabel({ shipmentGroupId, shipmentBoxId, boxLabel, ...palletIo(`reprint_box_${shipmentBoxId}`) });

  // Reprint the manifest for the ONE pallet whose card the operator clicked, scoped to that pallet's
  // shipment_group_id (a status filter collapsed multi-batch orders into one document: SO-1080).
  // The all-pallets / driver view stays on the Shipments-tab Manifest button and "Reprint combined
  // manifest". Legacy non-plan shipments use the per-pallet batch endpoint.
  const handleReprintManifest = (shipmentGroupId, palletNum) =>
    reprintManifest({ plan, shipmentGroupId, palletNum, ...palletIo(`reprint_manifest_${shipmentGroupId}`) });

  // Enqueue a current-state status label to the laser-Pi thermal printer
  // for a single container (pallet, box, or pallet-direct stack). Posts to
  // the same /print/queue used by the auto-fire on task complete; the
  // agent fetches the .pdf URL, sends to CUPS → ZP 505. If the PDF drain
  // times out, the agent falls back to the .epl endpoint inline.
  //
  // Routes to the laser-pi printer (NOT the browser print dialog) so the
  // operator can request a fresh "current state" label from anywhere in
  // the UI without worrying about which workstation they're standing at.
  // Status reflects live DB at the time of the print, so a Partial box
  // becomes Full → READY TO SEAL banner once the last laser cut lands.
  const _enqueueStatusLabel = (kind, idLabel, pdfPath) =>
    queueStatusLabel({ kind, idLabel, pdfPath, ...palletIo(`status_label_${kind}_${idLabel}`) });

  const handlePrintPalletStatusLabel = (palletId, palletShortId) => {
    return _enqueueStatusLabel(
      'pallet_status_label',
      palletShortId || `P${palletId}`,
      `/reports/pallet_status_label/${palletId}.pdf`,
    );
  };

  const handlePrintBoxStatusLabel = (boxId, boxShortId) => {
    return _enqueueStatusLabel(
      'box_status_label',
      boxShortId || `B${boxId}`,
      `/reports/box_status_label/${boxId}.pdf`,
    );
  };

  const handlePrintStackStatusLabel = (boxId, stackShortId) => {
    return _enqueueStatusLabel(
      'stack_status_label',
      stackShortId || `S${boxId}`,
      `/reports/stack_status_label/${boxId}.pdf`,
    );
  };

  // Print the rich part labels (image + material/finish/dimensions/weight/qty)
  // for one sealed/shipped pallet. Calls the legacy /reports/part_labels/batch
  // endpoint scoped to the pallet by ?pallet_number=N. The endpoint now
  // includes both pallet-direct (loose) items AND per-box items so the
  // packaging-plan flow gets full coverage.
  const handlePrintPartLabels = (shipmentGroupId, palletNum) =>
    printPartLabels({ apiBase, shipmentGroupId, palletNum, ...palletIo(`part_labels_${shipmentGroupId}_${palletNum}`) });

  // Print part labels for the WHOLE plan — every sealed pallet's worth, one PDF per shipment_group_id (each plan
  // pallet has its own group_id under per-box seal).
  const handlePrintAllPartLabels = () => printAllPartLabels({ apiBase, plan, ...palletIo('part_labels_all') });

  // Bulk ship all Ready pallets in one click. Per-pallet emails default
  // OFF (the typical follow-up is Finalize SO which sends one consolidated
  // email). Auto-advances the plan to Complete when the last pallet ships,
  // making the Finalize SO button appear immediately after.
  // Bulk-ship every Ready pallet AND, when the bulk action closes out the
  // plan (auto-advance to Complete), chain the SO-Finalize call so one
  // consolidated shipping notification email goes out without a second
  // PIN/click. Customer always gets exactly one email per SO. Per-pallet
  // emails are forced OFF — the consolidated email replaces them. If the
  // chained Finalize fails (transient network / SES hiccup), the bulk
  // action is still committed; the standalone "Finalize SO shipment"
  // button stays visible as a one-click retry.
  // The request, the load-out checklist, the timeout recovery and the finalize hand-off: planActions.shipAllReady.
  const handleShipAllReady = async (readyCount) => {
    if (!plan || !readyCount) return;
    if (deliveryLoadsOn(plan, signing)) { setLoadTruck({ palletIds: null }); return; }
    await shipAllReady({ apiBase, plan, salesOrderId, readyCount, ...palletIo('ship_all_ready') });
  };

  // SO-level Finalize: closes out a fully-shipped plan with one unified
  // manifest spanning every pallet, plus a consolidated customer email
  // composed in the SO-Finalize Email modal. The modal collects PIN +
  // recipient picks + editable subject/body, then POSTs to /finalize-so-shipment.
  // Idempotent server-side: re-running just reopens the same manifest URL.
  const handleFinalizeSO = () => {
    if (!plan) return;
    setError('');
    openFinalizeDialog({ kind: 'shipping', pin: '', preActionBits: [] });
  };

  // ---- Pickup workflow handlers (Apr 26 2026) -----------------------------
  // When the SO's fulfillment_type='pickup', the per-pallet button labeled
  // "Mark Ready for Pickup" calls /stage instead of /ship. Same task-finalize
  // work as ship; pallet lands in Staged instead of Shipped, plan auto-
  // advances to ReadyForPickup. Customer arrives → Confirm Pickup flips
  // pallet Staged → Shipped (no email, no qty work).

  const handleStagePallet = (palletId) =>
    stagePallet({ plan, palletId, ...palletIo(`stage_${palletId}`) });

  // Bulk-stage every Ready pallet AND, when the bulk action closes out
  // the plan (auto-advance to ReadyForPickup), chain the Finalize-SO-Pickup
  // call so one consolidated "Order Ready for Pickup" email goes out
  // without a second PIN/click. Customer always gets exactly one email
  // per SO. Per-pallet emails are forced OFF — the consolidated email
  // replaces them. If the chained Finalize fails (transient network /
  // SES hiccup), the bulk action is still committed; the standalone
  // "Finalize SO Pickup" button stays visible as a one-click retry.
  const handleStageAllReady = async (readyCount) => {
    if (!plan) return;
    await stageAllReady({ apiBase, plan, salesOrderId, readyCount, ...palletIo('stage_all_ready') });
  };

  // operator mode with customer signing on: the customer signs for the pallets (the hub's pickupSigning)
  const runSignedPickup = async (palletIds, btnKey) => {
    setError('');
    const r = await signedPickup({ plan, palletIds, setBusy: (on) => setBusy(on ? btnKey : '') });
    if (r && r.outcome) await loadPlan();
    if (r && r.message) setError(r.message);
    return r;
  };

  const handleConfirmPickup = (palletId) => (isOperator && signedPickup
    ? runSignedPickup([palletId], `confirm_pickup_${palletId}`)
    : confirmPickup({ plan, palletId, ...palletIo(`confirm_pickup_${palletId}`) }));

  const handleConfirmPickupAll = (stagedCount) => (isOperator && signedPickup
    ? runSignedPickup(null, 'confirm_pickup_all')
    : confirmPickupAll({ plan, stagedCount, ...palletIo('confirm_pickup_all') }));

  const handleFinalizeSoPickup = () => {
    if (!plan) return;
    setError('');
    openFinalizeDialog({ kind: 'pickup', pin: '', preActionBits: [] });
  };

  // Reprint the COMBINED shipment manifest (driver / pickup ops view).
  // Returns one merged PDF spanning every physical pallet this plan touches
  // — own staged/shipped pallets PLUS host pallets carrying outgoing
  // ride-alongs — with full pallet contents regardless of source SO and
  // per-box "From SO-XXXX" chips on multi-source pallets.
  //
  // The per-SO customer-facing manifest is still accessible from the
  // Shipments tab "Manifest" button above (scoped to this SO's items
  // with cross-SO footnotes).
  //
  // Read-only, no state changes. Available any time at least one
  // pallet has been sealed.
  const handleReprintSOManifest = () => reprintCombinedManifest({ apiBase, plan, ...palletIo('reprint_so_manifest') });

  // Header badge summarising plan state
  let headerBadge;
  if (!plan) {
    headerBadge = <span className="badge bg-secondary ms-2">No plan</span>;
  } else {
    // Cartons and stacks: a nested box is a section of its host carton, not one more box (palletRules.isCarton).
    const allUnits = (plan.pallets || []).flatMap(p => p.boxes || []).filter(b => !isNested(b));
    const totalBoxes = allUnits.length;
    const filled = allUnits.filter(b => b.status !== 'Empty').length;
    const nBox = allUnits.filter(isCarton).length;
    const nStk = allUnits.filter(b => b.is_pallet_direct).length;
    const countLabel = (() => {
      const parts = [];
      if (nBox) parts.push(`${nBox} box${nBox === 1 ? '' : 'es'}`);
      if (nStk) parts.push(`${nStk} stack${nStk === 1 ? '' : 's'}`);
      // "0 items" reads better than "0 boxes" for an empty plan that
      // could end up being any mix of cartons + pallet-direct stacks.
      return parts.join(' + ') || '0 items';
    })();
    const rev = `Rev ${plan.revision}`;
    if (plan.status === 'Draft') {
      headerBadge = <>
        <span className="badge bg-warning text-dark ms-2">Draft</span>
        <span className="badge bg-light text-dark ms-1">{rev} · {countLabel}</span>
      </>;
    } else if (plan.status === 'Complete') {
      headerBadge = <span className="badge bg-success ms-2">Complete</span>;
    } else if (plan.status === 'ReadyForPickup' && plan.fulfillment_type === 'pickup') {
      headerBadge = <>
        <span className="badge bg-info text-dark ms-2"><i className="bi bi-box-seam me-1"></i>Ready for Pickup</span>
        <span className="badge bg-light text-dark ms-1">{rev}</span>
      </>;
    } else {
      headerBadge = <>
        <span className="badge bg-primary ms-2">{plan.status}</span>
        <span className="badge bg-light text-dark ms-1">{rev} · {filled}/{totalBoxes} filled</span>
      </>;
    }
    // Add a customer-pickup chip whenever the SO is set up for pickup so
    // operators see the workflow at a glance even before staging starts.
    if (plan.fulfillment_type === 'pickup') {
      headerBadge = <>
        {headerBadge}
        <span className="badge bg-warning text-dark ms-1" title="This SO is configured for customer pickup. Pallets stage Ready→Staged→Shipped (Confirm Pickup) instead of Ready→Shipped.">
          <i className="bi bi-person me-1"></i>Customer Pickup
        </span>
      </>;
    } else if (plan.fulfillment_type === 'delivery') {
      headerBadge = <>
        {headerBadge}
        <span className="badge bg-secondary ms-1"><i className="bi bi-truck me-1"></i>We Deliver</span>
      </>;
    }
  }

  // Operator mode's "Office tools": an Admin PIN unlocks the office tools on this card only (never the app's admin
  // session). Loaded on demand: adminSession pulls in the phone dialogs.
  const handleOfficeTools = async () => {
    if (officeUnlocked) { setOfficeUnlocked(false); setEditMode(false); return; }
    const { requestAdminPin } = await import('../../adminSession.jsx');
    const admin = await requestAdminPin({ reason: 'The office tools need an Admin.' });
    if (admin) setOfficeUnlocked(true);
  };
  // Edit mode only while the edit tools can be shown (operator mode: after Office tools).
  const editModeOn = editMode && can('editPlan');

  return (
    <div className="card shadow-sm border-top-0 border-end-0 border-bottom-0 border-4 border-warning mt-1">
      <div
        className="card-header bg-white fw-bold d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <span>
          <i className="bi bi-box-seam-fill me-2 text-warning"></i>
          Packaging Plan
          {headerBadge}
          {loading && plan && (
            // a re-read while the plan is on screen: the body stays mounted (typed quantities, open rows), this says so
            <span className="spinner-border spinner-border-sm text-warning ms-2" role="status" aria-label="Re-reading the plan" data-testid="plan-refreshing"></span>
          )}
        </span>
        <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}></i>
      </div>
      {expanded && (
        <div className="card-body">
          {isOperator && (
            <div className="d-flex justify-content-end align-items-center gap-2 mb-2 small" data-testid="guide-office-tools">
              {officeUnlocked && <span className="badge bg-warning text-dark">Office tools on</span>}
              <button
                type="button"
                className={`btn btn-sm ${officeUnlocked ? 'btn-outline-secondary' : 'btn-outline-dark'}`}
                onClick={handleOfficeTools}
                disabled={!!busy}
                title={officeUnlocked
                  ? 'Hide the office tools again.'
                  : 'Plan changes (create, edit, refresh, unseal, move, reopen…) need an Admin PIN.'}
              >
                <i className={`bi ${officeUnlocked ? 'bi-lock' : 'bi-unlock'} me-1`}></i>
                {officeUnlocked ? 'Lock office tools' : 'Office tools'}
              </button>
            </div>
          )}
          {loading && !plan && <div className="text-center p-3"><div className="spinner-border spinner-border-sm text-warning"></div></div>}

          {loadError && !loading && (
            <div className="alert alert-danger py-2 small" role="alert" data-testid="plan-load-error">
              Couldn&apos;t read the packaging plan: {loadError}
            </div>
          )}

          {!loading && !plan && (
            <div className="text-center py-4">
              {viability && viability.blockers && viability.blockers.length > 0 && (
                <div className="alert alert-danger text-start mx-auto" style={{ maxWidth: 640 }}>
                  <div className="d-flex align-items-start">
                    <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
                    <div className="flex-grow-1">
                      <strong>Auto-plan blocked — part data incomplete.</strong>
                      <div className="small mt-1 mb-2">
                        A packaging plan could not be generated automatically because
                        the parts below are missing data the packer needs. Fix the
                        part records (or create a plan manually below) to proceed.
                      </div>
                      <ul className="small mb-0 ps-3">
                        {viability.blockers.map((b) => (
                          <li key={b.part_id}>
                            <ProductIdLink
                              productId={b.product_id || `Part ${b.part_id}`}
                              partId={b.part_id}
                              onInspect={setInspectedPartId}
                            />
                            {b.description ? ` — ${b.description}` : ''}
                            {': '}
                            <span className="text-danger">
                              missing {b.missing_fields.join(', ')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              <p className="text-muted mb-3">
                No packaging plan for this sales order. Generate one to see the
                pallet / box layout and enable auto-routing as laser jobs complete.
              </p>
              {can('createPlan') ? (
                <>
                <div className="d-inline-flex align-items-center gap-2 mb-3">
                  <label htmlFor="pkg-max-pallet" className="small text-muted mb-0">
                    Max pallet weight (lbs):
                  </label>
                  <input
                    id="pkg-max-pallet"
                    type="number"
                    min="100"
                    step="100"
                    className="form-control form-control-sm"
                    style={{ width: 120 }}
                    value={maxPalletWeightInput}
                    onChange={(e) => setMaxPalletWeightInput(e.target.value)}
                  />
                </div>
                <div className="d-inline-flex align-items-center gap-2 mb-3 ms-3">
                  <label htmlFor="pkg-max-stack" className="small text-muted mb-0">
                    Max stack height (in):
                  </label>
                  <input
                    id="pkg-max-stack"
                    type="number"
                    min="1"
                    max="24"
                    step="1"
                    className="form-control form-control-sm"
                    style={{ width: 90 }}
                    value={targetStackHeightInput}
                    onChange={(e) => setTargetStackHeightInput(e.target.value)}
                    title="Loose pallet-direct parts spread across the pallet, then stack only this tall before spilling onto another pallet. Lower = flatter, more-stable loads (more pallets). Capped at the 24-inch freight limit."
                  />
                </div>
                <div className="form-check d-inline-block mb-3 ms-3">
                  <input
                    id="pkg-notify-per-pallet"
                    type="checkbox"
                    className="form-check-input"
                    checked={!notifyPerPalletInput}
                    onChange={(e) => setNotifyPerPalletInput(!e.target.checked)}
                  />
                  <label
                    htmlFor="pkg-notify-per-pallet"
                    className="form-check-label small"
                    title="When checked, the customer only receives one email at SO Finalize. When unchecked (default), each pallet ship sends its own email."
                  >
                    Send <strong>one</strong> final notification at SO completion
                    (instead of per-pallet emails)
                  </label>
                </div>
                <div>
                  <button
                    className="btn btn-warning"
                    onClick={handleCreate}
                    disabled={busy === 'create'}
                  >
                    {busy === 'create'
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Generating...</>
                      : <><i className="bi bi-magic me-1"></i>Create draft plan</>}
                  </button>
                </div>
                </>
              ) : (
                <p className="small text-muted mb-0" data-testid="guide-create-office-only">
                  The office creates the plan (Office tools).
                </p>
              )}
            </div>
          )}

          {plan && (
            <>
              {/* Customer-notification guard rail — fires when every pallet
                  has shipped with per-pallet emails disabled but SO Finalize
                  hasn't run, meaning the customer has received ZERO emails.
                  Without this banner the order silently closes out with no
                  customer notification of any kind. */}
              {plan.status === 'Complete'
                && plan.notify_per_pallet === false
                && !plan.so_finalized_at && (
                <div className="alert alert-warning d-flex align-items-start gap-2 py-2 mb-3">
                  <i className="bi bi-exclamation-triangle-fill fs-5 mt-1 text-warning"></i>
                  <div className="flex-grow-1 small">
                    <strong>Customer has not been notified yet.</strong>
                    {' '}This plan was created with per-pallet emails turned off,
                    so no shipment notifications have been sent. Click
                    <strong> Finalize SO shipment</strong> below to send the
                    consolidated manifest email and close out the SO.
                  </div>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={handleFinalizeSO}
                    disabled={!!busy}
                  >
                    {busy === 'finalize_so'
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Finalizing...</>
                      : <><i className="bi bi-flag-fill me-1"></i>Finalize now</>}
                  </button>
                </div>
              )}
              {/* Controls row */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                {plan.status === 'Draft' && can('commitPlan') && (
                  <>
                    <button className="btn btn-sm btn-success" onClick={handleCommit} disabled={!!busy}>
                      {busy === 'commit'
                        ? <><span className="spinner-border spinner-border-sm me-1"></span>Committing...</>
                        : <><i className="bi bi-check2-circle me-1"></i>Commit plan</>}
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={handleDelete} disabled={!!busy}>
                      <i className="bi bi-trash me-1"></i>Delete draft
                    </button>
                  </>
                )}
                {(plan.status === 'Committed' || plan.status === 'Fulfilling') && (
                  <>
                    {can('refreshPlan') && (
                    <button className="btn btn-sm btn-outline-primary" onClick={handleRefresh} disabled={!!busy}>
                      {busy === 'refresh'
                        ? <><span className="spinner-border spinner-border-sm me-1"></span>Refreshing...</>
                        : <><i className="bi bi-arrow-clockwise me-1"></i>Refresh (append new parts)</>}
                    </button>
                    )}
                    {can('regeneratePlan') && (() => {
                      // Regenerate = delete-and-recreate with a fresh layout from
                      // CURRENT part/assembly data (vs Refresh, which only appends).
                      // Only offered while nothing physical is attached — the
                      // backend enforces the same guards.
                      const pallets = plan.pallets || [];
                      const regenBlockers = [];
                      if (pallets.some(p => (p.status || 'Building') !== 'Building')) regenBlockers.push('a pallet is past Building');
                      if (pallets.some(p => (p.boxes || []).some(b => b.status === 'Sealed'))) regenBlockers.push('a box is sealed');
                      if (pallets.some(p => p.shipment_group_id)) regenBlockers.push('a shipment batch has started');
                      if (pallets.some(p => (p.boxes || []).some(b => b.riding_from_so_number))) regenBlockers.push('ride-along boxes are on this plan');
                      if ((plan.outgoing_ride_alongs || []).length > 0) regenBlockers.push('boxes ride on another SO’s pallet');
                      const blocked = regenBlockers.length > 0;
                      return (
                        <button className="btn btn-sm btn-outline-danger" onClick={handleRegenerate}
                                disabled={!!busy || blocked}
                                title={blocked
                                  ? `Cannot regenerate: ${regenBlockers.join('; ')}.`
                                  : 'Delete this layout and re-pack from current part/assembly data (e.g. after entering an assembly’s Shipping Profile dims). Completed jobs re-route automatically; reprint routing slips after.'}>
                          {busy === 'regenerate'
                            ? <><span className="spinner-border spinner-border-sm me-1"></span>Regenerating...</>
                            : <><i className="bi bi-arrow-repeat me-1"></i>Regenerate (re-layout)</>}
                        </button>
                      );
                    })()}
                    {can('backfill') && (
                    <button className="btn btn-sm btn-outline-warning" onClick={handleBackfill} disabled={!!busy}
                            title="Retroactively route every already-completed laser task that could fill this plan (home SO + cross-SO allocations). Idempotent.">
                      {busy === 'backfill'
                        ? <><span className="spinner-border spinner-border-sm me-1"></span>Backfilling...</>
                        : <><i className="bi bi-clock-history me-1"></i>Backfill completed jobs</>}
                    </button>
                    )}
                    {can('printAllSlips') && (
                    <button className="btn btn-sm btn-danger" onClick={handlePrintAllSlips}
                            disabled={!!busy || routedJobs.length === 0}
                            title={routedJobs.length === 0
                              ? 'No routed jobs yet — complete a laser task or run Backfill first.'
                              : `Merge and print all ${routedJobs.length} job routing slips as one PDF.`}>
                      {busy === 'print_all'
                        ? <><span className="spinner-border spinner-border-sm me-1"></span>Building PDF...</>
                        : <><i className="bi bi-printer me-1"></i>Print all routing slips</>}
                    </button>
                    )}
                    {routedJobs.length > 0 && can('perJobSlips') && (
                      <button className="btn btn-sm btn-outline-secondary"
                              onClick={() => setShowJobList(v => !v)}>
                        <i className={`bi bi-chevron-${showJobList ? 'up' : 'down'} me-1`}></i>
                        Per-job reprint ({routedJobs.length})
                      </button>
                    )}
                    {(() => {
                      const sealed = sealedPallets(plan);
                      if (!sealed.length) return null;
                      return (
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={handlePrintAllPartLabels}
                          disabled={!!busy}
                          title={`Print part labels (image + material + dims + qty) for every part across all ${sealed.length} sealed pallet(s). Opens one PDF per pallet.`}
                        >
                          {busy === 'part_labels_all'
                            ? <><span className="spinner-border spinner-border-sm me-1"></span>Part labels...</>
                            : <><i className="bi bi-tags me-1"></i>Part labels (all sealed)</>}
                        </button>
                      );
                    })()}
                    {(() => {
                      // the package guide's Find all (2026-10-26): light every beacon on this order's pallets
                      // and show where each one is
                      const withBeacon = findablePallets(plan);
                      if (!withBeacon.length) return null;
                      return (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          data-testid="plan-find-all"
                          onClick={() => findPlanPallets({ plan, reloadPlan: loadPlan })}
                          disabled={!!busy}
                          title={`Light the beacons on this order's ${withBeacon.length} pallet(s) and show where each one is`}
                        >
                          <i className="bi bi-broadcast me-1"></i>Find all pallets ({withBeacon.length})
                        </button>
                      );
                    })()}
                  </>
                )}
                {(plan.status === 'Committed' || plan.status === 'Fulfilling') && (() => {
                  // planActions.bulkReadyOffer: Consolidated pallets don't count toward "all"; pickup orders stage
                  // instead of ship.
                  const offer = bulkReadyOffer(plan);
                  if (!offer) return null;
                  const { readyCount, totalCount, allReady, isPickup } = offer;
                  const label = isPickup
                    ? (allReady
                        ? `Mark all ${readyCount} pallet(s) ready for pickup — close out SO`
                        : `Mark ${readyCount} pallet(s) ready for pickup`)
                    : (allReady
                        ? `Ship all ${readyCount} pallet(s) — close out SO`
                        : `Ship ${readyCount} ready pallet(s)`);
                  const onClick = isPickup
                    ? () => handleStageAllReady(readyCount)
                    : () => handleShipAllReady(readyCount);
                  const busyKey = isPickup ? 'stage_all_ready' : 'ship_all_ready';
                  const colorClass = isPickup ? 'btn-warning' : 'btn-primary';
                  const outlineClass = isPickup ? 'btn-outline-warning' : 'btn-outline-primary';
                  const icon = isPickup ? 'bi-box-seam' : 'bi-truck';
                  return (
                    <button
                      className={`btn ${allReady ? `${colorClass} btn-lg` : `btn-sm ${outlineClass}`}`}
                      onClick={onClick}
                      disabled={!!busy}
                      title={allReady
                        ? `All ${totalCount} pallets are sealed. ${isPickup ? 'Stage them all for customer pickup AND send the consolidated "Order Ready for Pickup" email in one click — closes out the SO.' : 'Ship them all AND send the customer the consolidated shipping notification email in one click — closes out the SO.'}`
                        : (isPickup
                            ? `Stage ${readyCount} sealed pallet(s) for customer pickup. The customer email will be sent later when the remaining pallet(s) are staged and the SO closes out.`
                            : `Ship ${readyCount} sealed pallet(s). The customer email will be sent later when the remaining pallet(s) ship and the SO closes out.`)}
                    >
                      {busy === busyKey
                        ? <><span className="spinner-border spinner-border-sm me-1"></span>{isPickup ? 'Staging...' : 'Shipping...'}</>
                        : <><i className={`bi ${icon} me-1`}></i>{label}</>}
                    </button>
                  );
                })()}
                {signing.delivery && plan.fulfillment_type === 'delivery' && deliveryWaitingPallets(plan).length > 0 && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    data-testid="plan-delivery-qr"
                    onClick={handleDeliveryQr}
                    disabled={!!busy}
                    title="The receiver signs for the shipped pallets on a phone: shows the signing QR (the same code as on the printed manifest, or a new one when they shipped without one)"
                  >
                    {busy === 'delivery_qr'
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Starting...</>
                      : <><i className="bi bi-qr-code me-1"></i>Delivery signing QR ({deliveryWaitingPallets(plan).length} shipped)</>}
                  </button>
                )}
                {plan.status === 'ReadyForPickup' && plan.fulfillment_type === 'pickup' && (!plan.so_finalized_at || finalizeBatchAvailable) && (
                  <button
                    className="btn btn-warning btn-lg"
                    onClick={handleFinalizeSoPickup}
                    disabled={!!busy}
                    title={plan.so_finalized_at
                      ? "Pallets were staged/shipped AFTER the last SO finalize — send the customer a consolidated manifest email covering just this new batch."
                      : "Send the consolidated 'Order Ready for Pickup' email to the customer with the unified pickup manifest, and stamp the SO Pickup as finalized. Customer arrives → click Confirm Pickup on each pallet."}
                  >
                    {busy === 'finalize_so_pickup'
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Finalizing...</>
                      : <><i className="bi bi-flag-fill me-1"></i>{plan.so_finalized_at ? 'Send new-batch manifest (notify customer)' : 'Finalize SO Pickup (notify customer)'}</>}
                  </button>
                )}
                {(() => {
                  const offer = pickupAllOffer(plan);
                  if (!offer) return null;
                  const { stagedCount, allStaged } = offer;
                  return (
                    <button
                      className={`btn ${allStaged ? 'btn-success btn-lg' : 'btn-sm btn-outline-success'}`}
                      onClick={() => handleConfirmPickupAll(stagedCount)}
                      disabled={!!busy}
                      title={`Customer has arrived to pick up. Confirm pickup of ${stagedCount} staged pallet(s) — flips them Shipped + stamps picked_up_at/by + closes the plan.`}
                    >
                      {busy === 'confirm_pickup_all'
                        ? <><span className="spinner-border spinner-border-sm me-1"></span>Confirming...</>
                        : <><i className="bi bi-person-check me-1"></i>Customer here — confirm pickup of {stagedCount} pallet(s)</>}
                    </button>
                  );
                })()}
                {plan.status === 'Complete' && (!plan.so_finalized_at || finalizeBatchAvailable) && (
                  <button
                    className="btn btn-success btn-lg"
                    onClick={plan.fulfillment_type === 'pickup' ? handleFinalizeSoPickup : handleFinalizeSO}
                    disabled={!!busy}
                    title={plan.so_finalized_at
                      ? "Pallets went out AFTER the last SO finalize — send the customer a consolidated manifest email covering just this new batch."
                      : (plan.fulfillment_type === 'pickup'
                        ? "Send the consolidated 'Order Ready for Pickup' email and finalize the pickup."
                        : "Generate the unified manifest spanning every shipped pallet, optionally email the customer one consolidated notification, and stamp the SO as fully closed-out.")}
                  >
                    {(busy === 'finalize_so' || busy === 'finalize_so_pickup')
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Finalizing...</>
                      : <><i className="bi bi-flag-fill me-1"></i>{plan.so_finalized_at
                          ? 'Send new-batch manifest (notify customer)'
                          : (plan.fulfillment_type === 'pickup' ? 'Finalize SO Pickup' : 'Finalize SO shipment')}</>}
                  </button>
                )}
                {plan.status === 'Complete' && plan.so_finalized_at && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleReprintSOManifest}
                    disabled={!!busy}
                    title={`Driver / pickup ops view: every item on every physical pallet in this shipment (including ride-along parts from other SOs), with per-box "From SO-XXXX" chips on multi-source pallets. SO finalized at ${new Date(plan.so_finalized_at).toLocaleString()}${plan.so_finalized_by ? ` by ${plan.so_finalized_by}` : ''}. For the customer-facing per-SO manifest, use the Manifest button on the Shipments tab above.`}
                  >
                    {busy === 'reprint_so_manifest'
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Loading...</>
                      : <><i className="bi bi-truck me-1"></i>Reprint combined manifest</>}
                  </button>
                )}
                {plan.status === 'Complete' && can('reopenPlan') && (
                  <button
                    className="btn btn-sm btn-outline-warning"
                    onClick={handleReopenPlan}
                    disabled={!!busy}
                    title="Walk the plan back to Fulfilling to add pallets/boxes and assign late-finishing parts (e.g. brake parts that completed after the last pallet was picked up). The plan re-completes automatically once every pallet is staged and picked up / shipped; the SO-finalize record and combined manifest are preserved."
                  >
                    {busy === 'reopen_plan'
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Reopening…</>
                      : <><i className="bi bi-unlock me-1"></i>Reopen plan</>}
                  </button>
                )}
                <button className="btn btn-sm btn-outline-secondary" onClick={() => { setError(''); loadPlan(); }} disabled={!!busy}>
                  <i className="bi bi-arrow-repeat me-1"></i>Reload
                </button>
                {/* Edit plan toggle. Available on Draft/Committed/Fulfilling plans.
                    When on, per-zone Move dropdowns, manual-handling assign controls,
                    and box/pallet creation/deletion buttons appear. */}
                {(plan.status === 'Draft' || plan.status === 'Committed' || plan.status === 'Fulfilling') && can('editPlan') && (
                  <button
                    className={`btn btn-sm ${editMode ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setEditMode(v => !v)}
                    disabled={!!busy}
                    title={editMode
                      ? 'Exit edit mode — hides the Move / Assign / Delete controls.'
                      : 'Enter edit mode — hand-adjust zones, assign oversized / non-laser parts, consolidate or create pallets and boxes. Labels and manifests record the final positions.'}
                  >
                    <i className={`bi ${editMode ? 'bi-check2-square' : 'bi-pencil-square'} me-1`}></i>
                    {editMode ? 'Done editing' : 'Edit plan'}
                  </button>
                )}
              </div>

              {error && (() => {
                // Classify the banner: failures render danger-red, everything
                // else (success / progress messages) renders info-blue. The
                // `error` state is reused for both real errors and success
                // notifications throughout the card; this lets bulk-action
                // success messages like "Staged 2 pallet(s) for pickup ·
                // 5 task(s) finalized · SO SO-1051 pickup finalized" render
                // in blue instead of flashing red. catch blocks set messages
                // like "Stage all failed: …" / "Ship Pallet P1 failed: …" so
                // a `\bfailed\b` test catches the genuine error path.
                const isFailure = /\b(failed|error|exception|could not|unable to)\b/i.test(error);
                const variant = isFailure ? 'danger' : 'info';
                return (
                  <div className={`alert alert-${variant} py-2 small`}>
                    {error}
                  </div>
                );
              })()}

              {plan.max_pallet_weight_lbs != null && (
                <div className="small text-muted mb-2">
                  <i className="bi bi-speedometer2 me-1"></i>
                  Max pallet weight: <strong>{Math.round(plan.max_pallet_weight_lbs)} lbs</strong>
                  {plan.target_stack_height_in != null && (
                    <span className="ms-3">
                      <i className="bi bi-layers-half me-1"></i>
                      Max stack height: <strong>{plan.target_stack_height_in}&quot;</strong>
                    </span>
                  )}
                  {plan.notify_per_pallet === false && (
                    <span className="ms-3">
                      <i className="bi bi-envelope-slash me-1"></i>
                      Per-pallet emails: <strong>off</strong> (one consolidated email at SO Finalize)
                    </span>
                  )}
                  {plan.so_finalized_at && (
                    <span className="ms-3 text-success">
                      <i className="bi bi-check-circle-fill me-1"></i>
                      SO finalized {new Date(plan.so_finalized_at).toLocaleString()}
                      {plan.so_finalized_by ? ` by ${plan.so_finalized_by}` : ''}
                    </span>
                  )}
                </div>
              )}

              {/* Per-job reprint table (collapsed by default) */}
              {showJobList && routedJobs.length > 0 && can('perJobSlips') && (
                <div className="card bg-light mb-3">
                  <div className="card-body p-2">
                    <div className="small fw-bold mb-2">
                      <i className="bi bi-list-check me-1"></i>
                      Routed jobs — click to reprint the routing slip for that job
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0" style={{ background: 'white' }}>
                        <thead className="table-light">
                          <tr>
                            <th>Job #</th>
                            <th>Source SO</th>
                            <th className="text-center">Placements</th>
                            <th>Last routed</th>
                            <th className="text-end"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {routedJobs.map(j => {
                            const isCross = j.sales_order_id !== plan.sales_order_id;
                            const when = j.triggered_at ? new Date(j.triggered_at) : null;
                            return (
                              <tr key={j.job_id}>
                                <td><strong>{j.master_job_num}</strong></td>
                                <td>
                                  {isCross
                                    ? <span className="badge bg-warning text-dark">↪ {j.source_so_number}</span>
                                    : <span className="text-muted">{j.source_so_number}</span>}
                                </td>
                                <td className="text-center">{j.n_placements}</td>
                                <td className="small text-muted">
                                  {when ? when.toLocaleString() : '—'}
                                </td>
                                <td className="text-end">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={!!busy || !j.laser_task_id}
                                    onClick={() => handlePrintJobSlip(j.master_job_num, j.laser_task_id)}
                                  >
                                    {busy === `print_job_${j.laser_task_id}`
                                      ? <><span className="spinner-border spinner-border-sm"></span></>
                                      : <><i className="bi bi-printer"></i> Print</>}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Shipping progress strip: counts of pallets in each status */}
              <SOPackagingShipProgress plan={plan} />

              {/* Data-quality warning: any part without thickness? */}
              <SOPackagingAssemblyDimsWarning plan={plan} />
              <SOPackagingThicknessWarning plan={plan} />

              {/* Outgoing-only banner: this SO has no boxes/stacks of its
                  own — every item ships physically on another SO's pallet
                  (cross-SO ride-along). Without this banner the operator
                  sees the plan's empty placeholder pallet and tries to
                  pack it; the rider table below the map is easy to miss
                  on a long page. Banner makes it the FIRST thing they
                  read on the plan card. */}
              {(() => {
                const ownBoxCount = (plan.pallets || []).reduce(
                  (sum, p) => sum + ((p.boxes || []).length || 0), 0
                );
                const ownStackCount = (plan.pallets || []).reduce(
                  (sum, p) => sum + ((p.pallet_direct || p.stacks || []).length || 0), 0
                );
                const ridingCount = (plan.outgoing_ride_alongs || []).length;
                if (ownBoxCount + ownStackCount === 0 && ridingCount > 0) {
                  const destPallets = Array.from(new Set(
                    (plan.outgoing_ride_alongs || [])
                      .map(b => b.riding_to_pallet_short_id || b.riding_to_pallet_id)
                      .filter(Boolean)
                  ));
                  const destSOs = Array.from(new Set(
                    (plan.outgoing_ride_alongs || [])
                      .map(b => b.riding_to_so_number)
                      .filter(Boolean)
                  ));
                  return (
                    <div className="alert alert-info border-info mb-2" role="alert">
                      <div className="d-flex align-items-start">
                        <i className="bi bi-arrow-up-right-circle-fill me-2" style={{ fontSize: '1.4rem' }}></i>
                        <div>
                          <div className="fw-bold mb-1">
                            This SO has no pallets of its own — all {ridingCount} item(s) ship on other SOs' pallets.
                          </div>
                          <div className="small mb-1">
                            Riding on{' '}
                            {destPallets.map((p, i) => (
                              <React.Fragment key={p}>
                                {i > 0 ? ', ' : ''}
                                <strong>{p}</strong>
                              </React.Fragment>
                            ))}
                            {destSOs.length > 0 && <> (on {destSOs.map((s, i) => (
                              <React.Fragment key={s}>{i > 0 ? ', ' : ''}<strong>{s}</strong></React.Fragment>
                            ))})</>}
                            . See the "Riding on other SOs' pallets" table below for full details.
                          </div>
                          {(plan.outgoing_ride_alongs || []).filter(isNested).map(b => (
                            <div key={`nested-${b.box_id}`} className="small mb-1" data-testid={`so-outgoing-nested-${b.box_id}`}>
                              <i className="bi bi-box-arrow-in-down me-1"></i>
                              <strong>{b.short_id || boxLabel(b)}</strong> is {insideText(b)}: pack its pieces in {b.inside_box_short_id || 'that carton'}.
                            </div>
                          ))}
                          <div className="small text-muted">
                            The empty pallet on the map below is a placeholder — physical packing is handled by the host pallet's SO. You can delete it in edit mode if it's distracting.
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Staging queue — secondary-process parts awaiting a pallet.
                  Sits ABOVE the map and is always actionable (no edit-mode
                  gate) so the office can quickly designate a pallet per part
                  after braking. */}
              {planHasHoldingPallets(plan) ? (
                // Holding pallets (every part on a pallet at the laser): the Staging queue becomes Prepare shipment.
                <PrepareShipment
                  plan={plan}
                  busy={busy}
                  palletChecks={palletChecks}
                  onMoved={loadPlan}
                  onPromote={handlePromoteHoldingPallet}
                  onManualAssign={handleManualAssign}
                  onPackFromHolding={handlePackFromHolding}
                  onCreatePallet={handleCreateEmptyPallet}
                  onInspectPart={setInspectedPartId}
                  onReopenPlan={can('reopenPlan') ? handleReopenPlan : undefined}
                  onClearWip={can('clearWip') ? handleClearWip : undefined}
                  onFind={handleFindPlanPallet}
                />
              ) : (
                <SOPlanStagingQueue
                  plan={plan}
                  busy={busy}
                  onManualAssign={handleManualAssign}
                  onCreatePallet={handleCreateEmptyPallet}
                  onInspectPart={setInspectedPartId}
                  onReopenPlan={can('reopenPlan') ? handleReopenPlan : undefined}
                  onClearWip={can('clearWip') ? handleClearWip : undefined}
                />
              )}

              {/* Pallet map */}
              <SOPalletMap pallets={plan.pallets || []} outlineCache={outlineCache} />

              {editModeOn && (
                <div className="alert alert-warning py-2 small mb-2">
                  <i className="bi bi-pencil-square me-1"></i>
                  <strong>Edit mode on.</strong> Zones show a "Move to…" dropdown, empty
                  boxes / pallets can be deleted, and the manual-handling list lets
                  you assign oversized / non-laser parts into boxes or pallet-direct
                  stacks. Every edit is written to the audit log so labels and
                  manifests reflect the final positions. Only Building pallets and
                  unsealed boxes are editable — seal / ship actions still apply
                  normally.
                </div>
              )}

              {/* Boxes list grouped by pallet. Pallet cards carry Seal / Ship /
                  Reprint-label / Reprint-manifest actions depending on status. */}
              <SOPlannedBoxList
                highlightPalletId={highlightPalletId}
                palletChecks={palletChecks}
                kitSummary={plan.kit_box_summary}
                onCreateKitBox={handleCreateKitBox}
                onDeleteKitBox={handleDeleteKitBox}
                purchasedOptions={plan.purchased_options}
                onAddPurchasedItem={handleAddPurchasedItem}
                onDeletePurchasedItem={handleDeletePurchasedItem}
                plan={plan}
                apiBase={apiBase}
                busy={busy}
                outlineCache={outlineCache}
                onSeal={handleSealPallet}
                onShip={handleShipPallet}
                onStage={handleStagePallet}
                onConfirmPickup={handleConfirmPickup}
                onSealBox={handleSealBox}
                onReprintBoxLabel={handleReprintBoxLabel}
                onReprintLabels={handleReprintLabels}
                onReprintManifest={handleReprintManifest}
                onPrintPartLabels={handlePrintPartLabels}
                onPrintPalletStatusLabel={handlePrintPalletStatusLabel}
                onPrintBoxStatusLabel={handlePrintBoxStatusLabel}
                onPrintStackStatusLabel={handlePrintStackStatusLabel}
                onOpenMoveModal={can('moveMerge') ? (p) => setMoveModalSourcePallet(p) : undefined}
                onUnsealPallet={can('unseal') ? handleUnsealPallet : undefined}
                onUnsealBox={can('unseal') ? handleUnsealBox : undefined}
                onOpenNestModal={can('nestBox') ? handleOpenNestModal : undefined}
                onUnnestBox={can('unnestBox') ? handleUnnestBox : undefined}
                onConvertContainerType={can('convertContainer') ? handleConvertContainerType : undefined}
                fulfillmentType={plan.fulfillment_type || 'shipping'}
                editMode={editModeOn}
                onMoveZone={handleMoveZone}
                onEditZoneQty={handleEditZoneQty}
                onDeleteZone={handleDeleteZone}
                onDeleteEmptyBox={handleDeleteEmptyBox}
                onDeleteEmptyPallet={handleDeleteEmptyPallet}
                onCreateEmptyBox={handleCreateEmptyBox}
                onRepackPositions={can('editPositions') ? handleRepackPositions : undefined}
                onSaveBoxPositions={can('editPositions') ? handleSaveBoxPositions : undefined}
                boxCatalog={boxCatalog}
                onInspectPart={setInspectedPartId}
              />

              {editModeOn && (
                <div className="mt-2 mb-3 d-flex flex-wrap align-items-center gap-2">
                  <span className="small text-muted me-1">New empty pallet:</span>
                  {[
                    { label: '40×48',  size: '40x48',  hint: 'Standard GMA pallet (default)' },
                    { label: '48×96',  size: '48x96',  hint: 'Mid-oversize for parts that overflow 40×48' },
                    { label: '48×120', size: '48x120', hint: 'Long stock pallet — common for 8–10 ft sheet/plate' },
                    { label: '60×120', size: '60x120', hint: 'Largest oversize — for very wide parts' },
                  ].map(opt => (
                    <button
                      key={opt.size}
                      className="btn btn-sm btn-outline-primary"
                      disabled={!!busy}
                      onClick={() => handleCreateEmptyPallet(opt.size)}
                      title={opt.hint}
                    >
                      {busy === 'new_pallet'
                        ? <><span className="spinner-border spinner-border-sm me-1"></span>{opt.label}</>
                        : <><i className="bi bi-plus-circle me-1"></i>{opt.label}</>}
                    </button>
                  ))}
                  <span className="text-muted mx-2">·</span>
                  <SOEditNewBoxOnlyPicker
                    boxCatalog={boxCatalog}
                    busy={busy}
                    onCreateBoxOnly={handleCreateBoxOnly}
                  />
                </div>
              )}

              {/* Outgoing ride-aways: this SO's boxes that are physically
                  shipping via another SO's pallet (same customer). Logically
                  still on this plan, billing/yield + qty_shipped credits this
                  SO when the host pallet ships. Shown so operators can see
                  "I have nothing to physically pack, but my parts ARE going
                  out today on SO-XXXX's pickup." */}
              {(plan.outgoing_ride_alongs || []).length > 0 && (
                <div className="card border-info mt-3 mb-2">
                  <div className="card-header bg-info bg-opacity-10 d-flex justify-content-between align-items-center">
                    <span className="fw-bold">
                      <i className="bi bi-arrow-up-right-circle me-1"></i>
                      Riding on other SOs' pallets ({(plan.outgoing_ride_alongs || []).length})
                    </span>
                    <span className="small text-muted">
                      Same customer · this SO retains billing &amp; qty_shipped credit
                    </span>
                  </div>
                  <div className="card-body py-2">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr className="small text-muted">
                          <th>Box / Stack</th>
                          <th>Type</th>
                          <th>Qty</th>
                          <th>Riding to</th>
                          <th>Status</th>
                          {(can('nestBox') || can('unnestBox')) && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {plan.outgoing_ride_alongs.map(b => {
                          const totalQty = (b.zones || []).reduce((a, z) => a + (z.actual_qty || z.planned_qty || 0), 0);
                          const ztext = (b.zones || [])
                            .map(z => `${z.product_id} × ${z.actual_qty || z.planned_qty || 0}`)
                            .join('; ');
                          return (
                            <tr key={b.box_id}>
                              <td>
                                {b.short_id && (
                                  <span className="badge bg-dark me-1" style={{ fontFamily: 'monospace' }}>
                                    {b.short_id}
                                  </span>
                                )}
                                <span className="small">{ztext || '(no zones)'}</span>
                              </td>
                              <td className="small">
                                {b.is_pallet_direct
                                  ? <span className="badge bg-info text-dark">stack</span>
                                  : <span className="badge bg-light text-dark">box {b.sku || ''}</span>}
                              </td>
                              <td className="small">{totalQty}</td>
                              <td className="small">
                                {isNested(b) ? (
                                  <span data-testid={`so-outgoing-inside-${b.box_id}`}>
                                    inside <strong>{b.inside_box_short_id || `box ${b.inside_box_id}`}</strong>
                                    {b.inside_so_number && <> ({b.inside_so_number})</>}
                                    {' '}on <strong>{b.inside_pallet_short_id || b.riding_to_pallet_short_id || `pallet ${b.riding_to_pallet_id}`}</strong>
                                  </span>
                                ) : (
                                  <>
                                    <strong>{b.riding_to_pallet_short_id || `pallet ${b.riding_to_pallet_id}`}</strong>
                                    {b.riding_to_so_number && (
                                      <> on <strong>{b.riding_to_so_number}</strong></>
                                    )}
                                  </>
                                )}
                              </td>
                              <td className="small">
                                <span className="badge bg-secondary">{b.status}</span>
                                {b.riding_to_pallet_status && (
                                  <span className="ms-1 small text-muted">(host: {b.riding_to_pallet_status})</span>
                                )}
                              </td>
                              {(can('nestBox') || can('unnestBox')) && (
                                <td className="small text-end">
                                  {isNested(b) && can('unnestBox')
                                    && ['Building', 'Ready'].includes(b.riding_to_pallet_status || 'Building') && (
                                    <button
                                      className="btn btn-sm btn-outline-warning py-0"
                                      disabled={!!busy}
                                      onClick={() => handleUnnestBox(b)}
                                      title={`Take ${b.short_id || 'this box'} out of ${b.inside_box_short_id || 'its carton'}: it stays on the same pallet as a box of its own.`}
                                    >
                                      {busy === `unnest_${b.box_id}`
                                        ? <span className="spinner-border spinner-border-sm"></span>
                                        : <><i className="bi bi-box-arrow-up me-1"></i>Take out</>}
                                    </button>
                                  )}
                                  {can('nestBox')
                                    && boxActionState(b, { palletStatus: b.riding_to_pallet_status || 'Building' }).canNest && (
                                    <button
                                      className="btn btn-sm btn-outline-primary py-0"
                                      disabled={!!busy}
                                      onClick={() => handleOpenNestModal(b, plan.plan_id)}
                                      title="Pack this box inside a same-customer order's carton on its pallet: one carton, one label."
                                    >
                                      <i className="bi bi-box-arrow-in-down me-1"></i>Put inside a carton…
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Manual handling strip */}
              <SOKitCoveragePanel kitSummary={plan.kit_box_summary} />
              <SOPurchasedCoveragePanel summary={plan.purchased_summary} homeSalesOrderId={plan.sales_order_id} />
              <SOPlanManualHandling
                plan={plan}
                editMode={editModeOn}
                busy={busy}
                onManualAssign={handleManualAssign}
                onRideAlongAssign={can('rideAlongAssign') ? handleRideAlongAssign : undefined}
                crossSoTargets={planCrossSoTargets}
                boxCatalog={boxCatalog}
                onInspectPart={setInspectedPartId}
              />
            </>
          )}
        </div>
      )}

      {/* Part inspector — renders when any clickable product_id is clicked. */}
      {inspectedPartId && (
        <PartDetailModal
          apiBase={apiBase}
          partId={inspectedPartId}
          onClose={() => setInspectedPartId(null)}
        />
      )}

      {loadTruck && plan && (
        <LoadTruckModal apiBase={apiBase} ui={ui} order={loadTruckOrderFromPlan(plan, loadTruck.palletIds)}
          onClose={(r) => { setLoadTruck(null); if (r && r.loaded) loadPlan(); }} />
      )}
      {/* SO-Finalize Email modal — opens at SO close-out so the operator
          can pick recipients + edit the consolidated customer email body. */}
      <SoFinalizeEmailDialog
        show={finalizeDialog.open}
        planId={plan?.plan_id}
        kind={finalizeDialog.kind}
        pin={finalizeDialog.pin}
        allowEdit={can('finalizeEdit')}
        orderNumber={plan?.order_number}
        batchManifestUrl={finalizeDialog.batchManifestUrl}
        batchPalletLabels={finalizeDialog.batchPalletLabels}
        onClose={handleFinalizeDialogClose}
        onSent={handleFinalizeDialogSent}
      />

      {/* Put inside a carton: another order's carton on a same-customer pallet */}
      {nestModal && plan && (
        <SONestBoxModal
          apiBase={apiBase}
          planId={nestModal.planId}
          box={nestModal.box}
          onClose={() => setNestModal(null)}
          onNested={handleNested}
        />
      )}

      {/* Move/merge modal: consolidate boxes between Ready pallets */}
      {moveModalSourcePallet && plan && (
        <SOMovePalletModal
          apiBase={apiBase}
          sourcePallet={moveModalSourcePallet}
          plan={plan}
          onClose={() => setMoveModalSourcePallet(null)}
          onMoved={async (result) => {
            // Auto-reprint labels — only Ready mode actually has labels to
            // reprint. Building mode hasn't sealed anything yet, so the
            // labels will be issued at seal time on the consolidated pallet.
            try {
              if (result.dest_label_url) {
                await openPdfTab(
                  `${apiBase}${result.dest_label_url}`,
                  `Pallet_${result.dest_short_id || result.dest_pallet_num}_Label.pdf`,
                );
              }
              if (result.src_label_url) {
                await openPdfTab(
                  `${apiBase}${result.src_label_url}`,
                  `Pallet_${result.src_short_id || result.src_pallet_num}_Label.pdf`,
                );
              }
              // the box pages whose "B# of N" the move changed (jobq-api 2026-11-04), so the floor's labels match
              if (result.dest_box_labels_url) {
                await openPdfTab(`${apiBase}${result.dest_box_labels_url}`,
                  `Pallet_${result.dest_short_id || result.dest_pallet_num}_Boxes.pdf`);
              }
              if (result.src_box_labels_url) {
                await openPdfTab(`${apiBase}${result.src_box_labels_url}`,
                  `Pallet_${result.src_short_id || result.src_pallet_num}_Boxes.pdf`);
              }
            } catch (_) { /* labels may pop up later, don't block close */ }
            const modeNote = result.mode === 'Building'
              ? ' (Building merge — seal the dest pallet next to print one combined label)'
              : '';
            const summary = `Moved ${result.moved_box_count} box(es) ` +
              `from P${result.src_pallet_num} (${result.src_short_id || ''}) ` +
              `→ ${result.dest_short_id || `P${result.dest_pallet_num}`}` +
              (result.cross_so ? ' [CROSS-SO]' : '') +
              (result.src_status === 'Consolidated' ? ' · source pallet consolidated.' : '.') +
              modeNote +
              ((result.warnings || []).length ? ` ⚠ ${result.warnings.join('; ')}` : '');
            setError(summary);
            setMoveModalSourcePallet(null);
            await loadPlan();
          }}
        />
      )}
    </div>
  );
};

// Pallet map: row of SVG pallets with box footprints drawn to scale.
// Pallet dims come from each planned_pallets row (standard 40×48 by
// default, 48×96 or 60×120 for parts that don't fit the smaller size).
const SOPalletMap = ({ pallets }) => {
  // Hide consolidated pallets whose every box was moved to the consolidation
  // target — they're audit-trail rows, not pickable/shippable units, and
  // showing them on the map confuses the operator scanning the plan.
  // (A Consolidated pallet that still has boxes is rare but possible —
  // those stay visible so the operator can see what didn't get moved.)
  const visiblePallets = pallets.filter(
    p => !(p.status === 'Consolidated' && (p.boxes || []).length === 0)
  );
  const hiddenConsolidatedCount = pallets.length - visiblePallets.length;
  if (!visiblePallets.length) {
    return <div className="text-muted small fst-italic mb-3">Plan contains no packable pallets.</div>;
  }
  const SCALE = 4.0; // px per inch
  // Build the subtitle from the ACTUAL distinct pallet sizes in this plan,
  // not a hardcoded "40 × 48". Lets the operator see at a glance that the
  // plan is using an oversize pallet (48×96 / 60×120) for large parts.
  const sizeSet = new Map(); // "LxW" -> count
  for (const p of visiblePallets) {
    const key = `${Math.round(p.length_in || 40)}×${Math.round(p.width_in || 48)}`;
    sizeSet.set(key, (sizeSet.get(key) || 0) + 1);
  }
  const sizeLabels = Array.from(sizeSet.entries()).map(([k, n]) => (
    visiblePallets.length === 1 || sizeSet.size === 1 ? k : `${n}×${k}`
  ));
  const subtitle = sizeLabels.join(' + ');
  return (
    <div className="mb-3">
      <div className="fw-bold mb-2">
        <i className="bi bi-pallet me-1"></i>Pallet map
        <span className="text-muted small ms-2">
          {subtitle} · single layer · no overhang
          {hiddenConsolidatedCount > 0 && (
            <span className="ms-2" title="Consolidated pallets whose boxes were moved to the consolidation target — hidden from the map since they're empty audit-trail rows.">
              · {hiddenConsolidatedCount} consolidated pallet{hiddenConsolidatedCount > 1 ? 's' : ''} hidden
            </span>
          )}
        </span>
      </div>
      <div className="d-flex flex-wrap gap-3">
        {visiblePallets.map(p => {
          const w = (p.length_in || 40) * SCALE;
          const h = (p.width_in || 48) * SCALE;
          const isOversize = !(Math.round(p.length_in || 40) === 40
                               && Math.round(p.width_in || 48) === 48);
          const isBoxOnly = (p.container_type || 'Pallet') === 'Box';
          const isWip = !!p.is_wip;
          // WIP-staging pallets get distinct amber styling so operators
          // recognize at a glance that they're staging racks (Brake / Fab
          // queue), not real shipping pallets. They override the Box-only
          // styling when both flags are set.
          const wrapperClass = isWip
            ? 'rounded p-2 border border-warning bg-warning bg-opacity-10'
            : (isBoxOnly ? 'rounded p-2 bg-info bg-opacity-10 border border-info' : 'rounded p-2 border bg-light');
          // Stable global pallet identifier (e.g. PLT-M8ZV). Falls back to
          // the legacy P# ordinal only when no short_id exists, which
          // shouldn't happen post-migration but keeps the UI rendering for
          // any pre-Apr-2026 plan rows.
          // Box-only containers with a single box collapse to the BOX-XXXX
          // identity — the box is the ONLY physical artifact (no wood
          // pallet to label), so the PLT-XXXX is operationally noise.
          const realBoxes = (p.boxes || []).filter(isCarton);
          const boxOnlySoloShortId = (isBoxOnly && realBoxes.length === 1)
            ? (realBoxes[0].short_id || null) : null;
          const palletLabel = boxOnlySoloShortId || p.short_id || `P${p.pallet_num}`;
          return (
            <div
              key={p.pallet_id}
              className={wrapperClass}
              style={{ width: 'fit-content', cursor: 'pointer' }}
              title={`Jump to ${palletLabel} details below`}
              onClick={() => {
                const el = document.getElementById(`so-pallet-detail-${p.pallet_id}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              <div className="small fw-bold mb-1" style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                {isWip
                  ? `WIP ${palletLabel}`
                  : (isBoxOnly
                    ? (boxOnlySoloShortId ? palletLabel : `Box ${palletLabel}`)
                    : palletLabel)}
                {isWip && (
                  <span
                    className="badge bg-warning text-dark ms-1"
                    title={isTaskWipPallet(p)
                      ? `${taskWipHeader(p)}. Ship it as it is or pack its pieces from Prepare shipment.`
                      : "WIP staging pallet — parts still need a post-laser step (brake/fab/etc.) before they can ship. Convert via Edit mode after the next station finishes."}
                  >
                    <i className="bi bi-arrow-right-circle me-1"></i>
                    {isTaskWipPallet(p)
                      ? `HOLDING · THEN ${String(taskWipStation(p)).toUpperCase()}`
                      : `TO ${(p.wip_next_station || 'NEXT').toUpperCase()}`}
                  </span>
                )}
                {!isWip && isBoxOnly && (
                  <span className="badge bg-info text-dark ms-1" title="Loose carton — ships without a pallet. Manifest and labels read 'Box' at seal time.">
                    <i className="bi bi-box me-1"></i>NO PALLET
                  </span>
                )}
                {!isWip && !isBoxOnly && isOversize && (
                  <span className="badge bg-danger text-white ms-1" title="Oversize pallet — uses a non-standard 48×96 or 60×120 pallet because a part on it exceeds the 40×48 footprint.">
                    {Math.round(p.length_in)}×{Math.round(p.width_in)}″
                  </span>
                )}
                <span className={`badge ms-2 bg-${p.status === 'Ready' ? 'success' : p.status === 'Shipped' ? 'secondary' : 'primary'}`}>
                  {p.status}
                </span>
                {(() => {
                  const wt = (p.boxes || []).reduce(
                    (a, bx) => a + (bx.total_planned_weight || 0), 0);
                  if (wt <= 0) return null;
                  return (
                    <span className="badge bg-light text-dark ms-1">
                      ~{Math.round(wt)} lbs
                    </span>
                  );
                })()}
              </div>
              <svg
                width={w}
                height={h}
                style={{
                  background: isBoxOnly ? '#f8f9fa' : 'white',
                  border: isBoxOnly ? '1px dashed #b6effb' : '1px solid #999',
                  opacity: isBoxOnly ? 0.85 : 1,
                }}
              >
                <defs>
                  <pattern id="direct-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2="6" stroke="#6c757d" strokeWidth="1.2" />
                  </pattern>
                </defs>
                {(p.boxes || []).filter(bx => !isNested(bx)).map(bx => {
                  const bol = bx.pallet_rotated ? bx.outer_w : bx.outer_l;
                  const bow = bx.pallet_rotated ? bx.outer_l : bx.outer_w;
                  const x = (bx.pallet_pos_x || 0) * SCALE;
                  const y = (bx.pallet_pos_y || 0) * SCALE;
                  const bw = (bol || 0) * SCALE;
                  const bh = (bow || 0) * SCALE;
                  const isDirect = bx.is_pallet_direct;
                  // WIP staging lane: a pallet-direct box on an is_wip pallet is
                  // a staging area (parts headed to a secondary process), not a
                  // packed stack — and its footprint is just the first part's
                  // dims. Render it as ONE full-footprint amber tile with the
                  // part count + next station instead of N micro-strips. The
                  // per-part manifest lives in the detail card below.
                  if (isWip && isDirect) {
                    const partCount = (bx.zones || []).length;
                    return (
                      <g key={bx.box_id}>
                        <rect x={0} y={0} width={w} height={h}
                          fill="#ffc107" fillOpacity={0.18}
                          stroke="#fd7e14" strokeWidth={1.4} strokeDasharray="5 3" />
                        <text x={w / 2} y={h / 2 - 3} textAnchor="middle"
                          fontSize={Math.min(13, Math.max(9, w / 9))}
                          fontWeight="bold" fill="#b76300">
                          {partCount} part{partCount === 1 ? '' : 's'}
                        </text>
                        <text x={w / 2} y={h / 2 + 13} textAnchor="middle"
                          fontSize={Math.min(11, Math.max(7, w / 12))}
                          fill="#b76300">
                          → {(p.wip_next_station || 'STAGE').toUpperCase()}
                        </text>
                      </g>
                    );
                  }
                  // Boxed pallets: render the box's interior zones inside its
                  // pallet footprint (mirrors the bottom Boxes & Stacks card
                  // at smaller scale). Pallet-direct: render the persisted
                  // tile_cols × tile_rows grid of per-column tiles instead
                  // of the older hatched-rectangle placeholder. Falls back
                  // to single-rect for legacy zones missing tile dims.
                  const z0 = (bx.zones && bx.zones[0]) || null;
                  const tileCols = z0 ? z0.tile_cols : null;
                  const tileRows = z0 ? z0.tile_rows : null;
                  // A manually-consolidated pallet-direct stack holds >1 zone, and
                  // the moved-in zones carry NULL shelf-pack geometry (move_zone /
                  // manual_assign don't recompute it). The tile-grid renderer only
                  // has coords for the auto-packed first zone, so the others
                  // collapse to 0-size and vanish — the map would show just one of
                  // the parts. Detect that and fall back to an equal-strip layout
                  // (one strip per part), mirroring the bottom Boxes & Stacks
                  // card's geometry-less fallback.
                  const directZones = isDirect ? (bx.zones || []) : [];
                  const directStripFallback = isDirect && directZones.length > 1
                    && directZones.some(z => !z.zone_w || !z.zone_h);
                  const showTileGrid = isDirect && tileCols > 0 && tileRows > 0
                    && !directStripFallback;
                  const fillFallback = isDirect ? 'url(#direct-hatch)'
                    : (SO_BOX_SKU_COLOR[bx.sku] || '#adb5bd');
                  // Use effective_status so FullPerPolicy boxes (short but
                  // sealable per yield policy) render at the same opacity
                  // as Full instead of the washed-out Partial look.
                  const effSt = bx.effective_status || bx.status;
                  const fillOpacity = isDirect ? 1
                    : (effSt === 'Empty' ? 0.25 : effSt === 'Partial' ? 0.55 : 0.9);
                  const stroke = isDirect ? '#495057' : '#222';
                  // SVG cell label: the unique tail of the box's short_id
                  // (e.g. "A7K9" from "BOX-A7K9"). Stable across deletes
                  // and consolidations, short enough to fit a small map
                  // cell, and matches the BOX-XXXX badge on the card
                  // below. Falls back to legacy B#/S# ordinal on the rare
                  // pre-Apr-2026 row that lacks a short_id.
                  const displayNum = bx.display_box_num ?? bx.box_num;
                  const label = bx.short_id
                    ? bx.short_id.replace(/^[A-Z]+-/, '')
                    : (isDirect ? `S${displayNum}` : `B${displayNum}`);
                  const fontSize = Math.min(12, Math.max(7, Math.min(bw, bh) / 5));
                  return (
                    <g key={bx.box_id}>
                      {/* Pallet footprint border. Pallet-direct stacks
                          with a tile grid get only an overlay outline
                          drawn AFTER the cells (see below) so colored
                          cells form the visual footprint and the
                          outline sits on top to make any overflow
                          (cells extending past the rotated box rect —
                          visible if the rotation math regresses or if
                          bad data sneaks through) obvious. */}
                      {!(isDirect && showTileGrid) && !directStripFallback && (
                        <rect x={x} y={y} width={bw} height={bh}
                          fill={fillFallback}
                          fillOpacity={fillOpacity}
                          stroke={stroke} strokeWidth={isDirect ? 1.3 : 0.8}
                          strokeDasharray={isDirect ? '3 2' : null} />
                      )}
                      {/* Equal-strip layout for a manually-consolidated
                          pallet-direct stack: one colored strip per zone so all
                          parts are visible (the moved-in zones have no geometry
                          for the tile grid). Approximate — manual consolidation
                          doesn't recompute a combined footprint — but it shows
                          "this stack holds N parts" at a glance, matching the
                          bottom detail card. */}
                      {directStripFallback && (() => {
                        const n = directZones.length;
                        const sh = bh / n;
                        return directZones.map((z, i) => {
                          const color = SO_PKG_COLOR_PALETTE[(z.color_idx || 0) % SO_PKG_COLOR_PALETTE.length];
                          const sy = y + i * sh;
                          const labL = (z.product_id || '').slice(0, 12);
                          return (
                            <g key={`strip-${z.zone_id || z.part_id || i}`}>
                              <rect x={x} y={sy} width={bw} height={sh}
                                fill={color} fillOpacity={0.85}
                                stroke="#222" strokeWidth={0.4} />
                              {bw >= 26 && sh >= 9 && labL && (
                                <text x={x + bw / 2} y={sy + sh / 2 + 3}
                                  textAnchor="middle"
                                  fontSize={Math.min(9, Math.max(6, sh / 1.8))}
                                  fontWeight="bold" fill="#111"
                                  style={{ paintOrder: 'stroke', stroke: '#fff', strokeWidth: 1.5 }}>
                                  {labL}
                                </text>
                              )}
                            </g>
                          );
                        });
                      })()}
                      {/* Pallet-direct: per-column tile grid like the
                          bottom Stack-detail card. Each cell is colored
                          per zone; label = stack height. Zone box-local
                          coords (zone_x along outer_l; zone_y along
                          outer_w, bottom-relative within the box)
                          map to pallet space via pallet_rotated: when
                          true, outer_l → SVG-y and outer_w → SVG-x, so
                          zone_w/h, zone_x/y, and tile_cols/rows swap.
                          Without the swap a rotated stack's cells
                          extend along pre-rotation axes and overflow
                          the (correctly rotated) box outline. */}
                      {showTileGrid && (() => {
                        const rot = !!bx.pallet_rotated;
                        const boxOuterW = bx.outer_w || 0;
                        const boxTcSvg = rot ? (tileRows || 1) : (tileCols || 1);
                        const boxTrSvg = rot ? (tileCols || 1) : (tileRows || 1);
                        const cellW = bw / boxTcSvg;
                        const cellH = bh / boxTrSvg;
                        const cellFontSize = Math.min(8, Math.max(4, Math.min(cellW, cellH) / 2.5));
                        // Color per zone: walk zones, each gets a color
                        // bucket. For the top map we usually have one zone
                        // per pallet-direct block, so this collapses to a
                        // single color across all cells.
                        const cells = [];
                        for (const z of (bx.zones || [])) {
                          const color = SO_PKG_COLOR_PALETTE[(z.color_idx || 0) % SO_PKG_COLOR_PALETTE.length];
                          const localPosA = z.zone_x || 0;
                          const localPosB = boxOuterW - (z.zone_y || 0) - (z.zone_h || 0);
                          const palOffX = (rot ? localPosB : localPosA) * SCALE;
                          const palOffY = (rot ? localPosA : localPosB) * SCALE;
                          const zw = (rot ? (z.zone_h || 0) : (z.zone_w || 0)) * SCALE;
                          const zh = (rot ? (z.zone_w || 0) : (z.zone_h || 0)) * SCALE;
                          const zx = x + palOffX;
                          const zy = y + palOffY;
                          const zoneTC = z.tile_cols || tileCols;
                          const zoneTR = z.tile_rows || tileRows;
                          const tcSvg = rot ? zoneTR : zoneTC;
                          const trSvg = rot ? zoneTC : zoneTR;
                          const zCellW = zw / tcSvg;
                          const zCellH = zh / trSvg;
                          // Per-column height derived from planned_qty, NOT the
                          // stored stack_height (which can go stale on an
                          // over-ship grow, manual edit, or legacy zone). Most
                          // columns are baseLayers high; the first `tallCells`
                          // carry one extra layer when qty doesn't divide
                          // evenly. Falls back to stack_height when planned_qty
                          // is unavailable.
                          // One rule for every surface (utils/stackLayout,
                          // twin of jobq-api stack_layout.py): assembly units,
                          // the first spots carry the partial top layer.
                          const zoneLay = zoneStackLayout({ ...z, tile_cols: zoneTC, tile_rows: zoneTR });
                          let cellSeq = 0;
                          for (let r = 0; r < trSvg; r++) {
                            for (let c = 0; c < tcSvg; c++) {
                              const cx = zx + c * zCellW;
                              const cy = zy + r * zCellH;
                              const cellLayers = cellHeight(zoneLay, cellSeq);
                              cellSeq += 1;
                              cells.push(
                                <g key={`${z.zone_id || z.part_id}-${r}-${c}`}>
                                  <rect x={cx} y={cy} width={zCellW} height={zCellH}
                                    fill={color} fillOpacity={0.85}
                                    stroke="#222" strokeWidth={0.3} />
                                  {Math.min(zCellW, zCellH) >= 8 && (
                                    <text x={cx + zCellW / 2} y={cy + zCellH / 2 + cellFontSize / 3}
                                      textAnchor="middle" fontSize={cellFontSize}
                                      fontWeight="bold" fill="#111">
                                      {cellLayers || ''}
                                    </text>
                                  )}
                                </g>
                              );
                            }
                          }
                        }
                        return cells;
                      })()}
                      {/* Stack outline overlay — drawn after the cells
                          so the canonical (rotated) box boundary sits
                          on top. If a cell ever overflows the outline
                          (rotation math regression, bad zone data),
                          the operator sees colored bits poking past
                          the dashed border. */}
                      {isDirect && (showTileGrid || directStripFallback) && (
                        <rect x={x} y={y} width={bw} height={bh}
                          fill="none"
                          stroke={stroke} strokeWidth={1.3}
                          strokeDasharray="3 2"
                          pointerEvents="none" />
                      )}
                      {/* Center label (S## / B##) — drawn last so it sits
                          on top of the cell grid. Hidden when grid cells
                          are detailed enough that the label would overlap. */}
                      {!directStripFallback && (!showTileGrid || Math.min(bw, bh) >= 50) && (
                        <text x={x + bw / 2} y={y + bh / 2 + 3} textAnchor="middle"
                          fontSize={fontSize}
                          fontWeight="bold" fill="#111"
                          style={showTileGrid ? { paintOrder: 'stroke', stroke: '#fff', strokeWidth: 2 } : null}>
                          {label}
                        </text>
                      )}
                      {bx.status === 'Sealed' && !isDirect && (
                        <text x={x + bw - 6} y={y + 10} textAnchor="end" fontSize="10" fill="#2b8a3e">
                          ✓
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
              <div className="small text-muted mt-1" style={{ maxWidth: Math.max(w + 8, 280) }}>
                {(() => {
                  // One row per physical container (box or pallet-direct
                  // stack). Each row carries the S#/B# identifier with a
                  // colored tab matching the SVG cell, the part product_id,
                  // qty, layout (cols × layers — disambiguates the legacy
                  // "× N high" wording, which operators read as either
                  // pieces, layers, or inches), physical height, and
                  // weight. Lets the shop floor read the pallet card
                  // without cross-referencing the box-detail cards below.
                  // A nested box takes no floor space: it is listed inside its host carton's card below.
                  const list = (p.boxes || []).filter(bx => !isNested(bx));
                  if (!list.length) {
                    return <span className="fst-italic">empty pallet</span>;
                  }

                  const fmtDim = (n) => {
                    // Whole inches → "17"; fractional → up to 2 decimals
                    // (Uline carton dims like 24.75 should not round to 24.8).
                    const v = Number(n) || 0;
                    if (Number.isInteger(v)) return `${v}`;
                    return v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
                  };

                  const rows = list.flatMap((bx) => {
                    const wt = Math.round(bx.total_planned_weight || 0);
                    const wtTxt = wt > 0 ? ` · ${wt} lb` : '';

                    // WIP staging lane: one compact summary row (N parts → next
                    // station). The per-part manifest is in the detail card.
                    if (p.is_wip && bx.is_pallet_direct) {
                      const zc = (bx.zones || []).length;
                      const displayNum = bx.display_box_num ?? bx.box_num;
                      return [{
                        key: `S${bx.box_num}`,
                        idBadge: bx.short_id ? bx.short_id.replace(/^[A-Z]+-/, '') : `S${displayNum}`,
                        swatch: '#fd7e14',
                        partLabel: `${zc} part${zc === 1 ? '' : 's'} staged`,
                        details: `→ ${p.wip_next_station || 'STAGE'}${wtTxt}`,
                      }];
                    }

                    if (bx.is_pallet_direct) {
                      const dzones = bx.zones || [];
                      // Manually-consolidated stack (>1 part): one row per zone so
                      // every part is listed. The old zones[0]-only summary showed
                      // just the first part and (mis)attributed the whole stack's
                      // weight to it. Stack-total height + weight ride on row 0.
                      if (dzones.length > 1) {
                        const heightTxtAll = bx.outer_h ? ` · ${fmtDim(bx.outer_h)}″ tall` : '';
                        return dzones.map((z, i) => {
                          const zIsAssembly = !!z.assembly_id && (z.primary_part_quantity || 1) > 1;
                          const qty = zIsAssembly
                            ? (z.assembly_planned_count ?? Math.floor((z.planned_qty || 0) / Math.max(1, z.primary_part_quantity)))
                            : (z.planned_qty || 0);
                          const swatch = SO_PKG_COLOR_PALETTE[(z.color_idx || 0) % SO_PKG_COLOR_PALETTE.length];
                          const displayNum = bx.display_box_num ?? bx.box_num;
                          const idBadge = i === 0
                            ? (bx.short_id ? bx.short_id.replace(/^[A-Z]+-/, '') : `S${displayNum}`)
                            : '';
                          return {
                            key: `S${bx.box_num}-z${z.zone_id || z.part_id || i}`,
                            idBadge,
                            swatch,
                            partLabel: zIsAssembly && z.assembly_name
                              ? z.assembly_name
                              : (z.product_id || `Part ${z.part_id || '?'}`),
                            details: `${qty} ${zIsAssembly ? 'assemblies' : 'pcs'}${i === 0 ? heightTxtAll + wtTxt : ''}`,
                          };
                        });
                      }
                      const z = dzones[0] || {};
                      // Assembly zones: count + layers in finished-assembly
                      // units ("10 assemblies", not "40 pcs" of the primary).
                      const zIsAssembly = !!z.assembly_id && (z.primary_part_quantity || 1) > 1;
                      const qty = zIsAssembly
                        ? (z.assembly_planned_count ?? Math.floor((z.planned_qty || 0) / Math.max(1, z.primary_part_quantity)))
                        : (z.planned_qty || 0);
                      // The routing slip / status label / kiosk wording
                      // (utils/stackLayout): "36 stacks × 9 high + 12 stacks × 8 high".
                      const layoutPhrase = stackPhrase(stackLayout(qty, z.tile_cols, z.tile_rows, z.stack_height));
                      const heightTxt = bx.outer_h
                        ? ` · ${fmtDim(bx.outer_h)}″ tall`
                        : '';
                      const swatch = SO_PKG_COLOR_PALETTE[
                        (z.color_idx || 0) % SO_PKG_COLOR_PALETTE.length
                      ];
                      // Mark short-but-sealable stacks so the operator
                      // sees the policy-accepted gap at the pallet-map
                      // glance level (no need to scroll to the box card).
                      const eff = bx.effective_status || bx.status;
                      const policyHint = eff === 'FullPerPolicy'
                        ? ' · short ✓ (policy)' : '';
                      const displayNum = bx.display_box_num ?? bx.box_num;
                      const idBadge = bx.short_id
                        ? bx.short_id.replace(/^[A-Z]+-/, '')
                        : `S${displayNum}`;
                      return [{
                        key: `S${bx.box_num}`,  // key uses raw box_num for React stability
                        idBadge,
                        swatch,
                        partLabel: zIsAssembly && z.assembly_name
                          ? z.assembly_name
                          : (z.product_id || `Part ${z.part_id || '?'}`),
                        details: `${qty} ${zIsAssembly ? 'assemblies' : 'pcs'}${layoutPhrase ? ` · ${layoutPhrase}` : ''}${heightTxt}${wtTxt}${policyHint}`,
                      }];
                    }

                    // Boxed container.
                    const zones = bx.zones || [];
                    const totalQty = zones.reduce(
                      (a, z) => a + (z.planned_qty || 0), 0
                    );
                    const distinctParts = Array.from(
                      new Set(zones.map((z) => z.product_id).filter(Boolean))
                    );
                    // Interior dims match the printed Uline catalog name
                    // (e.g. S-16737 = "14×8×4″"). Outer dims add ~0.75″/side
                    // for pallet-overhang math and confuse operators trying
                    // to grab the right carton off the shelf.
                    const dims = bx.interior_l
                      ? ` ${fmtDim(bx.interior_l)}×${fmtDim(bx.interior_w)}×${fmtDim(bx.interior_h)}″`
                      : '';
                    const skuLabel = bx.sku ? `${bx.sku}${dims}` : `box${dims}`;
                    const partLabel = distinctParts.length === 1
                      ? `${distinctParts[0]} · ${skuLabel}`
                      : skuLabel;
                    const partsCountTxt = distinctParts.length > 1
                      ? ` · ${distinctParts.length} parts`
                      : '';
                    const swatch = SO_BOX_SKU_COLOR[bx.sku] || '#adb5bd';
                    // FullPerPolicy hint: box is short of planned but
                    // sealable per yield policy (line min met cumulatively
                    // + production done). Show actual qty + short marker
                    // instead of the bare planned-sum.
                    const eff = bx.effective_status || bx.status;
                    const totalActualBox = zones.reduce(
                      (a, z) => a + (z.actual_qty || 0), 0
                    );
                    const shortQty = Math.max(0, totalQty - totalActualBox);
                    const detailsTxt = eff === 'FullPerPolicy'
                      ? `${totalActualBox} pcs · ${shortQty} short ✓ (policy)${partsCountTxt}${wtTxt}`
                      : `${totalQty} pcs${partsCountTxt}${wtTxt}`;
                    const displayNumBoxed = bx.display_box_num ?? bx.box_num;
                    const idBadgeBoxed = bx.short_id
                      ? bx.short_id.replace(/^[A-Z]+-/, '')
                      : `B${displayNumBoxed}`;
                    return [{
                      key: `B${bx.box_num}`,  // key uses raw box_num for React stability
                      idBadge: idBadgeBoxed,
                      swatch,
                      partLabel,
                      details: detailsTxt,
                    }];
                  });

                  return (
                    <>
                      {rows.map((r) => (
                        <div
                          key={r.key}
                          className="d-flex align-items-baseline flex-wrap"
                          style={{ fontSize: '0.92em', lineHeight: 1.4, marginTop: 1 }}
                        >
                          <span
                            className="me-1 px-1"
                            style={{
                              fontFamily: 'monospace',
                              fontSize: '0.85em',
                              fontWeight: 700,
                              color: '#212529',
                              borderLeft: `4px solid ${r.swatch}`,
                              background: '#f8f9fa',
                              borderRadius: '2px',
                              minWidth: '2.4em',
                              display: 'inline-block',
                              textAlign: 'center',
                            }}
                          >
                            {r.idBadge}
                          </span>
                          <span className="text-dark me-1 fw-semibold">
                            {r.partLabel}
                          </span>
                          <span className="text-muted">· {r.details}</span>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Per-box detail list grouped by pallet.
// Banner that surfaces parts whose thickness wasn't recorded when the plan
// was generated. The packaging stacker falls back to a pessimistic 0.25"
// default for those zones, which works but yields sub-optimal pallet
// counts. Shown above the pallet map so the office sees it during plan
// review and can fix the part data before committing / shipping.
const SOPackagingThicknessWarning = ({ plan }) => {
  const pallets = plan.pallets || [];
  const missing = new Map(); // product_id -> description
  for (const p of pallets) {
    for (const b of p.boxes || []) {
      for (const z of b.zones || []) {
        if (z.thickness_assumed && z.product_id && !missing.has(z.product_id)) {
          missing.set(z.product_id, z.description || '');
        }
      }
    }
  }
  if (missing.size === 0) return null;
  const sample = Array.from(missing.keys()).slice(0, 5).join(', ');
  const more = missing.size > 5 ? ` and ${missing.size - 5} more` : '';
  return (
    <div className="alert alert-warning py-2 small mb-3">
      <i className="bi bi-exclamation-triangle-fill me-2"></i>
      <strong>{missing.size} part{missing.size === 1 ? '' : 's'} missing thickness data</strong>
      {' — '}plan used a conservative <code>0.25″</code> assumption for stacking.
      Pallet counts will be sub-optimal until material/thickness are set on each
      part, then regenerate this plan.
      <div className="mt-1 text-muted">Affected: {sample}{more}</div>
    </div>
  );
};

// Assembly zones laid out from the PRIMARY PART's dims because the assembly
// has no shipping profile (final assembled dims) — the layout is unreliable
// for welded products (the SO-1030 "6,204 lb in one carton" pathology).
// Office fixes: Assemblies Manager → Shipping Profile, then regenerate.
const SOPackagingAssemblyDimsWarning = ({ plan }) => {
  const pallets = plan.pallets || [];
  const missing = new Map(); // assembly_name -> primary product_id
  for (const p of pallets) {
    for (const b of p.boxes || []) {
      for (const z of b.zones || []) {
        if (z.assembly_id && z.assembly_dims_assumed) {
          const key = z.assembly_name || `Assembly #${z.assembly_id}`;
          if (!missing.has(key)) missing.set(key, z.product_id || '');
        }
      }
    }
  }
  if (missing.size === 0) return null;
  const sample = Array.from(missing.keys()).slice(0, 5).join(', ');
  const more = missing.size > 5 ? ` and ${missing.size - 5} more` : '';
  return (
    <div className="alert alert-danger py-2 small mb-3">
      <i className="bi bi-box-seam me-2"></i>
      <strong>{missing.size} assembl{missing.size === 1 ? 'y is' : 'ies are'} missing final shipping dimensions</strong>
      {' — '}this plan packed them using the primary part's dims, so the box/pallet
      layout is unreliable for the finished product. Set the Shipping Profile
      (L×W×H) on the assembly in the Assemblies Manager, then regenerate this plan.
      <div className="mt-1">Affected: {sample}{more}</div>
    </div>
  );
};

// Fixed-shape counts for the shipping progress strip above the plan body.
const SOPackagingShipProgress = ({ plan }) => {
  const pallets = plan.pallets || [];
  if (!pallets.length) return null;
  const isPickup = plan.fulfillment_type === 'pickup';
  const building     = pallets.filter(p => (p.status || 'Building') === 'Building').length;
  const ready        = pallets.filter(p => p.status === 'Ready').length;
  const staged       = pallets.filter(p => p.status === 'Staged').length;
  const shipped      = pallets.filter(p => p.status === 'Shipped').length;
  const consolidated = pallets.filter(p => p.status === 'Consolidated').length;
  // "Total pallets" excludes Consolidated — those don't ship under their
  // own pallet number, their boxes ride on the host pallet's freight slot.
  const nShipping = pallets.length - consolidated;
  // Boxes + stacks filled across the plan (Full + Sealed count as "done
  // packing"). Pallet-direct stacks aren't boxes — count them separately
  // so the label reads "3/3 stacks packed" on a stacks-only pallet
  // instead of the misleading "3/3 boxes packed".
  // A nested box is packed and sealed with its carton: count cartons and stacks only.
  const allBoxes = pallets.flatMap(p => p.boxes || []).filter(b => !isNested(b));
  const nBoxesTotal = allBoxes.length;
  // Use effective_status (yield-policy + live-zone aware) so a box the backend
  // reconciled to Full (defect shrink) but whose stored status column lags at
  // 'Partial' still counts as packed — otherwise the ratio contradicts the
  // green Full badges on the same screen.
  const nBoxesDone = allBoxes.filter(b => {
    const eff = b.effective_status || b.status;
    return eff === 'Full' || eff === 'FullPerPolicy' || eff === 'Sealed';
  }).length;
  const nRealBoxes  = allBoxes.filter(isCarton).length;
  const nStacks     = allBoxes.filter(b =>  b.is_pallet_direct).length;
  const packedNoun = (() => {
    if (nRealBoxes && nStacks) return 'item' + (nBoxesTotal === 1 ? '' : 's');
    if (nRealBoxes)            return 'box'  + (nBoxesTotal === 1 ? '' : 'es');
    if (nStacks)               return 'stack' + (nBoxesTotal === 1 ? '' : 's');
    return 'item' + (nBoxesTotal === 1 ? '' : 's');
  })();
  const packedBreakdown = (nRealBoxes && nStacks)
    ? ` (${nRealBoxes} box${nRealBoxes === 1 ? '' : 'es'}, ${nStacks} stack${nStacks === 1 ? '' : 's'})`
    : '';
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 border rounded bg-light small">
      <span className="fw-bold"><i className="bi bi-truck me-1"></i>{isPickup ? 'Pickup progress:' : 'Shipping progress:'}</span>
      <span>
        <span className="badge bg-secondary me-1">{building}</span>
        building
      </span>
      <span>
        <span className="badge bg-warning text-dark me-1">{ready}</span>
        ready {isPickup ? 'to stage' : 'to ship'}
      </span>
      {staged > 0 && (
        <span>
          <span className="badge bg-info text-dark me-1">{staged}</span>
          awaiting pickup
        </span>
      )}
      <span>
        <span className="badge bg-success me-1">{shipped}</span>
        {isPickup ? 'picked up' : 'shipped'}
      </span>
      {consolidated > 0 && (
        <span title="Pallets whose boxes were moved onto another pallet at the loading bay. Don't count toward the freight pallet total.">
          <span className="badge bg-info me-1">{consolidated}</span>
          consolidated
        </span>
      )}
      <span className="text-muted">· {nShipping} total pallet{nShipping === 1 ? '' : 's'}</span>
      {nBoxesTotal > 0 && (
        <span className="text-muted ms-2"
              title={packedBreakdown ? `${nRealBoxes} corrugated box(es) + ${nStacks} pallet-direct stack(s)` : null}>
          <i className="bi bi-box2 me-1"></i>{nBoxesDone}/{nBoxesTotal} {packedNoun} packed{packedBreakdown}
        </span>
      )}
      {plan.status === 'Complete' && (
        <span className="badge bg-success ms-auto">
          <i className="bi bi-check2-circle me-1"></i>Plan complete
        </span>
      )}
    </div>
  );
};

// Per-pallet header + action bar used inside the Boxes & Stacks list.
// Seal/Ship/Reprint/Stage/Confirm-Pickup buttons branch on the pallet's
// current status AND the SO's fulfillmentType ('shipping' | 'pickup' |
// 'delivery'). For pickup orders the Ready→Shipped path is split into
// Ready→Staged (Mark Ready for Pickup) → Shipped (Confirm Pickup).
const SOPalletActionBar = ({ pallet, busy, onSeal, onShip, onStage, onConfirmPickup, onReprintLabels, onReprintManifest, onPrintPartLabels, onPrintPalletStatusLabel, onOpenMoveModal, onUnsealPallet, onConvertContainerType, fulfillmentType, openProblems = [] }) => {
  const status = pallet.status || 'Building';
  const isPickup = fulfillmentType === 'pickup';
  // 'Pallet' (default) ships on a wood pallet; 'Box' is a single loose
  // carton with no pallet. Drives manifest/label/email wording at seal time.
  const containerType = pallet.container_type || 'Pallet';
  const isBoxContainer = containerType === 'Box';
  const isWip = !!pallet.is_wip;
  // A holding pallet (a laser task's WIP pallet, wip_task_id): never sealed here; shipped as it is or packed from Prepare shipment.
  const isTaskWip = isTaskWipPallet(pallet);
  const wipNextStation = isTaskWip ? taskWipStation(pallet) : (pallet.wip_next_station || 'next station');
  const containerKey = `container_${pallet.pallet_id}`;
  // Pallet-direct stacks need a real pallet — block the Box conversion.
  const hasPalletDirect = (pallet.boxes || []).some(b => b.is_pallet_direct);
  const statusColor = status === 'Shipped'      ? 'success'
                    : status === 'Staged'       ? 'info'
                    : status === 'Ready'        ? 'warning'
                    : status === 'Consolidated' ? 'info'
                    :                             'secondary';
  // Don't let the operator seal an empty pallet — require at least one
  // zone with actual_qty > 0 across its boxes. The backend enforces this
  // too, but disabling the button is better UX than a 400.
  const sealState = palletSealState(pallet); // features/salesOrders/palletRules.js (shared with the phone)
  const { anyPacked } = sealState;
  // In the per-box flow (2026-04-19+), Seal pallet is a finisher — it
  // only creates the pallet + stack labels. Every box that has parts in
  // it must be sealed individually first (via the Seal box button on
  // each box card). Gate the pallet-seal button on that so the operator
  // can't skip the box-seal step by accident. The backend falls back to
  // auto-sealing unsealed-but-full boxes for safety, but the UI rule is
  // "seal boxes first, then finalize the pallet."
  const { realBoxes, unsealedBoxesWithContent } = sealState;
  const palletSealGatedBySealBoxes = sealState.gatedBySealBoxes;
  const sealKey = `seal_${pallet.pallet_id}`;
  const shipKey = `ship_${pallet.pallet_id}`;
  const stageKey = `stage_${pallet.pallet_id}`;
  const confirmPickupKey = `confirm_pickup_${pallet.pallet_id}`;
  const reprintLabelsKey = `reprint_labels_${pallet.shipment_group_id || ''}`;
  const reprintManifestKey = `reprint_manifest_${pallet.shipment_group_id || ''}`;
  const partLabelsKey = `part_labels_${pallet.shipment_group_id || ''}_${pallet.pallet_num}`;
  const sealedAt = pallet.sealed_at ? new Date(pallet.sealed_at).toLocaleString() : null;
  const stagedAt = pallet.staged_at ? new Date(pallet.staged_at).toLocaleString() : null;
  const shippedAt = pallet.shipped_at ? new Date(pallet.shipped_at).toLocaleString() : null;
  const pickedUpAt = pallet.picked_up_at ? new Date(pallet.picked_up_at).toLocaleString() : null;
  const dims = (pallet.length_in && pallet.width_in)
    ? `${Math.round(pallet.length_in)}×${Math.round(pallet.width_in)}`
    : null;

  const moveKey = `move_${pallet.pallet_id}`;
  // Boxes eligible to consolidate. Two modes:
  //   - Building: Empty/Partial/Full (Sealed-on-Building needs ride-along).
  //     Includes Empty so operators can rebalance the plan BEFORE production
  //     starts — backend allowed_box_statuses on the Building path is
  //     ("Empty","Partial","Full"), so this matches.
  //   - Ready: only Sealed boxes (already wrapped, repoint shipment data).
  const eligibleBoxesOnPallet = (pallet.boxes || []).filter(b => (
    status === 'Building'
      ? b.status !== 'Sealed'
      : b.status === 'Sealed'
  ));
  const canMoveBoxes = (status === 'Ready' || status === 'Building')
    && eligibleBoxesOnPallet.length > 0
    && typeof onOpenMoveModal === 'function';

  // Box-only containers with a single box collapse identity to the
  // BOX-XXXX — there's no wood pallet to label, so the PLT-XXXX is
  // operational noise. Keep PLT-XXXX visible only when multiple boxes
  // share a box-only container (rare; defensive).
  const boxOnlySoloShortId = (isBoxContainer && realBoxes.length === 1)
    ? (realBoxes[0].short_id || null) : null;
  const headerLabel = boxOnlySoloShortId || pallet.short_id || `P${pallet.pallet_num}`;
  const headerLabelTitle = boxOnlySoloShortId
    ? "Single loose carton — this is the only physical artifact. Printed on the box label and used on every manifest, slip, and customer email."
    : "Stable global pallet ID — printed on the pallet label and used on every manifest, slip, radio call, and Slack message. Replaces the gap-prone P# ordinal.";

  return (
    <div className={`d-flex flex-wrap align-items-center gap-2 mb-1 ${status === 'Consolidated' ? 'opacity-50' : ''}`}>
      <span className="fw-bold">{isBoxContainer ? 'Box' : 'Pallet'}</span>
      <span
        className="badge bg-dark"
        style={{ fontFamily: 'monospace', letterSpacing: '0.5px', fontSize: '0.95em' }}
        title={headerLabelTitle}
      >
        {headerLabel}
      </span>
      {isBoxContainer ? (
        <span
          className="badge bg-info text-dark"
          title="This shipment leaves the dock as a single loose carton — no wood pallet underneath. Manifest, labels, and customer email all read 'Box' instead of 'Pallet'."
        >
          <i className="bi bi-box me-1"></i>BOX — NO PALLET
        </span>
      ) : (
        dims && <span className="badge bg-light text-dark">{dims}″</span>
      )}
      <span className={`badge bg-${statusColor}`}>{status}</span>
      {pallet.beacon_id && (
        <span
          className="badge bg-light text-dark border"
          style={{ fontFamily: 'monospace' }}
          title={pallet.beacon_light ? `${pallet.beacon_id} is on this pallet and flashing (${pallet.beacon_light})` : `${pallet.beacon_id} is on this pallet`}
          data-testid={`so-pallet-beacon-${pallet.pallet_id}`}
        >
          {pallet.beacon_light && (
            <span className="d-inline-block rounded-circle bg-success me-1" style={{ width: 7, height: 7 }} aria-label="lit" />
          )}
          {pallet.beacon_id}
        </span>
      )}
      {status !== 'Consolidated' && !isHoldingPallet(pallet) && (
        // The pallet check screen (what a beacon's QR opens) + a chip while a problem reported there is open. A holding
        // pallet's sits on its Prepare shipment card above, with Move pieces (Mike 2026-10-29).
        <PalletCheckLink palletId={pallet.pallet_id} openProblems={openProblems} />
      )}
      {status === 'Consolidated' && pallet.consolidated_into_short_id && (
        <span className="small text-info">
          <i className="bi bi-arrow-right me-1"></i>
          Boxes moved onto <strong>{pallet.consolidated_into_short_id}</strong>
        </span>
      )}
      {sealedAt && (
        <span className="small text-muted">
          <i className="bi bi-lock me-1"></i>Sealed {sealedAt}
          {pallet.sealed_by ? ` by ${pallet.sealed_by}` : ''}
        </span>
      )}
      {stagedAt && (
        <span className="small text-info">
          <i className="bi bi-box-seam me-1"></i>Awaiting pickup since {stagedAt}
          {pallet.staged_by ? ` (staged by ${pallet.staged_by})` : ''}
        </span>
      )}
      {pickedUpAt && (
        <span className="small text-success">
          <i className="bi bi-person-check me-1"></i>Picked up {pickedUpAt}
          {pallet.picked_up_by ? ` by ${pallet.picked_up_by}` : ''}
        </span>
      )}
      {shippedAt && !pickedUpAt && (
        <span className="small text-muted">
          <i className="bi bi-truck me-1"></i>Shipped {shippedAt}
          {pallet.shipped_by ? ` by ${pallet.shipped_by}` : ''}
        </span>
      )}
      {pallet.pod?.delivered_at && (
        <span className="small text-success" title="From the proof-of-delivery record">
          <i className="bi bi-check2-circle me-1"></i>Delivered {new Date(pallet.pod.delivered_at).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          {pallet.pod.delivered_by ? ` to ${pallet.pod.delivered_by}` : ''}
        </span>
      )}
      {pallet.pod?.photo_count > 0 && (
        <span className="badge bg-light text-dark border" title="Proof-of-delivery photos on file for this pallet (see the Proof of delivery card)">
          <i className="bi bi-camera me-1"></i>{pallet.pod.photo_count}
        </span>
      )}
      {isTaskWip && (
        <span
          className="badge bg-warning text-dark"
          title={`${holdingPalletText(pallet)}: the order's pieces of that laser task travel on it to each station. It is not sealed here: ship it as it is or pack its pieces from Prepare shipment.`}
        >
          <i className="bi bi-arrow-right-circle me-1"></i>
          {taskWipHeader(pallet)}
        </span>
      )}
      {isWip && !isTaskWip && (
        <span
          className="badge bg-warning text-dark"
          title={`This is a WIP staging pallet. Parts on it still need ${wipNextStation} before they can ship. After ${wipNextStation} completes, move zones to a real shipping pallet via Edit mode and delete this WIP pallet.`}
        >
          <i className="bi bi-arrow-right-circle me-1"></i>
          WIP — staging for {wipNextStation}
        </span>
      )}
      <div className="ms-auto d-flex flex-wrap gap-1">
        {/* "Print status label" is available in EVERY pallet status — the
            label reflects the pallet's current container rollup at the
            moment of printing, so it's useful both for fresh Building
            pallets ("here's what's queued") and for Sealed/Shipped
            pallets that operators want to reprint. Goes to the laser-Pi
            thermal printer via /print/queue (NOT the browser print
            dialog), so the operator gets a 4×6 ZP 505 label even if
            they're standing at the office computer. */}
        {typeof onPrintPalletStatusLabel === 'function' && (
          <button
            className="btn btn-sm btn-outline-info"
            disabled={!!busy}
            onClick={() => onPrintPalletStatusLabel(pallet.pallet_id, pallet.short_id)}
            title="Print a fresh 4×6 thermal status label for this pallet at the laser-Pi printer. Reflects live container rollup (IN PROGRESS vs READY TO SEAL banner, per-container qty status). Useful when the auto-fired label is lost/torn or you want to re-label after rearranging contents."
          >
            {busy === `status_label_pallet_status_label_${pallet.short_id || pallet.pallet_id}`
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Queuing…</>
              : <><i className="bi bi-printer me-1"></i>Status label</>}
          </button>
        )}
        {status === 'Building' && !isWip && (
          <>
            {typeof onConvertContainerType === 'function' && (
              <button
                className={`btn btn-sm ${isBoxContainer ? 'btn-outline-secondary' : 'btn-outline-info'}`}
                disabled={!!busy || (!isBoxContainer && hasPalletDirect)}
                onClick={() => onConvertContainerType(
                  pallet.pallet_id,
                  pallet.pallet_num,
                  isBoxContainer ? 'Pallet' : 'Box',
                )}
                title={isBoxContainer
                  ? 'Convert back to a palletized shipment — manifest, labels, and customer email will use Pallet wording at seal time.'
                  : hasPalletDirect
                    ? 'Cannot convert to a loose box — this pallet has pallet-direct stacks (loose parts on the wood) that need a real pallet under them. Move the stacks to another pallet first.'
                    : 'Convert to a single loose box (no pallet). Manifest, labels, and customer email will read "Box" instead of "Pallet" at seal time.'}
              >
                {busy === containerKey
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Converting...</>
                  : isBoxContainer
                    ? <><i className="bi bi-pallet me-1"></i>Use pallet</>
                    : <><i className="bi bi-box me-1"></i>Box only (no pallet)</>}
              </button>
            )}
            {canMoveBoxes && (
              <button
                className="btn btn-sm btn-outline-warning"
                disabled={!!busy}
                onClick={() => onOpenMoveModal(pallet)}
                title="Move/merge boxes or pallet-direct stacks from this pallet onto another Building pallet (same customer). Useful when you realize two pallets' contents will physically fit together — do this BEFORE sealing so the combined pallet gets one label."
              >
                {busy === moveKey
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Moving...</>
                  : <><i className="bi bi-arrows-collapse me-1"></i>Move/Merge</>}
              </button>
            )}
            <button
              className="btn btn-sm btn-warning"
              disabled={!!busy || !anyPacked || palletSealGatedBySealBoxes}
              title={!anyPacked
                ? 'No parts packed on this pallet yet (all zone actual_qty = 0).'
                : palletSealGatedBySealBoxes
                  ? `Seal the remaining ${unsealedBoxesWithContent.length} box(es) first, then finalize the pallet. "Seal pallet" emits the pallet + stack labels only — boxes get their own labels at box-seal time.`
                  : 'Finalize this pallet — records any pallet-direct stacks in the shipment system and opens the pallet label + stack labels PDF.'}
              onClick={() => onSeal(pallet.pallet_id, pallet.pallet_num)}
            >
              {busy === sealKey
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Sealing...</>
                : <><i className="bi bi-lock-fill me-1"></i>Seal pallet</>}
            </button>
          </>
        )}
        {/* WIP-staging pallet, Building: the backend WIP gate
            (_enforce_wip_staging_gate) auto-promotes this pallet to a normal
            shipping pallet and allows the seal once every post-laser step
            (Finish/Brake/Fab/...) for the staged parts is marked complete in
            JobQ. Previously the Seal button was hidden for is_wip pallets, so
            that backend release path was unreachable and operators were
            dead-ended (had to hand-move zones via Edit mode or ship via the
            legacy Shipping page). Show the button and let the gate decide — if
            production is still open it returns a 400 listing what's pending,
            which handleSealPallet surfaces. */}
        {status === 'Building' && isWip && !isTaskWip && (
          <button
            className="btn btn-sm btn-warning"
            disabled={!!busy || !anyPacked}
            title={!anyPacked
              ? 'No parts packed on this pallet yet (all zone actual_qty = 0).'
              : `Release this WIP staging pallet and seal it. Allowed once every post-laser step (${wipNextStation}) for the staged parts is marked complete in JobQ. If production is still open you'll get a message listing what's pending — finish those steps, then seal.`}
            onClick={() => onSeal(pallet.pallet_id, pallet.pallet_num)}
          >
            {busy === sealKey
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Sealing...</>
              : <><i className="bi bi-lock-fill me-1"></i>Seal pallet (release from staging)</>}
          </button>
        )}
        {status === 'Ready' && (
          <>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={!!busy}
              onClick={() => onReprintLabels(pallet.shipment_group_id, pallet.pallet_num)}
              title="Reprint the pallet label + per-box labels + per-stack labels for this pallet."
            >
              {busy === reprintLabelsKey
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Labels...</>
                : <><i className="bi bi-printer me-1"></i>Reprint labels</>}
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              disabled={!!busy}
              onClick={() => onPrintPartLabels(pallet.shipment_group_id, pallet.pallet_num)}
              title="Print 4×6 part labels (image, customer, material/finish/dimensions/weight, qty) for every part on this pallet — both pallet-direct stacks and per-box items. Tape one to each part."
            >
              {busy === partLabelsKey
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Part labels...</>
                : <><i className="bi bi-tags me-1"></i>Part labels</>}
            </button>
            {canMoveBoxes && (
              <button
                className="btn btn-sm btn-outline-warning"
                disabled={!!busy}
                onClick={() => onOpenMoveModal(pallet)}
                title="Move/merge boxes or pallet-direct stacks from this pallet onto another Ready pallet. Same customer only. Useful when a small box or stack can ride along with another pallet to save freight space."
              >
                {busy === moveKey
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Moving...</>
                  : <><i className="bi bi-arrows-collapse me-1"></i>Move/Merge</>}
              </button>
            )}
            {typeof onUnsealPallet === 'function' && (
              <button
                className="btn btn-sm btn-outline-danger"
                disabled={!!busy}
                onClick={() => onUnsealPallet(pallet.pallet_id, pallet.pallet_num)}
                title="Unseal this pallet — reverses the seal so you can add or move more boxes onto it, then re-seal. Blocked once shipped. Any printed labels become stale."
              >
                {busy === `unseal_pallet_${pallet.pallet_id}`
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Unsealing...</>
                  : <><i className="bi bi-unlock me-1"></i>Unseal</>}
              </button>
            )}
            {isPickup ? (
              <button
                className="btn btn-sm btn-warning"
                disabled={!!busy || typeof onStage !== 'function'}
                onClick={() => onStage(pallet.pallet_id, pallet.pallet_num)}
                title="Mark this pallet ready for customer pickup. Prompts for PIN and emails the customer the 'Ready for Pickup' notification with the manifest. Pallet status flips to Staged; parts stay on-floor until customer arrives and you click Confirm Pickup."
              >
                {busy === stageKey
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Staging...</>
                  : <><i className="bi bi-box-seam me-1"></i>Mark Ready for Pickup</>}
              </button>
            ) : (
              <button
                className="btn btn-sm btn-success"
                disabled={!!busy}
                onClick={() => onShip(pallet.pallet_id, pallet.pallet_num)}
                title="Finalize this pallet as shipped. Prompts for PIN and emails the customer the shipping manifest."
              >
                {busy === shipKey
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Shipping...</>
                  : <><i className="bi bi-truck me-1"></i>Ship pallet</>}
              </button>
            )}
          </>
        )}
        {status === 'Staged' && (
          <>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={!!busy}
              onClick={() => onReprintLabels(pallet.shipment_group_id, pallet.pallet_num)}
              title="Reprint the pallet label + per-box labels + per-stack labels for this pallet."
            >
              {busy === reprintLabelsKey
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Labels...</>
                : <><i className="bi bi-printer me-1"></i>Reprint labels</>}
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              disabled={!!busy}
              onClick={() => onPrintPartLabels(pallet.shipment_group_id, pallet.pallet_num)}
              title="Reprint 4×6 part labels for every part on this pallet."
            >
              {busy === partLabelsKey
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Part labels...</>
                : <><i className="bi bi-tags me-1"></i>Part labels</>}
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={!!busy}
              onClick={() => onReprintManifest(pallet.shipment_group_id, pallet.pallet_num, status)}
              title="Reprint the pickup manifest. Useful if customer needs another copy at handoff."
            >
              {busy === reprintManifestKey
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Manifest...</>
                : <><i className="bi bi-file-earmark-text me-1"></i>Manifest</>}
            </button>
            <button
              className="btn btn-sm btn-success"
              disabled={!!busy || typeof onConfirmPickup !== 'function'}
              onClick={() => onConfirmPickup(pallet.pallet_id, pallet.pallet_num)}
              title="Customer is here. Confirm pickup of this pallet — flips it from Staged to Shipped, stamps picked_up_at/by, no email."
            >
              {busy === confirmPickupKey
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Confirming...</>
                : <><i className="bi bi-person-check me-1"></i>Confirm Pickup</>}
            </button>
          </>
        )}
        {status === 'Shipped' && (
          <>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={!!busy}
              onClick={() => onReprintLabels(pallet.shipment_group_id, pallet.pallet_num)}
            >
              {busy === reprintLabelsKey
                ? <><span className="spinner-border spinner-border-sm me-1"></span>...</>
                : <><i className="bi bi-printer me-1"></i>Labels</>}
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              disabled={!!busy}
              onClick={() => onPrintPartLabels(pallet.shipment_group_id, pallet.pallet_num)}
              title="Reprint part labels for this pallet."
            >
              {busy === partLabelsKey
                ? <><span className="spinner-border spinner-border-sm me-1"></span>...</>
                : <><i className="bi bi-tags me-1"></i>Part labels</>}
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={!!busy}
              onClick={() => onReprintManifest(pallet.shipment_group_id, pallet.pallet_num, status)}
            >
              {busy === reprintManifestKey
                ? <><span className="spinner-border spinner-border-sm me-1"></span>...</>
                : <><i className="bi bi-file-earmark-text me-1"></i>Manifest</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// SO-plan analog of the cut-sheet `PalletPositionEditor`. Renders an
// SVG of one pallet, with every box (boxed carton + pallet-direct
// stack) as a draggable rect. Snap-to-1″, bounds clamp, window-level
// mouse handlers so a drag continues outside the rect. Each box has
// a ↻ rotate-90° overlay (top-right corner) that toggles
// `pallet_rotated` and re-clamps the position into the new bounds.
// Live overlap / overhang detection paints conflicting rects red with
// a dashed outline; the Save button is disabled until conflicts clear
// (the backend also rejects with 400 when allow_conflicts=false, so
// this is a UX gate not a security one).
//
// The display uses display_box_num (compact rank-within-pallet) for
// the human label; the raw box_num stays internal for box_id keying.
// Pallet dims come from pallet.length_in / width_in so this supports
// oversize 48×96 / 60×120 layouts identically.
const SOPalletPositionEditor = ({ pallet, busy, onSave, onCancel, onResetToAuto }) => {
  const PALLET_L = Number(pallet.length_in) || 40;
  const PALLET_W = Number(pallet.width_in) || 48;
  // Scale: aim for ~280px on the long axis at minimum, but scale down
  // for oversize pallets so the whole thing fits on screen.
  const SCALE = Math.max(4, Math.min(7, 280 / PALLET_L));
  const PAD_LEFT = 28;
  const PAD_BOTTOM = 22;
  const PAD_TOP = 4;
  const PAD_RIGHT = 6;

  // Extract draggable boxes from the pallet. Each entry carries the
  // dims (footprint, post-rotation) + initial position + box metadata
  // for rendering labels. Pallet-direct stacks use the first zone's
  // zone_w / zone_h as their footprint (same fallback as the
  // backend's _repack_pallet_box_positions helper).
  const initialBoxes = React.useMemo(() => {
    const out = [];
    for (const bx of (pallet.boxes || [])) {
      const z0 = (bx.zones && bx.zones[0]) || null;
      // Manually-authored stacks (manual_assign / move_zone / move-boxes onto
      // a new stack) can have NULL zone_w/zone_h. load_plan now back-fills the
      // box's interior_l/w from the part footprint for these; fall back through
      // interior dims → the zone's own part size so the stack stays draggable
      // instead of being skipped as geometry-less.
      const dimL = bx.is_pallet_direct
        ? (z0?.zone_w || bx.interior_l || z0?.size_length || 0)
        : (bx.outer_l ?? bx.interior_l ?? 0);
      const dimW = bx.is_pallet_direct
        ? (z0?.zone_h || bx.interior_w || z0?.size_width || 0)
        : (bx.outer_w ?? bx.interior_w ?? 0);
      if (!dimL || !dimW) continue;  // skip boxes with no geometry
      out.push({
        box_id: bx.box_id,
        is_pallet_direct: !!bx.is_pallet_direct,
        sku: bx.sku || null,
        short_id: bx.short_id || null,
        display_num: bx.display_box_num ?? bx.box_num,
        // dimL/W are the box's NATIVE (non-rotated) footprint;
        // rotated swaps them at render time.
        dim_l: Number(dimL),
        dim_w: Number(dimW),
        x: Number(bx.pallet_pos_x) || 0,
        y: Number(bx.pallet_pos_y) || 0,
        rotated: !!bx.pallet_rotated,
        // Stable color from box_id for visual consistency.
        color: SO_BOX_SKU_COLOR[bx.sku] || (bx.is_pallet_direct
          ? '#6c757d'
          : SO_PKG_COLOR_PALETTE[(bx.box_id || 0) % SO_PKG_COLOR_PALETTE.length]),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [draft, setDraft] = React.useState(initialBoxes);
  const [drag, setDrag] = React.useState(null);

  // Window-level drag handlers so the drag continues even when the
  // pointer leaves the rect.
  React.useEffect(() => {
    if (!drag) return;
    const handleMove = (e) => {
      const dxIn = (e.clientX - drag.startMouseX) / SCALE;
      const dyIn = (e.clientY - drag.startMouseY) / SCALE;
      setDraft((prev) => prev.map((b) => {
        if (b.box_id !== drag.boxId) return b;
        const fl = b.rotated ? b.dim_w : b.dim_l;
        const fw = b.rotated ? b.dim_l : b.dim_w;
        const newX = Math.round(Math.max(0, Math.min(PALLET_L - fl, drag.startX + dxIn)));
        const newY = Math.round(Math.max(0, Math.min(PALLET_W - fw, drag.startY + dyIn)));
        return { ...b, x: newX, y: newY };
      }));
    };
    const handleUp = () => setDrag(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [drag, SCALE, PALLET_L, PALLET_W]);

  const beginDrag = (e, boxId) => {
    e.preventDefault();
    const b = draft.find((x) => x.box_id === boxId);
    if (!b) return;
    setDrag({
      boxId,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: b.x,
      startY: b.y,
    });
  };

  const rotateBox = (boxId) => {
    setDraft((prev) => prev.map((b) => {
      if (b.box_id !== boxId) return b;
      const newRot = !b.rotated;
      // New footprint dims (after toggle).
      const fl = newRot ? b.dim_w : b.dim_l;
      const fw = newRot ? b.dim_l : b.dim_w;
      return {
        ...b,
        rotated: newRot,
        x: Math.round(Math.max(0, Math.min(PALLET_L - fl, b.x))),
        y: Math.round(Math.max(0, Math.min(PALLET_W - fw, b.y))),
      };
    }));
  };

  // Pairwise overlap + per-rect overhang detection.
  const flagsByBoxId = React.useMemo(() => {
    const flags = {};
    const rects = draft.map((b) => {
      const fl = b.rotated ? b.dim_w : b.dim_l;
      const fw = b.rotated ? b.dim_l : b.dim_w;
      flags[b.box_id] = {
        overhang: b.x + fl > PALLET_L + 0.01 || b.y + fw > PALLET_W + 0.01,
        overlap: false,
      };
      return { box_id: b.box_id, x1: b.x, y1: b.y, x2: b.x + fl, y2: b.y + fw };
    });
    for (let i = 0; i < rects.length; i++) {
      for (let j = i + 1; j < rects.length; j++) {
        const a = rects[i], r = rects[j];
        if (a.x1 < r.x2 - 0.01 && a.x2 > r.x1 + 0.01 && a.y1 < r.y2 - 0.01 && a.y2 > r.y1 + 0.01) {
          flags[a.box_id].overlap = true;
          flags[r.box_id].overlap = true;
        }
      }
    }
    return flags;
  }, [draft, PALLET_L, PALLET_W]);

  const anyConflict = Object.values(flagsByBoxId).some((f) => f.overhang || f.overlap);

  // Detect dirty: any box's position or rotation differs from its
  // initial state. Used to enable / label the Save button.
  const dirtyBoxIds = React.useMemo(() => {
    const ids = [];
    const initialById = Object.fromEntries(initialBoxes.map((b) => [b.box_id, b]));
    for (const b of draft) {
      const init = initialById[b.box_id];
      if (!init) continue;
      if (b.x !== init.x || b.y !== init.y || b.rotated !== init.rotated) {
        ids.push(b.box_id);
      }
    }
    return ids;
  }, [draft, initialBoxes]);

  const palletPxW = PALLET_L * SCALE;
  const palletPxH = PALLET_W * SCALE;
  const svgW = palletPxW + PAD_LEFT + PAD_RIGHT;
  const svgH = palletPxH + PAD_BOTTOM + PAD_TOP;
  const px0 = PAD_LEFT;
  const py0 = PAD_TOP;

  const handleSave = () => {
    if (anyConflict || !dirtyBoxIds.length) return;
    const positions = draft
      .filter((b) => dirtyBoxIds.includes(b.box_id))
      .map((b) => ({
        box_id: b.box_id,
        pallet_pos_x: b.x,
        pallet_pos_y: b.y,
        pallet_rotated: b.rotated,
      }));
    onSave(positions);
  };

  return (
    <div className="border border-warning rounded p-2 my-2" style={{ background: '#fffbf0' }}>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <small className="text-muted">
          <i className="bi bi-grip-vertical me-1"></i>
          Drag boxes / stacks to position. Snap = 1″. Click the
          <span className="mx-1" style={{ display: 'inline-block', border: '1px solid #444', borderRadius: '50%', width: 16, height: 16, textAlign: 'center', lineHeight: '14px', fontSize: 11, verticalAlign: 'middle' }}>↻</span>
          on a box to rotate 90°. Save commits to the plan; non-moved boxes stay put.
        </small>
        <div className="d-flex gap-2">
          {typeof onResetToAuto === 'function' && (
            <button className="btn btn-sm btn-outline-secondary"
              onClick={onResetToAuto}
              disabled={!!busy}
              title="Discard manual edits and run the auto FFDH packer over every box on this pallet."
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>Reset to auto
            </button>
          )}
          <button className="btn btn-sm btn-outline-secondary" onClick={onCancel} disabled={!!busy}>
            Cancel
          </button>
          <button
            className="btn btn-sm btn-success"
            onClick={handleSave}
            disabled={!!busy || anyConflict || dirtyBoxIds.length === 0}
            title={
              anyConflict
                ? 'Resolve overlap / overhang before saving (red rects).'
                : dirtyBoxIds.length === 0
                  ? 'No changes to save'
                  : `Save positions for ${dirtyBoxIds.length} box${dirtyBoxIds.length === 1 ? '' : 'es'}`
            }
          >
            <i className="bi bi-save me-1"></i>
            Save {dirtyBoxIds.length > 0 ? `(${dirtyBoxIds.length})` : ''}
          </button>
        </div>
      </div>
      {anyConflict && (
        <div className="alert alert-warning py-1 small mb-2">
          ⚠ Some boxes overlap each other or extend past the pallet edge. Adjust before saving.
        </div>
      )}
      <svg
        width={svgW}
        height={svgH}
        style={{ background: '#fafbfc', border: '1px solid #ced4da', borderRadius: 4, userSelect: 'none' }}
      >
        {/* Pallet base */}
        <rect
          x={px0} y={py0}
          width={palletPxW} height={palletPxH}
          fill="#f0e6d2"
          stroke="#8a6f44" strokeWidth={1.2}
        />
        {/* Deck-board lines along the long axis (decorative). */}
        {Array.from({ length: 5 }).map((_, i) => {
          const x = px0 + (i + 1) * (palletPxW / 6);
          return <line key={`db${i}`} x1={x} y1={py0 + 2} x2={x} y2={py0 + palletPxH - 2} stroke="#c4a878" strokeWidth={0.6} />;
        })}

        {/* Draggable boxes */}
        {draft.map((b) => {
          const fl = b.rotated ? b.dim_w : b.dim_l;
          const fw = b.rotated ? b.dim_l : b.dim_w;
          const px = px0 + b.x * SCALE;
          const py = py0 + b.y * SCALE;
          const rectW = fl * SCALE;
          const rectH = fw * SCALE;
          const flag = flagsByBoxId[b.box_id] || {};
          const isDragged = drag?.boxId === b.box_id;
          const isBad = flag.overhang || flag.overlap;
          const isDirty = dirtyBoxIds.includes(b.box_id);
          const labelKind = b.is_pallet_direct ? 'S' : 'B';
          const labelText = `${labelKind}${b.display_num}`;
          return (
            <g key={b.box_id} style={{ cursor: isDragged ? 'grabbing' : 'grab' }}>
              <rect
                x={px} y={py} width={rectW} height={rectH}
                fill={b.color}
                fillOpacity={isDragged ? 0.7 : (isBad ? 0.5 : (b.is_pallet_direct ? 0.6 : 0.85))}
                stroke={isBad ? '#dc3545' : (isDirty ? '#fd7e14' : '#222')}
                strokeWidth={isBad ? 2 : (isDragged || isDirty ? 2 : 1)}
                strokeDasharray={isBad ? '4 2' : (b.is_pallet_direct ? '3 2' : null)}
                onMouseDown={(e) => beginDrag(e, b.box_id)}
              />
              {Math.min(rectW, rectH) >= 18 && (
                <text
                  x={px + rectW / 2}
                  y={py + rectH / 2 + 4}
                  textAnchor="middle"
                  fontSize={Math.min(13, Math.max(8, Math.min(rectW, rectH) / 4))}
                  fontWeight="bold"
                  fill="#222"
                  pointerEvents="none"
                  style={{ paintOrder: 'stroke', stroke: '#fff', strokeWidth: 2.5 }}
                >
                  {labelText}
                </text>
              )}
              {Math.min(rectW, rectH) >= 30 && b.short_id && (
                <text
                  x={px + rectW / 2}
                  y={py + rectH / 2 + 16}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#444"
                  pointerEvents="none"
                  fontFamily="monospace"
                  style={{ paintOrder: 'stroke', stroke: '#fff', strokeWidth: 2 }}
                >
                  {b.short_id}
                </text>
              )}
              {/* Rotate-90° button overlay (top-right corner). Hidden
                  when the rect is too small to fit it. Clicking
                  toggles `rotated` and clamps the position. */}
              {Math.min(rectW, rectH) >= 18 && (
                <g
                  onClick={(e) => { e.stopPropagation(); rotateBox(b.box_id); }}
                  style={{ cursor: 'pointer' }}
                >
                  <circle
                    cx={px + rectW - 9}
                    cy={py + 9}
                    r={8}
                    fill="#fff"
                    fillOpacity={0.92}
                    stroke="#444"
                    strokeWidth={1}
                  />
                  <text
                    x={px + rectW - 9}
                    y={py + 12}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="bold"
                    fill="#444"
                    pointerEvents="none"
                  >
                    ↻
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Pallet dim labels */}
        <text x={px0 + palletPxW / 2} y={py0 + palletPxH + 14} textAnchor="middle" fontSize="11" fill="#6c757d" fontFamily="monospace">
          {PALLET_L}″
        </text>
        <text x={12} y={py0 + palletPxH / 2} textAnchor="middle" fontSize="11" fill="#6c757d" fontFamily="monospace"
              transform={`rotate(-90 12 ${py0 + palletPxH / 2})`}>
          {PALLET_W}″
        </text>
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// KIT BOXES — loose-ship consumables (bolt-on slats, hardware) on a pallet.
// Data comes from plan.pallets[].kit_boxes and plan.kit_box_summary
// (per assembly: loose items with covered/uncovered sets). Manifests print the
// same kit blocks, so what the operator sees here is what the customer reads.
// ---------------------------------------------------------------------------
const SOKitBoxCard = ({ kit, palletStatus, busy, editMode, onDeleteKitBox }) => {
  const canDelete = editMode && (palletStatus === 'Building' || palletStatus === 'Sealed') && typeof onDeleteKitBox === 'function';
  const items = kit.items || [];
  return (
    <div className="border rounded p-2 bg-white h-100" style={{ borderStyle: 'double', borderColor: '#2e7d32' }}>
      <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-1">
        <span className="fw-bold d-inline-flex align-items-center gap-1">
          <span className="badge" style={{ background: '#2e7d32', fontFamily: 'monospace', letterSpacing: '0.5px' }} title="Kit box: loose-ship items for N assembly sets, listed on every manifest for this pallet.">
            KIT {kit.display_label}
          </span>
          <span className="badge bg-secondary text-light">{(kit.kind || 'box').toUpperCase()}</span>
          <span className="small text-muted">{kit.assembly_name} · {kit.covers_sets} set{kit.covers_sets === 1 ? '' : 's'}</span>
        </span>
        {canDelete && (
          <button className="btn btn-sm btn-outline-danger py-0" disabled={!!busy} onClick={() => onDeleteKitBox(kit.kit_box_id)} title="Remove this kit box">
            {busy === `kit_del_${kit.kit_box_id}` ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-trash"></i>}
          </button>
        )}
      </div>
      <ul className="list-unstyled mb-1 small">
        {items.map(it => (
          <li key={it.consumable_id} className="d-flex justify-content-between">
            <span><strong>{Number(it.quantity)}×</strong> {it.product_id} <span className="text-muted">{(it.description || '').slice(0, 48)}</span></span>
            {it.line_weight_lbs !== null && it.line_weight_lbs !== undefined && <span className="text-muted ms-2">{Number(it.line_weight_lbs).toFixed(1)} lbs</span>}
          </li>
        ))}
      </ul>
      {kit.total_weight_lbs !== null && kit.total_weight_lbs !== undefined && (
        <div className="small text-muted">Kit weight ~{Number(kit.total_weight_lbs).toFixed(1)} lbs{kit.weight_complete ? '' : ' (some items have no weight on file)'}</div>
      )}
    </div>
  );
};

const SOAddKitBoxForm = ({ pallet, kitSummary, busy, onCreateKitBox }) => {
  const assemblies = Object.values(kitSummary || {}).filter(a => (a.loose_items || []).length > 0);
  const [asmId, setAsmId] = useState(assemblies.length ? String(assemblies[0].assembly_id) : '');
  const [covers, setCovers] = useState('');
  const [kind, setKind] = useState('box');
  const [chosen, setChosen] = useState(null); // null = all loose items
  const asm = assemblies.find(a => String(a.assembly_id) === String(asmId)) || assemblies[0];
  if (!asm) return null;
  const loose = asm.loose_items || [];
  const selected = chosen === null ? loose.map(it => it.consumable_id) : chosen;
  const maxCover = selected.length
    ? Math.min(...loose.filter(it => selected.includes(it.consumable_id)).map(it => it.uncovered_sets || 0))
    : 0;
  const coversNum = parseInt(covers, 10);
  const key = `kit_add_${pallet.pallet_id}`;
  const toggle = (cid) => setChosen(prev => {
    const base = prev === null ? loose.map(it => it.consumable_id) : prev;
    return base.includes(cid) ? base.filter(x => x !== cid) : [...base, cid];
  });
  const submit = async () => {
    if (!coversNum || coversNum < 1) return;
    await onCreateKitBox(pallet.pallet_id, {
      assembly_id: asm.assembly_id, covers_sets: coversNum, kind,
      consumable_ids: chosen === null ? null : selected,
    });
    setCovers(''); setChosen(null);
  };
  return (
    <div className="border rounded p-2 mb-2" style={{ background: '#f1f8f1', borderColor: '#2e7d32' }}>
      <div className="fw-bold small mb-1"><i className="bi bi-box-seam me-1"></i>Add kit box / bundle to {pallet.short_id || `P${pallet.pallet_num}`}
        <span className="fw-normal text-muted ms-2">loose-ship items ({maxCover} set{maxCover === 1 ? '' : 's'} still uncovered)</span>
      </div>
      <div className="d-flex flex-wrap gap-2 align-items-center">
        {assemblies.length > 1 && (
          <select className="form-select form-select-sm" style={{ maxWidth: 220 }} value={asmId} onChange={e => { setAsmId(e.target.value); setChosen(null); }}>
            {assemblies.map(a => <option key={a.assembly_id} value={a.assembly_id}>{a.assembly_name}</option>)}
          </select>
        )}
        <select className="form-select form-select-sm" style={{ maxWidth: 120 }} value={kind} onChange={e => setKind(e.target.value)} title="Box = carton of hardware; bundle = strapped bars/slats">
          <option value="box">Box</option>
          <option value="bundle">Bundle</option>
        </select>
        <div className="input-group input-group-sm" style={{ maxWidth: 190 }}>
          <span className="input-group-text">covers</span>
          <input type="number" className="form-control" min={1} max={maxCover} value={covers} disabled={!!busy || maxCover < 1} onChange={e => setCovers(e.target.value)} placeholder={maxCover ? `1–${maxCover}` : 'none left'} />
          <span className="input-group-text">sets</span>
        </div>
        <button className="btn btn-sm btn-success" disabled={!!busy || !coversNum || coversNum < 1 || coversNum > maxCover || !selected.length} onClick={submit}>
          {busy === key ? <><span className="spinner-border spinner-border-sm me-1"></span>Adding...</> : <><i className="bi bi-plus-circle me-1"></i>Add kit</>}
        </button>
      </div>
      <div className="d-flex flex-wrap gap-3 mt-1 small">
        {loose.map(it => (
          <label key={it.consumable_id} className="d-inline-flex align-items-center gap-1 mb-0">
            <input type="checkbox" className="form-check-input mt-0" checked={selected.includes(it.consumable_id)} onChange={() => toggle(it.consumable_id)} />
            <span>{Number(it.qty_per_set)}× {it.product_id}/set <span className="text-muted">({it.uncovered_sets} of {asm.sets_total} uncovered)</span></span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Add-purchased-item form (edit mode, unsealed carton on a Building pallet).
// Options come from load_plan.purchased_options: this SO's standalone
// purchased lines first, then same-customer open SOs — each with the pieces
// not yet in any box. Server re-checks the cap and same-customer rule.
const SOAddPurchasedItemForm = ({ box, options, busy, onAdd }) => {
  const opts = options || [];
  const [lineId, setLineId] = useState(opts.length ? String(opts[0].so_line_id) : '');
  const [qty, setQty] = useState('');
  const opt = opts.find(o => String(o.so_line_id) === String(lineId)) || opts[0];
  if (!opt) return null;
  const max = Number(opt.remaining_qty) || 0;
  const qtyNum = parseInt(qty, 10);
  const key = `purchased_add_${box.box_id}`;
  const submit = async () => {
    if (!qtyNum || qtyNum < 1 || qtyNum > max) return;
    const out = await onAdd(box.box_id, { so_line_id: opt.so_line_id, quantity: qtyNum });
    if (out) setQty('');
  };
  return (
    <div className="border rounded p-2 mb-2" style={{ background: '#eef7fb', borderColor: '#0dcaf0' }}>
      <div className="fw-bold small mb-1">
        <i className="bi bi-cart-plus me-1"></i>Add purchased item
        <span className="fw-normal text-muted ms-2">bought finished, no production</span>
      </div>
      <div className="d-flex flex-wrap gap-2 align-items-center">
        <select className="form-select form-select-sm" style={{ maxWidth: 260 }} value={lineId} onChange={e => { setLineId(e.target.value); setQty(''); }}>
          {opts.map(o => (
            <option key={o.so_line_id} value={o.so_line_id}>
              {o.product_id} · {o.order_number}{o.is_home_so ? '' : ' (other SO)'} · {o.remaining_qty} left
            </option>
          ))}
        </select>
        <div className="input-group input-group-sm" style={{ maxWidth: 150 }}>
          <span className="input-group-text">qty</span>
          <input type="number" className="form-control" min={1} max={max} value={qty} disabled={!!busy || max < 1}
                 onChange={e => setQty(e.target.value)} placeholder={max ? `1–${max}` : 'none left'} />
        </div>
        <button className="btn btn-sm btn-info" disabled={!!busy || !qtyNum || qtyNum < 1 || qtyNum > max} onClick={submit}>
          {busy === key ? <><span className="spinner-border spinner-border-sm me-1"></span>Adding...</> : <><i className="bi bi-plus-circle me-1"></i>Add</>}
        </button>
      </div>
      {!opt.is_home_so && (
        <div className="small text-muted mt-1">
          {opt.order_number}{opt.customer_po ? ` · PO ${opt.customer_po}` : ''} rides in this carton; its pieces stay billed and tallied on that SO.
        </div>
      )}
    </div>
  );
};

// Plan-level coverage for THIS SO's standalone purchased lines: ordered vs
// packed (in any carton, any plan) vs shipped, with where each piece sits.
const SOPurchasedCoveragePanel = ({ summary, homeSalesOrderId }) => {
  const lines = (summary || []);
  if (!lines.length) return null;
  return (
    <div className="mt-3">
      <h6 className="mb-1"><i className="bi bi-cart-fill me-1"></i>Purchased items (bought finished, resold)</h6>
      {lines.map(l => {
        const packed = Number(l.planned_qty) || 0;
        const ordered = Number(l.quantity_ordered) || 0;
        const shipped = Number(l.shipped_qty) || 0;
        const remaining = Number(l.remaining_qty) || 0;
        const cls = shipped >= ordered && ordered > 0 ? 'alert-success' : (remaining > 0 ? 'alert-warning' : 'alert-info');
        return (
          <div key={l.so_line_id} className={`alert py-2 px-3 mb-2 small ${cls}`}>
            <div className="fw-bold">
              {l.product_id} — {ordered} ordered · {packed} in boxes · {shipped} shipped
              {remaining > 0 && <span className="text-danger"> · {remaining} NOT in any box</span>}
            </div>
            {(l.locations || []).length > 0 && (
              <div className="text-muted">
                {l.locations.map(loc => {
                  const host = loc.host_sales_order_id && homeSalesOrderId && loc.host_sales_order_id !== homeSalesOrderId
                    ? ` (${loc.host_order_number}'s carton)` : '';
                  return `${loc.quantity}× in ${loc.box_short_id || `B${loc.box_num}`} on ${loc.pallet_short_id || `P${loc.pallet_num || '?'}`}${host}${loc.pallet_status ? ` · ${loc.pallet_status}` : ''}`;
                }).join(' · ')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SOKitCoveragePanel = ({ kitSummary }) => {
  const assemblies = Object.values(kitSummary || {}).filter(a => (a.loose_items || []).length > 0);
  if (!assemblies.length) return null;
  return (
    <div className="mt-3">
      <h6 className="mb-1"><i className="bi bi-box-seam me-1"></i>Loose-ship items (kit boxes)</h6>
      {assemblies.map(a => (
        <div key={a.assembly_id} className={`alert py-2 px-3 mb-2 small ${a.has_uncovered ? 'alert-warning' : 'alert-success'}`}>
          <div className="fw-bold">{a.assembly_name} — {a.sets_total} set{a.sets_total === 1 ? '' : 's'} ordered</div>
          <ul className="mb-1">
            {(a.loose_items || []).map(it => (
              <li key={it.consumable_id}>
                {Number(it.qty_per_set)}× <strong>{it.product_id}</strong> per set — {it.covered_sets} covered by kit boxes
                {it.uncovered_sets > 0
                  ? <span className="text-danger fw-bold"> · {it.uncovered_sets} NOT in any kit box (ships loose with the unit)</span>
                  : <span className="text-success"> · all covered</span>}
              </li>
            ))}
          </ul>
          {(a.kit_boxes || []).length > 0 && (
            <div className="text-muted">
              {a.kit_boxes.map(k => `${k.display_label} (${k.kind}) on ${k.pallet_short_id || `P${k.pallet_num}`} covers ${k.covers_sets}${k.status ? ` · ${k.status}` : ''}`).join(' · ')}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const SOPlannedBoxList = ({
  plan, apiBase, busy, outlineCache, palletChecks = {},
  onSeal, onShip, onStage, onConfirmPickup, onSealBox, onReprintBoxLabel,
  onReprintLabels, onReprintManifest, onPrintPartLabels,
  onPrintPalletStatusLabel, onPrintBoxStatusLabel, onPrintStackStatusLabel,
  onOpenMoveModal,
  onUnsealPallet, onUnsealBox, onConvertContainerType,
  onOpenNestModal, onUnnestBox,
  fulfillmentType,
  editMode, onMoveZone, onEditZoneQty, onDeleteZone,
  onDeleteEmptyBox, onDeleteEmptyPallet, onCreateEmptyBox, onRepackPositions,
  onSaveBoxPositions,
  boxCatalog, onInspectPart,
  kitSummary, onCreateKitBox, onDeleteKitBox,
  purchasedOptions, onAddPurchasedItem, onDeletePurchasedItem,
  highlightPalletId = null,
}) => {
  // Which pallet (if any) is currently in drag-to-reposition mode.
  // Only one pallet at a time across the whole plan card — keeps state
  // shallow and matches the cut-sheet PalletPositionEditor's modal feel.
  const [editingPositionsPalletId, setEditingPositionsPalletId] = React.useState(null);
  const allPallets = plan.pallets || [];
  if (!allPallets.length) return null;
  // Hide Consolidated containers left empty after their boxes moved to
  // another pallet (same rule as SOPalletMap). SO-1138, 10/23: four
  // "+ Box only" containers emptied onto PLT-X1YE read as shipped.
  const pallets = allPallets.filter(
    p => !(p.status === 'Consolidated' && (p.boxes || []).length === 0)
  );
  const hiddenConsolidated = allPallets.filter(p => !pallets.includes(p));
  const hiddenHosts = [...new Set(hiddenConsolidated.map(p => p.consolidated_into_short_id).filter(Boolean))];
  // Eligible destination boxes for zone moves: every box on a Building
  // pallet that is not itself Sealed. Shared across every zone dropdown.
  // Includes the box short_id (BOX-XXXX) so operators can match a dropdown
  // pick to a physical carton / stack tagged with the same handle.
  // A nested box (inside another order's carton) is never a destination: its carton is.
  const eligibleDestBoxes = [];
  for (const p of pallets) {
    if (p.status !== 'Building') continue;
    for (const b of (p.boxes || [])) {
      if (b.status === 'Sealed' || isNested(b)) continue;
      const kind = b.is_pallet_direct ? 'S' : 'B';
      const suffix = b.is_pallet_direct
        ? ' (stack)'
        : (b.sku ? ` (${b.sku})` : '');
      const sidTail = b.short_id ? ` · ${b.short_id}` : '';
      const displayNum = b.display_box_num ?? b.box_num;
      eligibleDestBoxes.push({
        box_id: b.box_id,
        label: `${p.short_id || `P${p.pallet_num}`} / ${b.short_id || `${kind}${displayNum}`}${suffix}`,
      });
    }
  }
  // Pallet accent palette — a hex per pallet-INDEX (not pallet_num since
  // numbers can gap after deletion). Matches the Quote UI pattern: each
  // pallet gets a thick colored left border + a faint tinted background
  // so a long list of pallets reads as distinct sections at a glance.
  const PALLET_ACCENTS = [
    '#0d6efd', // primary (blue)
    '#198754', // success (green)
    '#fd7e14', // orange (warning-ish but readable)
    '#6610f2', // purple
    '#0dcaf0', // cyan / info
    '#d63384', // pink
    '#dc3545', // red
    '#6c757d', // gray
  ];
  const accentBgFor = (hex) => {
    // Convert #RRGGBB to a ~5% rgba tint for a subtle section background.
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.06)`;
  };
  return (
    <div className="mb-3">
      <div className="fw-bold mb-2"><i className="bi bi-boxes me-1"></i>Boxes &amp; Stacks</div>
      {hiddenConsolidated.length > 0 && (
        <div
          className="small text-muted mb-2"
          data-testid="so-hidden-consolidated"
          title={`Empty containers whose boxes were moved onto another pallet: ${hiddenConsolidated.map(p => p.short_id).filter(Boolean).join(', ')}. Nothing on them ships.`}
        >
          {hiddenConsolidated.length} empty merged container{hiddenConsolidated.length > 1 ? 's' : ''} hidden
          {hiddenHosts.length > 0 && <> (boxes moved onto {hiddenHosts.join(', ')})</>}
        </div>
      )}
      {pallets.map((p, palletIdx) => {
        const palletBoxCount = (p.boxes || []).length;
        const canDeletePallet = editMode
          && p.status === 'Building'
          && palletBoxCount === 0
          && !p.shipment_group_id;
        const accent = PALLET_ACCENTS[palletIdx % PALLET_ACCENTS.length];
        return (
          <div
            key={p.pallet_id}
            id={`so-pallet-detail-${p.pallet_id}`}
            className="mb-3 rounded-3 p-3"
            data-highlighted={highlightPalletId === p.pallet_id ? 'true' : undefined}
            style={{
              borderLeft: `6px solid ${accent}`,
              background: accentBgFor(accent),
              scrollMarginTop: '12px',
              ...(highlightPalletId === p.pallet_id
                ? { boxShadow: '0 0 0 3px var(--bs-warning, #ffc107)', transition: 'box-shadow 0.3s' }
                : {}),
            }}
          >
            <SOPalletActionBar
              pallet={p}
              busy={busy}
              onSeal={onSeal}
              onShip={onShip}
              onStage={onStage}
              onConfirmPickup={onConfirmPickup}
              onReprintLabels={onReprintLabels}
              onReprintManifest={onReprintManifest}
              onPrintPartLabels={onPrintPartLabels}
              onPrintPalletStatusLabel={onPrintPalletStatusLabel}
              onOpenMoveModal={onOpenMoveModal}
              onUnsealPallet={onUnsealPallet}
              onConvertContainerType={onConvertContainerType}
              fulfillmentType={fulfillmentType}
              openProblems={openProblemsFor(palletChecks, p.pallet_id)}
            />
            {(p.kit_boxes || []).length > 0 && (
              <div className="row g-2 mb-2">
                {(p.kit_boxes || []).map(k => (
                  <div key={k.kit_box_id} className="col-md-6 col-lg-4">
                    <SOKitBoxCard kit={k} palletStatus={p.status} busy={busy} editMode={editMode} onDeleteKitBox={onDeleteKitBox} />
                  </div>
                ))}
              </div>
            )}
            {editMode && (p.status === 'Building' || p.status === 'Sealed') && typeof onCreateKitBox === 'function' && (
              <SOAddKitBoxForm pallet={p} kitSummary={kitSummary} busy={busy} onCreateKitBox={onCreateKitBox} />
            )}
            {editMode && p.status === 'Building' && (
              <div className="d-flex flex-wrap gap-2 mb-2 mt-1">
                <SOEditNewBoxPicker
                  palletId={p.pallet_id}
                  palletNum={p.pallet_num}
                  palletShortId={p.short_id}
                  boxCatalog={boxCatalog}
                  busy={busy}
                  onCreateEmptyBox={onCreateEmptyBox}
                />
                {canDeletePallet && (
                  <button
                    className="btn btn-sm btn-outline-danger"
                    disabled={!!busy}
                    onClick={() => onDeleteEmptyPallet(p.pallet_id)}
                    title="This pallet has no boxes and was never sealed — delete it."
                  >
                    {busy === `del_pallet_${p.pallet_id}`
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Deleting...</>
                      : <><i className="bi bi-trash me-1"></i>Delete empty pallet {p.short_id || `P${p.pallet_num}`}</>}
                  </button>
                )}
              </div>
            )}
            {/* Re-layout is always visible on Building pallets with real boxes —
                not gated on editMode since it's a safe, recoverable operation
                that fixes map positions without touching plan data. Manual
                "Edit positions" sits next to it (also on any Building pallet
                with ≥1 box), letting operators drag/rotate via SOPalletPositionEditor. */}
            {p.status === 'Building' && (p.boxes || []).length > 0 && (
              <div className="mb-2 mt-1 d-flex flex-wrap gap-2">
                {typeof onRepackPositions === 'function'
                  && (p.boxes || []).some(isCarton)
                  && (
                  <button
                    className="btn btn-sm btn-outline-info"
                    disabled={!!busy || editingPositionsPalletId === p.pallet_id}
                    onClick={() => onRepackPositions(p.pallet_id)}
                    title="Auto re-shelf-pack via FFDH. Useful when the pallet map shows items overlapping (common on plans created before Apr 22, and on consolidated stacks before May 5)."
                  >
                    {busy === `repack_${p.pallet_id}`
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Re-laying out...</>
                      : <><i className="bi bi-grid-3x3-gap me-1"></i>Re-layout boxes (auto)</>}
                  </button>
                )}
                {typeof onSaveBoxPositions === 'function' && (
                  <button
                    className={`btn btn-sm ${
                      editingPositionsPalletId === p.pallet_id
                        ? 'btn-warning'
                        : 'btn-outline-warning'
                    }`}
                    disabled={!!busy && editingPositionsPalletId !== p.pallet_id}
                    onClick={() => setEditingPositionsPalletId(
                      editingPositionsPalletId === p.pallet_id ? null : p.pallet_id
                    )}
                    title="Manually drag boxes and stacks to the positions you want, with rotate-90° + snap-to-1″. Replaces the auto-layout for the boxes you move; non-moved boxes stay put."
                  >
                    {editingPositionsPalletId === p.pallet_id
                      ? <><i className="bi bi-x-circle me-1"></i>Close position editor</>
                      : <><i className="bi bi-arrows-move me-1"></i>Edit positions (manual)</>}
                  </button>
                )}
              </div>
            )}
            {editingPositionsPalletId === p.pallet_id && (
              <SOPalletPositionEditor
                pallet={p}
                busy={busy}
                onSave={async (positions) => {
                  const out = await onSaveBoxPositions(p.pallet_id, positions);
                  if (out) setEditingPositionsPalletId(null);
                }}
                onCancel={() => setEditingPositionsPalletId(null)}
                onResetToAuto={async () => {
                  await onRepackPositions(p.pallet_id);
                  setEditingPositionsPalletId(null);
                }}
              />
            )}
            <div className="row g-2">
              {(() => {
                // A nested box renders inside its host carton's card (the "Inside this carton" block), never as a card
                // of its own; one whose host is not on this pallet (should not happen) keeps its card.
                const onPallet = new Set((p.boxes || []).map(b => b.box_id));
                return (p.boxes || []).filter(bx => !(isNested(bx) && onPallet.has(bx.inside_box_id)));
              })().map(bx => (
                <div key={bx.box_id} className="col-md-6 col-lg-4">
                  <SOPlannedBoxCard
                    box={bx}
                    palletBoxes={p.boxes || []}
                    planId={plan.plan_id}
                    onOpenNestModal={onOpenNestModal}
                    onUnnestBox={onUnnestBox}
                    homeSalesOrderId={plan.sales_order_id}
                    palletId={p.pallet_id}
                    palletNum={p.pallet_num}
                    palletStatus={p.status || 'Building'}
                    palletShipmentGroupId={p.shipment_group_id}
                    busy={busy}
                    outlineCache={outlineCache}
                    onSealBox={onSealBox}
                    onReprintBoxLabel={onReprintBoxLabel}
                    onUnsealBox={onUnsealBox}
                    onPrintBoxStatusLabel={onPrintBoxStatusLabel}
                    onPrintStackStatusLabel={onPrintStackStatusLabel}
                    editMode={editMode}
                    onMoveZone={onMoveZone}
                    onEditZoneQty={onEditZoneQty}
                    onDeleteZone={onDeleteZone}
                    onDeleteEmptyBox={onDeleteEmptyBox}
                    eligibleDestBoxes={eligibleDestBoxes}
                    onInspectPart={onInspectPart}
                    purchasedOptions={purchasedOptions}
                    onAddPurchasedItem={onAddPurchasedItem}
                    onDeletePurchasedItem={onDeletePurchasedItem}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Small controls cluster for adding a fresh empty box or pallet-direct
// stack to a Building pallet. Separate component so the dropdown state
// stays local and doesn't force a re-render of the full box list on each
// keystroke.
const SOEditNewBoxPicker = ({ palletId, palletNum, palletShortId, boxCatalog, busy, onCreateEmptyBox }) => {
  const palletLabel = palletShortId || `P${palletNum}`;
  const [choice, setChoice] = useState('');
  const onChange = async (e) => {
    const val = e.target.value;
    if (!val) return;
    setChoice(val);
    if (val === '__direct__') {
      await onCreateEmptyBox(palletId, true, null);
    } else {
      await onCreateEmptyBox(palletId, false, val);
    }
    setChoice('');
  };
  const busyKey = `new_box_${palletId}`;
  return (
    <div className="d-flex align-items-center gap-1">
      <select
        className="form-select form-select-sm"
        style={{ maxWidth: 260 }}
        value={choice}
        disabled={!!busy}
        onChange={onChange}
        title={`Add a new empty box or pallet-direct stack to pallet ${palletLabel}. Then move zones into it.`}
      >
        <option value="">+ New box / stack on {palletLabel}…</option>
        <option value="__direct__">Pallet-direct stack (no carton)</option>
        {(boxCatalog || []).map(bx => {
          const label = bx.sku + (bx.interior_l ? ` · ${+bx.interior_l}×${+bx.interior_w}×${+bx.interior_h}″` : '');
          return <option key={bx.sku} value={bx.sku}>{label}</option>;
        })}
      </select>
      {busy === busyKey && (
        <span className="spinner-border spinner-border-sm text-muted"></span>
      )}
    </div>
  );
};

// Plan-level "+ Box only (no pallet)" picker. Lives in the edit-mode
// "New empty pallet:" row next to the pallet-size buttons. One-click
// creates a phantom 40×48 container with container_type='Box' + a single
// empty box of the picked SKU inside. Replaces the prior dance of make
// pallet → flip container_type → add box.
const SOEditNewBoxOnlyPicker = ({ boxCatalog, busy, onCreateBoxOnly }) => {
  const [choice, setChoice] = useState('');
  const onChange = async (e) => {
    const val = e.target.value;
    if (!val) return;
    setChoice(val);
    await onCreateBoxOnly(val);
    setChoice('');
  };
  return (
    <div className="d-flex align-items-center gap-1">
      <select
        className="form-select form-select-sm"
        style={{ maxWidth: 300 }}
        value={choice}
        disabled={!!busy}
        onChange={onChange}
        title="Add a loose-carton shipment (no wood pallet). Manifest, labels, and the customer email will read 'Box' instead of 'Pallet'."
      >
        <option value="">+ Box only (no pallet)…</option>
        {(boxCatalog || []).map(bx => {
          const label = bx.sku + (bx.interior_l ? ` · ${+bx.interior_l}×${+bx.interior_w}×${+bx.interior_h}″` : '');
          return <option key={bx.sku} value={bx.sku}>{label}</option>;
        })}
      </select>
      {busy === 'new_box_only' && (
        <span className="spinner-border spinner-border-sm text-muted"></span>
      )}
    </div>
  );
};

// Clickable wrapper around a part's product_id. When clicked, calls

// Modal for moving boxes from one pallet to another. Same customer only.
// Source comes from the calling SOPalletActionBar; destination is any other
// pallet on this plan (in-plan dropdown) or any pallet from another plan
// addressed by short_id (PLT-XXXX text field for cross-SO consolidation
// between two SOs of the same customer).
//
// Three modes (auto-suggested based on src/dest state, operator can override):
//   - Plain Building: pre-seal consolidation. Rewrites planned_boxes.pallet_id.
//     Both src and dest must be Building. No labels yet — issued when dest seals.
//     Cross-plan moves transfer planning home (plan_id) too.
//   - Plain Ready: post-seal consolidation. Both src and dest must be Ready.
//     Repoints shipment data + auto-reprints dest pallet label.
//   - Ride-along: ship-time consolidation. Allows ANY non-Empty box (incl.
//     Sealed-on-Building source) and ANY non-Shipped/non-Consolidated dest.
//     Box's plan_id stays on its source SO (preserves billing/yield/qty_shipped
//     attribution); only shipped_pallet_id is rewritten so it physically rides
//     on the host pallet. Lazy-creates dest's shipment shell when needed.
//     Required for cross-state pairs (e.g. Sealed boxes on a Building source
//     pallet moving to a Ready host on another SO).
const SOMovePalletModal = ({ apiBase, sourcePallet, plan, onClose, onMoved }) => {
  const srcStatus = sourcePallet?.status || 'Building';

  // Mode selector: 'plain' or 'ride-along'. Defaults to 'plain' when source is
  // Building or Ready and the operator picks a same-state dest. Auto-flips to
  // 'ride-along' when state mismatch detected (or operator can flip manually).
  const [mode, setMode] = useState('plain');
  const isRideAlong = mode === 'ride-along';
  const isBuilding = srcStatus === 'Building';

  // Eligible boxes — must match backend `allowed_box_statuses`:
  //   - Plain Building: Empty/Partial/Full (Sealed-on-Building needs ride-along).
  //     Including Empty lets operators rebalance the plan BEFORE production.
  //   - Plain Ready:    Sealed only.
  //   - Ride-along:     Partial/Full/Sealed (any non-Empty — needs real content
  //     to ship-time consolidate).
  const eligibleBoxes = (sourcePallet?.boxes || []).filter(b => {
    // A nested box moves only with its carton (the API refuses it alone: take it out first).
    if (isNested(b)) return false;
    if (isRideAlong) return b.status !== 'Empty';
    return isBuilding ? b.status !== 'Sealed' : b.status === 'Sealed';
  });
  // Destination pallets in this same plan. Plain mode requires same state;
  // ride-along permits any non-Shipped/Consolidated.
  const otherPallets = (plan?.pallets || []).filter(p => {
    if (p.pallet_id === sourcePallet?.pallet_id) return false;
    if (isRideAlong) return p.status === 'Building' || p.status === 'Ready';
    return p.status === srcStatus;
  });

  const [selectedBoxIds, setSelectedBoxIds] = useState(() => eligibleBoxes.map(b => b.box_id));
  const [destPalletId, setDestPalletId] = useState(otherPallets[0]?.pallet_id || '');
  const [destShortIdInput, setDestShortIdInput] = useState('');
  const [destLookup, setDestLookup] = useState(null);
  const [destLookupErr, setDestLookupErr] = useState('');
  const [destLookupBusy, setDestLookupBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const [movedBy, setMovedBy] = useState('');
  const [overrideWeight, setOverrideWeight] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState('');

  // When mode changes, reset selection so the eligible-set rule actually
  // applies (avoids carrying a Sealed pre-selected box into Plain Building mode
  // where it'd be ineligible — server would reject).
  useEffect(() => {
    setSelectedBoxIds(eligibleBoxes.map(b => b.box_id));
    // eslint-disable-next-line
  }, [mode]);

  // Cross-SO move targets: other same-customer plans' pallets. Plain mode
  // filters to matching srcStatus; ride-along keeps the full list (Building
  // and Ready) so the operator can pick either.
  const [crossSoTargets, setCrossSoTargets] = useState([]);
  const [crossSoBusy, setCrossSoBusy] = useState(false);
  useEffect(() => {
    if (!plan?.plan_id) return;
    let cancelled = false;
    setCrossSoBusy(true);
    fetch(`${apiBase}/packaging/plans/${plan.plan_id}/cross-so-move-targets`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => {
        if (cancelled) return;
        // Keep ALL targets — the visible-list filter below applies srcStatus
        // for plain mode, ride-along shows everything.
        setCrossSoTargets(data?.targets || []);
      })
      .catch(() => { /* non-fatal; operator can still use the PLT-XXXX lookup */ })
      .finally(() => { if (!cancelled) setCrossSoBusy(false); });
    return () => { cancelled = true; };
  }, [apiBase, plan?.plan_id]);

  // Filtered visible targets — plain mode strict, ride-along permissive.
  const visibleCrossSoTargets = crossSoTargets.filter(t =>
    isRideAlong ? (t.status === 'Building' || t.status === 'Ready') : (t.status === srcStatus)
  );

  if (!sourcePallet) return null;

  const toggleBox = (id) => {
    setSelectedBoxIds(prev => prev.includes(id)
      ? prev.filter(x => x !== id)
      : [...prev, id]);
  };

  const lookupShortId = async () => {
    const sid = (destShortIdInput || '').trim().toUpperCase();
    if (!sid) {
      setDestLookup(null);
      setDestLookupErr('');
      return;
    }
    setDestLookupBusy(true);
    setDestLookupErr('');
    try {
      const res = await fetch(`${apiBase}/packaging/pallets/by-short-id/${encodeURIComponent(sid)}`);
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(detailToString(b.detail) || `Pallet ${sid} not found.`);
      }
      const data = await res.json();
      if (data.pallet_id === sourcePallet.pallet_id) {
        throw new Error("That's the source pallet — pick a different destination.");
      }
      // Plain mode: strict same-state. Ride-along: any non-Shipped/non-Consolidated.
      if (isRideAlong) {
        if (data.status === 'Shipped' || data.status === 'Consolidated') {
          throw new Error(
            `Pallet ${sid} is ${data.status} — ride-along can only target Building or Ready pallets.`
          );
        }
      } else if (data.status !== srcStatus) {
        throw new Error(
          `Pallet ${sid} is ${data.status} but the source is ${srcStatus}. ` +
          `Plain consolidation requires matching states. Switch to ride-along mode if you need to consolidate across states.`
        );
      }
      setDestLookup(data);
      setDestPalletId('');
    } catch (err) {
      setDestLookup(null);
      setDestLookupErr(err.message || String(err));
    } finally {
      setDestLookupBusy(false);
    }
  };

  const effectiveDestPalletId = destLookup?.pallet_id || destPalletId;
  const effectiveDestPallet = destLookup || otherPallets.find(p => p.pallet_id === effectiveDestPalletId);
  const isCrossPlan = destLookup && destLookup.plan_id !== plan?.plan_id;

  // Auto-suggest ride-along when the operator selects a cross-state dest in
  // Plain mode. Don't force — banner suggests, operator confirms by clicking.
  const destStatus = effectiveDestPallet?.status;
  const stateMismatch = destStatus && destStatus !== srcStatus;
  const hasSealedOnBuildingSrc =
    srcStatus === 'Building'
    && selectedBoxIds.some(id => {
      const b = (sourcePallet?.boxes || []).find(x => x.box_id === id);
      return b?.status === 'Sealed';
    });
  const rideAlongSuggested = !isRideAlong && (stateMismatch || hasSealedOnBuildingSrc);

  const submit = async () => {
    if (!selectedBoxIds.length) {
      setSubmitErr('Select at least one box to move.');
      return;
    }
    if (!effectiveDestPalletId) {
      setSubmitErr('Pick a destination pallet (dropdown OR look up by short ID).');
      return;
    }
    setSubmitBusy(true);
    setSubmitErr('');
    try {
      const res = await fetch(
        `${apiBase}/packaging/plans/${plan.plan_id}/pallets/${sourcePallet.pallet_id}/move-boxes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            box_ids: selectedBoxIds,
            dest_pallet_id: effectiveDestPalletId,
            moved_by: movedBy || planEditor(),
            notes: notes || null,
            override_weight_warning: overrideWeight,
            // ride_along=true preserves source SO billing/yield/qty_shipped;
            // box's plan_id stays put, only shipped_pallet_id is rewritten.
            // Server lazy-creates dest shipment shell when needed.
            ride_along: isRideAlong,
          }),
        }
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        if (res.status === 409) {
          setSubmitErr(`${b.detail} Tick the override box and resubmit if you've confirmed the load is safe.`);
          setOverrideWeight(false);
          return;
        }
        throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
      }
      const result = await res.json();
      onMoved(result);
    } catch (err) {
      setSubmitErr(err.message || String(err));
    } finally {
      setSubmitBusy(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      tabIndex="-1"
      role="dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-arrows-collapse me-2"></i>
              Move boxes from Pallet
              <span className="ms-2 badge bg-dark" style={{ fontFamily: 'monospace' }}>
                {sourcePallet.short_id || `P${sourcePallet.pallet_num}`}
              </span>
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {/* Mode selector — Plain (same-state) vs Ride-along (ship-time
                cross-state, preserves source SO billing). */}
            <div className="mb-3">
              <label className="form-label fw-bold mb-1">Mode</label>
              <div className="btn-group w-100" role="group" aria-label="Move mode">
                <input
                  type="radio"
                  className="btn-check"
                  name="movemode"
                  id="movemode_plain"
                  autoComplete="off"
                  checked={!isRideAlong}
                  onChange={() => setMode('plain')}
                />
                <label className="btn btn-outline-primary" htmlFor="movemode_plain">
                  Plain consolidation
                  <div className="small text-muted">
                    {isBuilding
                      ? 'Pre-seal · same-state · transfers planning home'
                      : 'Post-seal · same-state · repoints shipment data'}
                  </div>
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  name="movemode"
                  id="movemode_ridealong"
                  autoComplete="off"
                  checked={isRideAlong}
                  onChange={() => setMode('ride-along')}
                />
                <label className="btn btn-outline-warning" htmlFor="movemode_ridealong">
                  Ride-along (ship-time)
                  <div className="small text-muted">
                    Cross-state OK · source SO keeps billing/yield/qty_shipped
                  </div>
                </label>
              </div>
              {rideAlongSuggested && (
                <div className="alert alert-warning py-2 small mt-2 mb-0">
                  <i className="bi bi-lightbulb me-1"></i>
                  <strong>Tip:</strong> {stateMismatch
                    ? `Source pallet is ${srcStatus} and destination is ${destStatus} — ride-along is the right mode for this consolidation.`
                    : `You have a Sealed box on a Building source pallet — ride-along preserves its source SO billing while letting it ship on another pallet.`}
                  {' '}
                  <button
                    type="button"
                    className="btn btn-sm btn-warning ms-1 py-0"
                    onClick={() => setMode('ride-along')}
                  >
                    Switch to Ride-along
                  </button>
                </div>
              )}
            </div>

            <div className="alert alert-info py-2 small">
              <i className="bi bi-info-circle me-1"></i>
              {isRideAlong ? (
                <>
                  <strong>Ride-along mode</strong>: each box's planning home (plan_id)
                  stays on its source SO so billing, yield policy, and qty_shipped
                  attribution are preserved. Only the box's <code>shipped_pallet_id</code>
                  is rewritten to point at the destination pallet — when the host pallet
                  ships, the box's source SO Shipping tasks are advanced too. Required
                  when source and destination are in different states (e.g. Sealed boxes
                  on a Building source moving to a Ready host on another SO).
                </>
              ) : isBuilding ? (
                <>
                  <strong>Building mode</strong>: pre-seal consolidation. Boxes
                  move to the destination pallet's plan; no labels printed
                  yet — when you seal the destination later you'll get one
                  combined label.
                </>
              ) : (
                <>
                  <strong>Ready mode</strong>: post-seal consolidation. Box
                  shipment data is repointed to the destination so labels and
                  manifests reflect the actual load. Destination pallet label
                  auto-reprints on success.
                </>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">
                {isRideAlong ? 'Boxes/stacks to ride-along' : (isBuilding ? 'Boxes/stacks to move' : 'Sealed boxes to move')}
                {' '}({selectedBoxIds.length} of {eligibleBoxes.length} selected)
              </label>
              <div className="border rounded p-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                {eligibleBoxes.map(b => {
                  const wt = (b.zones || []).reduce(
                    (a, z) => a + (z.actual_qty || 0) * (z.weight_lbs || 0),
                    0,
                  ) + (b.purchased_items || []).reduce(
                    (a, it) => a + (Number(it.quantity) || 0) * (Number(it.weight_lbs) || 0),
                    0,
                  );
                  return (
                    <div key={b.box_id} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`movebox_${b.box_id}`}
                        checked={selectedBoxIds.includes(b.box_id)}
                        onChange={() => toggleBox(b.box_id)}
                      />
                      <label className="form-check-label" htmlFor={`movebox_${b.box_id}`}>
                        {(() => {
                          const dn = b.display_box_num ?? b.box_num;
                          const id = b.short_id || (b.is_pallet_direct ? `S${dn}` : `B${dn}`);
                          return b.is_pallet_direct
                            ? <>Stack <strong style={{ fontFamily: 'monospace' }}>{id}</strong></>
                            : <>Box <strong style={{ fontFamily: 'monospace' }}>{id}</strong></>;
                        })()}
                        {b.sku && <span className="ms-1 text-muted">({b.sku})</span>}
                        <span className="ms-2 small text-muted">~{wt.toFixed(0)} lb</span>
                      </label>
                    </div>
                  );
                })}
                {!eligibleBoxes.length && (
                  <em className="text-muted">
                    No {isRideAlong ? 'non-empty' : (isBuilding ? '' : 'sealed ')}boxes on this pallet.
                  </em>
                )}
              </div>
              <div className="form-text">
                All eligible boxes start checked (whole-pallet merge). Uncheck any you'd rather leave on this pallet.
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Destination pallet</label>
              <div className="row g-2">
                <div className="col-md-7">
                  <select
                    className="form-select"
                    // Key: combine source so same-plan and cross-SO options live in one select.
                    // "own:<id>" → same-plan (destPalletId); "xso:<id>" → cross-SO (destLookup)
                    value={
                      destLookup
                        ? `xso:${destLookup.pallet_id}`
                        : destPalletId ? `own:${destPalletId}` : ''
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        setDestPalletId('');
                        setDestLookup(null);
                        return;
                      }
                      const [kind, idStr] = v.split(':');
                      const id = parseInt(idStr, 10);
                      if (kind === 'own') {
                        setDestPalletId(id);
                        setDestLookup(null);
                        setDestShortIdInput('');
                        setDestLookupErr('');
                      } else if (kind === 'xso') {
                        const tgt = visibleCrossSoTargets.find(t => t.pallet_id === id);
                        if (tgt) {
                          // Populate destLookup in the same shape get_pallet_by_short_id
                          // returns, so the existing submit + confirmation-banner path
                          // just works for cross-SO selections too.
                          setDestLookup({
                            pallet_id:       tgt.pallet_id,
                            plan_id:         tgt.plan_id,
                            pallet_num:      tgt.pallet_num,
                            short_id:        tgt.short_id,
                            status:          tgt.status,
                            sales_order_id:  tgt.sales_order_id,
                            order_number:    tgt.order_number,
                            customer_name:   '',
                            // Pass through for per-option weight hint display later.
                            _max_pallet_weight_lbs: tgt.max_pallet_weight_lbs,
                            _current_weight_lbs:    tgt.current_weight_lbs,
                          });
                          setDestPalletId('');
                          setDestShortIdInput('');
                          setDestLookupErr('');
                        }
                      }
                    }}
                  >
                    <option value="">— pick a {isRideAlong ? 'Building or Ready' : srcStatus} pallet —</option>
                    {otherPallets.length > 0 && (
                      <optgroup label="This SO">
                        {otherPallets.map(p => {
                          const wt = (p.boxes || []).reduce(
                            (a, b) => a + (b.zones || []).reduce(
                              (aa, z) => aa + (z.actual_qty || 0) * (z.weight_lbs || 0), 0), 0);
                          const boxCount = (p.boxes || []).length;
                          return (
                            <option key={`own-${p.pallet_id}`} value={`own:${p.pallet_id}`}>
                              Pallet {p.short_id || `P${p.pallet_num}`} · {boxCount} box(es) · ~{wt.toFixed(0)} lb · {p.status}
                            </option>
                          );
                        })}
                      </optgroup>
                    )}
                    {visibleCrossSoTargets.length > 0 && (
                      <optgroup label="Same customer — other plans (cross-SO)">
                        {visibleCrossSoTargets.map(t => {
                          const cap  = Math.round(t.max_pallet_weight_lbs || 0);
                          const cur  = Math.round(t.current_weight_lbs || 0);
                          const room = cap > 0 ? `${cur} / ${cap} lb` : `~${cur} lb`;
                          return (
                            <option key={`xso-${t.pallet_id}`} value={`xso:${t.pallet_id}`}>
                              {t.order_number} · {t.short_id || `P${t.pallet_num}`} · {t.box_count} box(es) · {room} · {t.status}
                            </option>
                          );
                        })}
                      </optgroup>
                    )}
                  </select>
                  {!otherPallets.length && !visibleCrossSoTargets.length && !crossSoBusy && (
                    <div className="form-text text-warning">
                      No eligible {isRideAlong ? 'Building/Ready' : srcStatus} pallets on this SO or any same-customer plan. Use the short-ID field to target something unusual.
                    </div>
                  )}
                  {crossSoBusy && (
                    <div className="form-text text-muted small">
                      Looking up same-customer pallets on other open plans…
                    </div>
                  )}
                </div>
                <div className="col-md-5">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Or scan/type PLT-XXXX"
                      value={destShortIdInput}
                      onChange={(e) => {
                        setDestShortIdInput(e.target.value);
                        setDestLookup(null);
                        setDestLookupErr('');
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupShortId(); } }}
                      style={{ fontFamily: 'monospace' }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={destLookupBusy || !destShortIdInput.trim()}
                      onClick={lookupShortId}
                    >
                      {destLookupBusy ? '...' : 'Look up'}
                    </button>
                  </div>
                </div>
              </div>
              {destLookupErr && <div className="text-danger small mt-1">{destLookupErr}</div>}
              {destLookup && (() => {
                // Projected weight after the move = destination current + selected boxes' content weight.
                // current is populated either from the cross-so-targets endpoint (_current_weight_lbs)
                // or by summing the destination pallet's boxes when it's same-plan.
                let curWt = Number(destLookup._current_weight_lbs);
                if (!Number.isFinite(curWt)) {
                  const destLocal = (plan?.pallets || []).find(p => p.pallet_id === destLookup.pallet_id);
                  curWt = (destLocal?.boxes || []).reduce(
                    (a, b) => a + (b.zones || []).reduce(
                      (aa, z) => aa + (z.actual_qty || 0) * (z.weight_lbs || 0), 0), 0);
                }
                const addWt = eligibleBoxes
                  .filter(b => selectedBoxIds.includes(b.box_id))
                  .reduce((a, b) => a + (b.zones || []).reduce(
                    (aa, z) => aa + (z.actual_qty || 0) * (z.weight_lbs || 0), 0), 0);
                const cap = Number(destLookup._max_pallet_weight_lbs) || Number(plan?.max_pallet_weight_lbs) || 0;
                const projected = curWt + addWt;
                const overCap = cap > 0 && projected > cap;
                return (
                  <div className={`alert alert-${overCap ? 'warning' : 'success'} py-1 small mt-2 mb-0`}>
                    Destination: Pallet <strong style={{ fontFamily: 'monospace' }}>{destLookup.short_id || `P${destLookup.pallet_num}`}</strong>
                    {' on '}<strong>{destLookup.order_number}</strong>
                    {destLookup.customer_name ? ` for ${destLookup.customer_name}` : ''}
                    {' '}<span className="badge bg-secondary">{destLookup.status}</span>
                    {isCrossPlan && (
                      <span className="ms-2 badge bg-warning text-dark">CROSS-SO</span>
                    )}
                    {isRideAlong && (
                      <span className="ms-2 badge bg-info text-dark">RIDE-ALONG</span>
                    )}
                    {cap > 0 && (
                      <div className="mt-1">
                        Projected weight: <strong>{Math.round(projected)} lb</strong>
                        {' '} / cap {Math.round(cap)} lb
                        {overCap && (
                          <span className="ms-2 badge bg-danger">
                            exceeds cap by {Math.round(projected - cap)} lb
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <label className="form-label">Operator name <span className="text-muted small">(optional)</span></label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={movedBy}
                  onChange={(e) => setMovedBy(e.target.value)}
                  placeholder="e.g. Mike"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Notes <span className="text-muted small">(optional)</span></label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Why this consolidation, etc."
                />
              </div>
            </div>

            {submitErr && (
              <div className="alert alert-danger py-2 small mb-2">
                {submitErr}
                {submitErr.toLowerCase().includes('weight') && (
                  <div className="mt-2 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="overrideWeightChk"
                      checked={overrideWeight}
                      onChange={(e) => setOverrideWeight(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="overrideWeightChk">
                      Override the weight cap (I've confirmed the load is safe)
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={isRideAlong ? "btn btn-info" : "btn btn-warning"}
              disabled={submitBusy || !selectedBoxIds.length || !effectiveDestPalletId}
              onClick={submit}
            >
              {submitBusy
                ? <><span className="spinner-border spinner-border-sm me-1"></span>{isRideAlong ? 'Setting up ride-along…' : 'Moving…'}</>
                : <><i className={`bi ${isRideAlong ? 'bi-arrow-up-right-circle' : 'bi-arrows-collapse'} me-1`}></i>{isRideAlong ? 'Ride-along' : 'Move'} {selectedBoxIds.length} box(es) → {effectiveDestPallet ? (effectiveDestPallet.short_id || `P${effectiveDestPallet.pallet_num}`) : '?'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Put inside a carton (jobq-api 2026-11-04, one carton holding two orders' made parts). Lists the same customer's
// cartons on other open orders' Building / Ready pallets (GET .../nest-targets) and POSTs .../nest. The box stays its
// own order's (billing, qty_shipped, Delivered); it is drawn, counted and labelled as contents of the carton. Same
// hand-rolled modal as SOMovePalletModal. `planId` = the box's own plan.
const SONestBoxModal = ({ apiBase, planId, box, onClose, onNested }) => {
  const [targets, setTargets] = useState([]);
  const [loadBusy, setLoadBusy] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [hostBoxId, setHostBoxId] = useState(null);
  const [notes, setNotes] = useState('');
  const [movedBy, setMovedBy] = useState('');
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const name = box ? (box.short_id || boxLabel(box)) : '';

  useEffect(() => {
    if (!planId || !box) return undefined;
    let cancelled = false;
    setLoadBusy(true); setLoadErr('');
    fetch(`${apiBase}/packaging/plans/${planId}/boxes/${box.box_id}/nest-targets`)
      .then(async (r) => {
        if (!r.ok) {
          const b = await r.json().catch(() => ({}));
          throw new Error(detailToString(b.detail) || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        const list = data?.targets || [];
        setTargets(list);
        const firstAllowed = list.find(t => t.allowed !== false);
        setHostBoxId(firstAllowed ? firstAllowed.box_id : null);
      })
      .catch((err) => { if (!cancelled) setLoadErr(err.message || String(err)); })
      .finally(() => { if (!cancelled) setLoadBusy(false); });
    return () => { cancelled = true; };
  }, [apiBase, planId, box]);

  if (!box) return null;

  const fmtLb = (n) => (n == null || n === '' ? null : `${Math.round(Number(n))} lb`);

  const submit = async (overrideWeight = false) => {
    if (!hostBoxId) { setSubmitErr('Pick a carton.'); return; }
    setSubmitBusy(true); setSubmitErr('');
    try {
      const res = await fetch(`${apiBase}/packaging/plans/${planId}/boxes/${box.box_id}/nest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host_box_id: hostBoxId,
          moved_by: movedBy || planEditor(),
          notes: notes || null,
          override_weight_warning: overrideWeight,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        const detail = detailToString(b.detail) || `HTTP ${res.status}`;
        // 409 over the carton's max weight: the operator may confirm and resubmit with the override.
        if (res.status === 409 && !overrideWeight && /weight/i.test(detail)) {
          if (window.confirm(`${detail}\n\nPut ${name} inside anyway? (I've checked the carton holds it.)`)) {
            setSubmitBusy(false);
            await submit(true);
          }
          return;
        }
        throw new Error(detail);
      }
      const result = await res.json();
      onNested(result);
    } catch (err) {
      setSubmitErr(err.message || String(err));
    } finally {
      setSubmitBusy(false);
    }
  };

  const picked = targets.find(t => t.box_id === hostBoxId) || null;

  return (
    <div
      className="modal show d-block"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      tabIndex="-1"
      role="dialog"
      data-testid="so-nest-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-box-arrow-in-down me-2"></i>
              Put
              <span className="mx-2 badge bg-dark" style={{ fontFamily: 'monospace' }}>{name}</span>
              inside a carton
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="small text-muted mb-2">
              The same customer's cartons on other open orders' Building / Ready pallets. {name} stays its own
              order's box (billing and shipping credit unchanged); the carton's label lists both orders' lines, and
              {' '}{name} is sealed with the carton.
            </div>
            {loadBusy && <div className="small text-muted"><span className="spinner-border spinner-border-sm me-1"></span>Loading cartons…</div>}
            {loadErr && <div className="alert alert-danger py-2 small">{loadErr}</div>}
            {!loadBusy && !loadErr && targets.length === 0 && (
              <div className="alert alert-secondary py-2 small mb-2">
                No carton to put it in: the customer has no other open order with a carton on a Building or Ready pallet.
              </div>
            )}
            {targets.length > 0 && (
              <div className="border rounded p-2 mb-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
                {targets.map(t => {
                  const allowed = t.allowed !== false;
                  const over = t.max_weight_lbs != null && t.weight_after_lbs != null
                    && Number(t.weight_after_lbs) > Number(t.max_weight_lbs);
                  const id = `nest-target-${t.box_id}`;
                  return (
                    <div key={t.box_id} className={`form-check mb-1 ${allowed ? '' : 'text-muted'}`} data-testid={id}>
                      <input
                        type="radio"
                        className="form-check-input"
                        name="nest-target"
                        id={id}
                        disabled={!allowed}
                        checked={hostBoxId === t.box_id}
                        onChange={() => setHostBoxId(t.box_id)}
                      />
                      <label className="form-check-label small" htmlFor={id}>
                        <strong>{t.order_number}</strong>
                        {' · '}{t.pallet_short_id || `P${t.pallet_num}`}
                        {t.pallet_status ? <span className="text-muted"> ({t.pallet_status})</span> : null}
                        {' · '}<span style={{ fontFamily: 'monospace' }}>{t.short_id}</span>
                        {' '}{t.box_name || t.sku}
                        {t.status ? <span className="text-muted"> ({t.status})</span> : null}
                        {t.contents ? <> · {t.contents}</> : null}
                        {(t.weight_after_lbs != null || t.max_weight_lbs != null) && (
                          <div className={over ? 'text-danger' : 'text-muted'}>
                            Weight after: {fmtLb(t.weight_after_lbs) || '?'}
                            {t.max_weight_lbs != null ? ` of max ${fmtLb(t.max_weight_lbs)}` : ''}
                            {over ? ' (over the carton’s limit)' : ''}
                          </div>
                        )}
                        {!allowed && t.reason && <div className="text-danger">{t.reason}</div>}
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="row g-2">
              <div className="col-md-5">
                <label className="form-label small mb-1">Done by</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={movedBy}
                  onChange={(e) => setMovedBy(e.target.value)}
                  placeholder={planEditor() || 'Your name'}
                />
              </div>
              <div className="col-md-7">
                <label className="form-label small mb-1">Notes (optional)</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Why one carton, etc."
                />
              </div>
            </div>
            {submitErr && <div className="alert alert-danger py-2 small mt-2 mb-0">{submitErr}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={submitBusy || !hostBoxId}
              onClick={() => submit(false)}
            >
              {submitBusy
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Putting inside…</>
                : <><i className="bi bi-box-arrow-in-down me-1"></i>Put inside {picked ? picked.short_id : '…'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Per-zone edit controls rendered inside SOPlannedBoxCard when Edit-plan
// mode is on. Local state on the qty input so typing doesn't re-render the
// whole box tree. "Save" commits; discard (X) reverts to current zone qty.
const ZoneEditRow = ({ zone, box, busy, eligibleDestBoxes, onMoveZone, onEditZoneQty, onDeleteZone, onInspectPart }) => {
  // Assembly-aware units — mirrors the rollup at the bottom of
  // SOPlannedBoxCard so the Edit-box overlay shows "ASY311787 1/1" instead
  // of "FAB308616 4/4" when the zone is an assembly. Storage stays in
  // primary-part-space; for assembly zones the qty INPUT also displays and
  // edits in FINISHED-ASSEMBLY units (matching the 2/2 label beside it) and
  // converts back ×ppq at the save boundary — otherwise the row read
  // "ASY311787-LUG 2/2" next to an inexplicable "8".
  // B-5: codified primary-part-space → assembly count via assemblyMath helper.
  // Units: features/salesOrders/zoneUnits.js (the box is in assemblies on an assembly zone; the API takes pieces).
  const zoneIsAssembly = !!zone.assembly_id && !!zone.assembly_name;
  const zonePpq = Math.max(1, parseInt(zone.primary_part_quantity, 10) || 1);
  const { unitFactor, unitWord, displayPlanned: displayPlannedQty } = zoneUnits(zone);
  const [qtyInput, setQtyInput] = React.useState(String(displayPlannedQty));
  // Reset input when the underlying zone qty changes (e.g. after a successful save).
  React.useEffect(() => {
    setQtyInput(String(displayPlannedQty));
  }, [displayPlannedQty]);
  const parsed = parseInt(qtyInput, 10);
  const dirty = Number.isFinite(parsed) && parsed > 0 && parsed !== displayPlannedQty;
  const editKey = `edit_zone_${zone.zone_id}`;
  const delKey = `del_zone_${zone.zone_id}`;
  const moveKey = `move_zone_${zone.zone_id}`;
  // Destructive reduce: the proposed qty drops planned below current actual,
  // which the backend rejects (and pre-2026-05-12 would silently orphan
  // already-routed pieces). The ✓ button stays interactive but red + a
  // descriptive tooltip; user is funneled toward the Move-to dropdown for
  // a split that preserves the routings. Compared in primary-part-space.
  const actualQty = zone.actual_qty || 0;
  const isDestructiveReduce = dirty && (parsed * unitFactor) < actualQty;
  const saveEnabled = dirty && !busy && !isDestructiveReduce;
  const saveQty = () => {
    if (!saveEnabled || typeof onEditZoneQty !== 'function') return;
    onEditZoneQty(zone.zone_id, parsed * unitFactor);
  };
  const move = zoneMoveQty(zone, qtyInput);
  const moveText = zoneMoveText(zone, qtyInput);
  const zoneDisplayActual = zoneIsAssembly
    ? assemblyCountFromPrimarySpace(zone.actual_qty, zonePpq)
    : (zone.actual_qty || 0);
  const zoneDisplayPlanned = zoneIsAssembly
    ? assemblyCountFromPrimarySpace(zone.planned_qty, zonePpq)
    : (zone.planned_qty || 0);
  return (
    <div className="d-flex align-items-center gap-1 mb-1 flex-wrap">
      <span className="flex-grow-1 text-truncate" style={{ minWidth: 120 }}>
        {zoneIsAssembly ? (
          <>
            <span
              className="badge bg-success me-1"
              style={{ fontSize: '0.65em', verticalAlign: 'middle' }}
              title={`Assembly — primary part ${zone.product_id || `#${zone.part_id}`}, ${zonePpq}× per assembly`}
            >
              ASSY
            </span>
            <span className="fw-semibold">{zone.assembly_name}</span>
          </>
        ) : (
          <ProductIdLink
            productId={zone.product_id || (zone.part_id ? `part #${zone.part_id}` : '—')}
            partId={zone.part_id}
            onInspect={onInspectPart}
          />
        )}
        <span className="text-muted ms-1">{zoneDisplayActual}/{zoneDisplayPlanned}</span>
      </span>
      {typeof onEditZoneQty === 'function' && (
        <>
          <input
            type="number"
            className={`form-control form-control-sm ${isDestructiveReduce ? 'border-danger' : ''}`}
            style={{ width: 64 }}
            min={1}
            placeholder={unitFactor > 1 ? 'asm' : 'pcs'}
            aria-label={`Quantity (${unitWord})`}
            value={qtyInput}
            disabled={!!busy}
            onChange={(e) => setQtyInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveQty(); }}
            title={
              isDestructiveReduce
                ? `Would orphan ${zoneDisplayActual - parsed} already-routed ${unitWord}. Use the Move-to dropdown on the right to split into another stack/box (keeps the pieces).`
                : `Edit planned quantity${unitFactor > 1 ? ' (finished assemblies)' : ''}. Backend caps this at the SO ordered qty — or the produced qty, if more was made under a Min+Extras yield policy.`
            }
          />
          <button
            className={`btn btn-sm ${
              isDestructiveReduce
                ? 'btn-danger'
                : (dirty ? 'btn-success' : 'btn-outline-secondary')
            }`}
            disabled={!saveEnabled}
            onClick={saveQty}
            title={
              isDestructiveReduce
                ? `Disabled — would orphan ${zoneDisplayActual - parsed} already-routed ${unitWord}. Use Move-to → to split into another stack/box instead.`
                : (dirty ? `Save new quantity (${parsed} ${unitWord})` : 'Quantity unchanged')
            }
          >
            {busy === editKey
              ? <span className="spinner-border spinner-border-sm"></span>
              : <i className={`bi ${isDestructiveReduce ? 'bi-exclamation-triangle' : 'bi-check-lg'}`}></i>}
          </button>
        </>
      )}
      <select
        className="form-select form-select-sm"
        style={{ maxWidth: 200 }}
        value=""
        disabled={!!busy}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) return;
          const destId = parseInt(v, 10);
          if (destId && destId !== box.box_id && typeof onMoveZone === 'function') {
            // A number below the whole zone (in the unit shown) = a SPLIT of that many, sent in PIECES
            // (the API's qty); empty / the whole zone / more = move the entire zone (qty omitted).
            onMoveZone(zone.zone_id, destId, move.qty);
          }
          e.target.value = '';
        }}
        title={moveText.title}
      >
        <option value="">{moveText.option}</option>
        {(eligibleDestBoxes || [])
          .filter(d => d.box_id !== box.box_id)
          .map(d => (
            <option key={d.box_id} value={d.box_id}>{d.label}</option>
          ))}
      </select>
      {typeof onDeleteZone === 'function' && (
        <button
          className="btn btn-sm btn-outline-danger"
          disabled={!!busy}
          onClick={() => onDeleteZone(zone.zone_id, zone.product_id)}
          title="Remove this zone from the box entirely (wipe the assignment, not just zero the qty)."
        >
          {busy === delKey
            ? <span className="spinner-border spinner-border-sm"></span>
            : <i className="bi bi-trash"></i>}
        </button>
      )}
      {busy === moveKey && (
        <span className="spinner-border spinner-border-sm text-muted"></span>
      )}
    </div>
  );
};

const SOPlannedBoxCard = ({
  box, homeSalesOrderId, palletBoxes = [], planId = null, onOpenNestModal, onUnnestBox,
  palletId, palletNum, palletStatus, palletShipmentGroupId,
  busy, outlineCache, onSealBox, onReprintBoxLabel, onUnsealBox,
  onPrintBoxStatusLabel, onPrintStackStatusLabel,
  editMode, onMoveZone, onEditZoneQty, onDeleteZone,
  onDeleteEmptyBox, eligibleDestBoxes, onInspectPart,
  purchasedOptions, onAddPurchasedItem, onDeletePurchasedItem,
}) => {
  const isDirect = box.is_pallet_direct;
  const intL = box.interior_l || 0;
  const intW = box.interior_w || 0;
  const svgW = 180;
  const scale = intL > 0 ? svgW / intL : 1;
  const svgH = Math.max(40, intW * scale);
  const zones = box.zones || [];
  // Purchased items (standalone purchased-component SO lines packed as box
  // contents) count as planned AND packed — operator-asserted placement.
  const purchased = box.purchased_items || [];
  const purchasedQty = purchased.reduce((a, it) => a + (Number(it.quantity) || 0), 0);
  const totalActual = zones.reduce((a, z) => a + (z.actual_qty || 0), 0) + purchasedQty;
  const totalPlanned = zones.reduce((a, z) => a + (z.planned_qty || 0), 0) + purchasedQty;
  const fillPct = totalPlanned > 0 ? Math.round(100 * totalActual / totalPlanned) : 0;
  // When EVERY zone in the box belongs to assemblies with one uniform ppq,
  // the box-level counts read in finished-assembly units ("10/10 assemblies"
  // instead of "40/40 pcs") and "Filled by" divides the same way. Mixed or
  // non-assembly boxes keep piece counts.
  const boxAssemblyPpq = (() => {
    if (!zones.length) return null;
    let ppq = null;
    for (const z of zones) {
      if (!z.assembly_id) return null;
      const p = Math.max(1, parseInt(z.primary_part_quantity, 10) || 1);
      if (ppq === null) ppq = p;
      else if (ppq !== p) return null;
    }
    return ppq && ppq > 1 ? ppq : null;
  })();
  const fmtBoxQty = (n) => boxAssemblyPpq ? Math.floor((n || 0) / boxAssemblyPpq) : (n || 0);
  const boxQtyUnit = boxAssemblyPpq ? 'assemblies' : 'pcs';
  // effective_status (from load_plan) accounts for the SO's yield policy:
  // when actual < planned but the SO-line minimum is met cumulatively AND
  // production is done, the box flips from raw 'Partial' to 'FullPerPolicy'
  // so the operator can seal a short-but-shippable box (defect / over-prod
  // shortfall under "Min + Extras (Bill Ordered)" et al.). Falls back to
  // raw box.status on legacy plans where the field hasn't been computed.
  const effStatus = box.effective_status || box.status;
  const statusColor = {
    Empty: 'secondary', Partial: 'warning', Full: 'success',
    FullPerPolicy: 'success', Sealed: 'primary',
  }[effStatus] || 'secondary';
  const totalShort = Math.max(0, totalPlanned - totalActual);
  // Shortage in the box's display unit (whole assemblies for an assembly box,
  // else pieces). Ceil for the "needs N more" count so a partial assembly still
  // reads "needs 1 more", never "needs 0 more". Floor for the policy-accepted
  // gap on a FullPerPolicy box (matches the "8/10 assemblies" style elsewhere).
  const shortDisplay = boxAssemblyPpq ? Math.ceil(totalShort / boxAssemblyPpq) : totalShort;
  const acceptedShortDisplay = fmtBoxQty(totalShort);
  // A "genuine" shortage is a box that is short of planned AND has FINISHED all
  // its production (backend `production_done`) yet the yield policy still didn't
  // clear it to seal (line minimum not met, or exact-count / below-minimum
  // policy) — the shop genuinely needs to cut more. A box that is merely still
  // being produced (production_done false) is normal WIP and stays a neutral
  // 'Partial' chip: gating on production_done is what stops the red badge from
  // firing on every in-progress box across every plan. Legacy plans (no
  // production_done field) fall back to the old behavior only when the field is
  // absent AND the box is short — but with the field present it's authoritative.
  const needsMore = effStatus === 'Partial' && totalShort > 0
    && box.production_done === true;
  const badgeText = effStatus === 'FullPerPolicy'
    ? `Ready (-${acceptedShortDisplay})`
    : needsMore
      ? `Needs ${shortDisplay} more`
      : effStatus;
  const badgeTitle = effStatus === 'FullPerPolicy'
    ? (
        `Box is ${acceptedShortDisplay} ${boxQtyUnit} short of planned, but the SO ` +
        `line minimum is met cumulatively under the yield policy and production ` +
        `is done — safe to seal.`
      )
    : needsMore
      ? (
          `Production is done for this box's part(s) but it's ${shortDisplay} ` +
          `${boxQtyUnit} short of planned and the yield policy has NOT cleared it ` +
          `to seal (line minimum not met, or an exact-count / below-minimum ` +
          `policy). Cut ${shortDisplay} more to complete this box.`
        )
      : null;
  // Genuine shortage gets a red chip (needs action) vs the yellow "in
  // progress" Partial — the operator must schedule more production, this
  // won't clear itself by sealing.
  const statusBadgeColor = needsMore ? 'danger' : statusColor;
  // Per-box Seal button. Only shown for real boxes (pallet-direct stacks
  // get labelled at pallet-seal). Enabled when the box is sealable per the
  // raw rule (actual >= planned) OR per the yield-policy rule (line min
  // met + production done), provided the pallet itself is still Building.
  const boxRules = boxActionState(box, { palletStatus, palletShipmentGroupId }); // shared with the phone
  const canSealBox = boxRules.canSeal && typeof onSealBox === 'function';
  const sealBoxKey = `seal_box_${box.box_id}`;
  // Other orders' boxes packed inside this carton (load_plan `nested_box_ids`, found in the same pallet's boxes).
  const insideBoxes = nestedBoxesOf(box, palletBoxes);
  const canTakeOut = typeof onUnnestBox === 'function' && (palletStatus === 'Building' || palletStatus === 'Ready');
  const canPutInside = typeof onOpenNestModal === 'function' && boxRules.canNest;
  // Reprint-label button lives on sealed boxes. load_plan joins
  // shipment_pallet_boxes via (shipment_group_id, box_num) and surfaces
  // shipment_box_id so we can point the label endpoint at the exact box
  // that was sealed (and not regenerate a fresh label for a different box).
  const canReprintBoxLabel = boxRules.canReprintLabel && typeof onReprintBoxLabel === 'function';
  const reprintBoxKey = `reprint_box_${box.shipment_box_id || ''}`;
  // Contents by part (palletRules.boxContents, shared with the phone): assembly zones count in
  // primary-part space and display whole assemblies ("250 of MEB CRIBBING", not "250 of MEBDIVIDER").
  const rolled = boxContents(box);
  // Stable global container ID (BOX-XXXX format on both real cartons
  // and pallet-direct stacks). Replaces the gap-prone B# / S# ordinal
  // as the operator-facing handle — matches the label printed on the
  // physical container and every reference on manifests, slips, and
  // radio/Slack calls. Falls back to legacy ordinal only on pre-Apr-2026
  // rows that lack a short_id.
  const headerLabel = boxLabel(box); // palletRules.boxLabel
  // SKU badge carries interior dims (24×18×4″) alongside the Uline code
  // because no-one memorizes the catalog — operators need to grab the
  // right carton from the shelf without cross-referencing a sheet.
  // Falls back to SKU-only when any dim is missing (legacy rows).
  const boxDimsSuffix = (!isDirect && box.interior_l && box.interior_w && box.interior_h)
    ? ` · ${(+box.interior_l)}×${(+box.interior_w)}×${(+box.interior_h)}″`
    : '';
  const skuBadge = isDirect
    ? <span className="badge bg-info text-dark ms-1">DIRECT</span>
    : <span className="badge bg-light text-dark ms-1">{box.sku}{boxDimsSuffix}</span>;
  // Direct stacks: physical stack height is set on interior_h by the
  // backend (thickness × stack_height). Fall back to per-piece × qty if
  // not available. Hide the label if neither is computable.
  let directHeightLabel = null;
  if (isDirect) {
    const z0 = zones[0] || {};
    const candidate = box.interior_h && box.interior_h > 0
      ? box.interior_h
      : (z0.thickness_in > 0 && z0.stack_height ? z0.thickness_in * z0.stack_height : 0);
    if (candidate > 0) {
      directHeightLabel = `~${candidate.toFixed(candidate < 10 ? 2 : 1)}″ tall`;
    }
  }
  return (
    <div className="border rounded p-2 bg-white h-100" style={isDirect ? { borderStyle: 'dashed' } : null}>
      <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-1">
        <span className="fw-bold d-inline-flex align-items-center gap-1">
          <span
            className="badge bg-dark"
            style={{ fontFamily: 'monospace', letterSpacing: '0.5px', fontSize: '0.95em' }}
            title={`Stable global ${isDirect ? 'stack' : 'box'} ID — printed on the ${isDirect ? 'stack' : 'box'} label and on every manifest/slip/Slack reference. Replaces the gap-prone ${isDirect ? 'S#' : 'B#'} ordinal.`}
          >
            {headerLabel}
          </span>
          {isDirect && <span className="badge bg-secondary text-light ms-1" title="Pallet-direct stack — loose parts on the wood pallet, no carton.">STACK</span>}
          {skuBadge}
          {/* Incoming cross-plan ride-along badge: this box's planning home
              is another SO (same customer); it's physically riding on this
              SO's pallet for shipping efficiency. The pickup advances the
              source SO's Shipping tasks (not this SO's), and billing/yield
              stays with the source SO. Operators on this SO see exactly
              what's physically on the pallet, with attribution clearly
              flagged so they know which packing slip the contents come from. */}
          {box.riding_from_plan_id && (
            <span
              className="badge bg-info text-dark"
              title={`This ${isDirect ? 'stack' : 'box'} belongs to ${box.riding_from_so_number || 'another SO'} (same customer) and is riding on this pallet. Pickup credits ${box.riding_from_so_number || 'the source SO'}'s Shipping tasks; billing/yield stays there.`}
            >
              <i className="bi bi-arrow-down-right me-1"></i>
              Riding from {box.riding_from_so_number || `plan ${box.riding_from_plan_id}`}
            </span>
          )}
          {isNested(box) && (
            <span className="badge bg-warning text-dark" title="This box is packed inside another carton and is sealed with it.">
              {insideText(box)}
            </span>
          )}
        </span>
        <span className={`badge bg-${statusBadgeColor}`} title={badgeTitle || undefined}>
          {needsMore && <i className="bi bi-exclamation-triangle-fill me-1"></i>}
          {badgeText}
        </span>
      </div>
      {needsMore && (
        <div
          className="small text-danger fw-semibold mb-1 d-flex align-items-center gap-1"
          title={badgeTitle}
        >
          <i className="bi bi-scissors"></i>
          Cut {shortDisplay} more {boxQtyUnit} — production done but short of the order minimum
        </div>
      )}
      {canSealBox && (
        <div className="mb-2">
          <button
            className="btn btn-sm btn-warning w-100"
            disabled={!!busy}
            title={`Seal ${headerLabel} on Pallet P${palletNum} — creates the shipment record for this box and opens just this box's label.${insideBoxes.length ? ` Also seals the ${insideBoxes.length} box(es) inside it (one label).` : ''}`}
            onClick={() => onSealBox(palletId, palletNum, box.box_id, headerLabel)}
          >
            {busy === sealBoxKey
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Sealing...</>
              : <><i className="bi bi-lock-fill me-1"></i>Seal {headerLabel} & print label</>}
          </button>
        </div>
      )}
      {canReprintBoxLabel && (
        <div className="mb-2">
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            disabled={!!busy}
            title={`Reprint ${headerLabel}'s label — useful if the original was smudged, torn, or never printed.`}
            onClick={() => onReprintBoxLabel(palletShipmentGroupId, box.shipment_box_id, headerLabel)}
          >
            {busy === reprintBoxKey
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Reprinting...</>
              : <><i className="bi bi-printer me-1"></i>Reprint {headerLabel} label</>}
          </button>
        </div>
      )}
      {/* "Print status label" for this individual box / stack. Reflects
          live actual_qty per zone — Partial → IN PROGRESS banner,
          Full → READY TO SEAL banner. Goes through the laser-Pi thermal
          printer (NOT the browser print dialog) so the operator gets a
          4×6 ZP 505 label from anywhere in the UI. Available on every
          status, including Sealed (the label is still useful for
          reconciling a stuck/dropped post-pack auto-print). */}
      {!isDirect && typeof onPrintBoxStatusLabel === 'function' && (
        <div className="mb-2">
          <button
            className="btn btn-sm btn-outline-info w-100"
            disabled={!!busy}
            title={`Print a fresh 4×6 thermal status label for ${headerLabel} at the laser-Pi printer. Reflects live actual/planned qty per zone and the right banner (IN PROGRESS / READY TO SEAL).`}
            onClick={() => onPrintBoxStatusLabel(box.box_id, box.short_id || headerLabel)}
          >
            {busy === `status_label_box_status_label_${box.short_id || headerLabel}`
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Queuing…</>
              : <><i className="bi bi-printer me-1"></i>Print {headerLabel} status</>}
          </button>
        </div>
      )}
      {isDirect && typeof onPrintStackStatusLabel === 'function' && (
        <div className="mb-2">
          <button
            className="btn btn-sm btn-outline-info w-100"
            disabled={!!busy}
            title={`Print a fresh 4×6 thermal status label for stack ${headerLabel} at the laser-Pi printer. Sticks to the TOP of the stack so the forklift driver / next operator sees part identity + current count at a glance.`}
            onClick={() => onPrintStackStatusLabel(box.box_id, box.short_id || headerLabel)}
          >
            {busy === `status_label_stack_status_label_${box.short_id || headerLabel}`
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Queuing…</>
              : <><i className="bi bi-printer me-1"></i>Print {headerLabel} status</>}
          </button>
        </div>
      )}
      {/* Box-level unseal: reverses a premature Seal box on a still-
          Building pallet so the operator can add zones, move the box to
          another pallet, or delete it. Pallet-level unseal lives on the
          pallet action bar (Ready pallets only). */}
      {boxRules.canUnseal
        && typeof onUnsealBox === 'function' && (
        <div className="mb-2">
          <button
            className="btn btn-sm btn-outline-danger w-100"
            disabled={!!busy}
            title={`Unseal ${headerLabel} — removes it from the shipment system and flips it back to Full so you can edit zones or move the box. The printed label becomes stale.`}
            onClick={() => onUnsealBox(palletId, palletNum, box.box_id, headerLabel)}
          >
            {busy === `unseal_box_${box.box_id}`
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Unsealing...</>
              : <><i className="bi bi-unlock me-1"></i>Unseal {headerLabel}</>}
          </button>
        </div>
      )}
      {canPutInside && (
        <div className="mb-2">
          <button
            className="btn btn-sm btn-outline-primary w-100"
            disabled={!!busy}
            title={`Pack ${headerLabel} inside a same-customer order's carton on its pallet: one carton, one label, each order keeps its own pieces.`}
            onClick={() => onOpenNestModal(box, box.riding_from_plan_id || planId)}
          >
            <i className="bi bi-box-arrow-in-down me-1"></i>Put inside a carton…
          </button>
        </div>
      )}
      <div className="small text-muted mb-1">
        {isDirect
          ? <>Pallet stack · {intL}″ × {intW}″{directHeightLabel ? ` · ${directHeightLabel}` : ''}</>
          : <>{box.box_name} · {intL}″ × {intW}″ × {box.interior_h}″</>
        }
      </div>
      <svg width={svgW} height={svgH} style={{ background: '#fafafa', border: '1px solid #333', display: 'block' }}>
        {/* Zones without shelf-pack geometry (e.g. moved or manually-assigned
            via Edit mode) render as equal horizontal strips filling the
            interior so the operator still sees one labeled cell per part. */}
        {(() => {
          const anyMissingGeo = zones.some(z => !z.zone_w || !z.zone_h);
          const renderZones = anyMissingGeo && zones.length > 0 && intW > 0 && intL > 0
            ? zones.map((z, i) => ({
                ...z,
                _fallback_layout: true,
                zone_x: z.zone_w && z.zone_h ? z.zone_x : 0,
                zone_y: z.zone_w && z.zone_h ? z.zone_y : (intW / zones.length) * i,
                zone_w: z.zone_w && z.zone_h ? z.zone_w : intL,
                zone_h: z.zone_w && z.zone_h ? z.zone_h : (intW / zones.length),
                tile_cols: z.zone_w && z.zone_h ? z.tile_cols : null,
                tile_rows: z.zone_w && z.zone_h ? z.tile_rows : null,
              }))
            : zones;
          return renderZones.map((z, i) => {
          const color = SO_PKG_COLOR_PALETTE[(z.color_idx || 0) % SO_PKG_COLOR_PALETTE.length];
          const x = (z.zone_x || 0) * scale;
          const y = (intW - ((z.zone_y || 0) + (z.zone_h || 0))) * scale;
          const w = (z.zone_w || 0) * scale;
          const h = (z.zone_h || 0) * scale;
          const shortFall = (z.actual_qty || 0) < z.planned_qty;
          // shortAccepted (from load_plan) — the gap exists but the SO-line
          // minimum is met cumulatively and production is done; the slot
          // won't be filled. Render with a dashed stroke instead of the
          // washed-out "still pending" opacity, plus a hover title.
          const shortAccepted = !!z.short_accepted;
          // Pallet-direct spread block: render a tile_cols × tile_rows
          // grid of columns so the operator sees the 30 physical stacks
          // on the pallet, not one big rectangle. Label each cell with
          // the per-column stack height. Falls back to the single-rect
          // rendering when tile_cols/tile_rows are missing (legacy
          // pre-migration zones).
          // Pre-rendered DXF outline (inlined so stroke stays crisp at
          // any cell size — `<image>` stroke goes sub-pixel on tiny
          // cells and small holes disappear). Data comes from the
          // plan-level outline cache; missing entry → label-only.
          const outlineData = (outlineCache && z.outline_svg_path)
            ? outlineCache[z.outline_svg_path] : null;
          const hasOutline = !!(outlineData && outlineData.paths && outlineData.paths.length);
          if (isDirect && z.tile_cols && z.tile_rows) {
            const cellW = w / z.tile_cols;
            const cellH = h / z.tile_rows;
            const cellFontSize = Math.min(9, Math.max(5, Math.min(cellW, cellH) / 2.5));
            const cells = [];
            // Per-column height from planned_qty, not the stored
            // stack_height (stale-proof — see SOPalletMap note). First
            // `tallCells` columns carry one extra layer when qty doesn't
            // divide evenly across the tile grid.
            // One rule for every surface (utils/stackLayout).
            const zoneLay = zoneStackLayout(z);
            let cellSeq = 0;
            for (let r = 0; r < z.tile_rows; r++) {
              for (let c = 0; c < z.tile_cols; c++) {
                const cx = x + c * cellW;
                const cy = y + r * cellH;
                const cellLayers = cellHeight(zoneLay, cellSeq);
                cellSeq += 1;
                cells.push(
                  <g key={`${z.zone_id || i}-${r}-${c}`}>
                    <rect x={cx} y={cy} width={cellW} height={cellH}
                      fill={color} stroke="#222" strokeWidth={0.5}
                      strokeDasharray={shortAccepted ? '3 2' : null}
                      fillOpacity={shortAccepted ? 0.95 : (shortFall ? 0.55 : 0.95)}>
                      {shortAccepted && <title>Short {z.short_qty} — accepted under yield policy (line min met cumulatively, production done)</title>}
                    </rect>
                    {hasOutline && Math.min(cellW, cellH) >= 8 && (
                      <OutlineOverlay data={outlineData}
                        cx={cx + 1} cy={cy + 1}
                        cw={cellW - 2} ch={cellH - 2}
                        autoOrient />
                    )}
                    <text x={cx + cellW / 2} y={cy + cellH / 2 + 3} textAnchor="middle"
                      fontSize={cellFontSize}
                      fontWeight="bold" fill="#111"
                      style={hasOutline ? { paintOrder: 'stroke', stroke: '#fff', strokeWidth: 2 } : null}>
                      {cellLayers || ''}
                    </text>
                  </g>
                );
              }
            }
            return <g key={z.zone_id || i}>{cells}</g>;
          }
          // Boxed zone: single rect with optional outline overlay.
          // shortAccepted zones get a dashed border + show actual qty
          // (no "/planned" suffix) since the missing slot won't be filled.
          return (
            <g key={z.zone_id || i}>
              <rect x={x} y={y} width={w} height={h} fill={color}
                stroke="#222" strokeWidth={0.5}
                strokeDasharray={shortAccepted ? '3 2' : null}
                fillOpacity={shortAccepted ? 0.95 : (shortFall ? 0.55 : 0.95)}>
                {shortAccepted && <title>Short {z.short_qty} — accepted under yield policy (line min met cumulatively, production done)</title>}
              </rect>
              {hasOutline && Math.min(w, h) >= 10 && (
                <OutlineOverlay data={outlineData}
                  cx={x + 1} cy={y + 1}
                  cw={w - 2} ch={h - 2}
                  autoOrient />
              )}
              <text x={x + w / 2} y={y + h / 2 + 3} textAnchor="middle"
                fontSize={Math.min(10, Math.max(6, Math.min(w, h) / 2.5))}
                fontWeight="bold" fill="#111"
                style={hasOutline ? { paintOrder: 'stroke', stroke: '#fff', strokeWidth: 2 } : null}>
                {(() => {
                  // Assembly cells render in assembly-count-space (ppq divided)
                  // so a ppq=4 assembly reads "1/1" per cell, not "4/4".
                  // B-5: codified primary-part-space → assembly count.
                  const cellIsAssembly = !!z.assembly_id && !!z.assembly_name;
                  const cellPpq = Math.max(1, parseInt(z.primary_part_quantity, 10) || 1);
                  const cellActual = cellIsAssembly
                    ? assemblyCountFromPrimarySpace(z.actual_qty, cellPpq)
                    : (z.actual_qty || 0);
                  const cellPlanned = cellIsAssembly
                    ? assemblyCountFromPrimarySpace(z.planned_qty, cellPpq)
                    : (z.planned_qty || 0);
                  return shortAccepted ? `${cellActual}` : `${cellActual}/${cellPlanned}`;
                })()}
              </text>
            </g>
          );
        });
        })()}
      </svg>
      <div className="progress mt-1" style={{ height: 6 }}>
        <div className={`progress-bar bg-${statusColor}`}
             style={{ width: `${effStatus === 'FullPerPolicy' ? 100 : fillPct}%` }}></div>
      </div>
      <div className="small text-muted mt-1">
        {effStatus === 'FullPerPolicy' ? (
          <span title={badgeTitle || undefined}>
            {fmtBoxQty(totalActual)} {boxQtyUnit} · {fmtBoxQty(totalShort)} short ✓ · Rev {box.revision_opened}
          </span>
        ) : (
          <>{fmtBoxQty(totalActual)}/{fmtBoxQty(totalPlanned)} {boxQtyUnit} · Rev {box.revision_opened}</>
        )}
      </div>
      <div className="small mt-1">
        {rolled.map(r => {
          // Assembly rows render the assembly name (operator-facing identity)
          // with a green "Assembly" badge + tooltip pointing at the primary
          // part; the counts are whole assemblies (palletRules.boxContents).
          const { isAssembly, ppq, displayActual, displayPlanned } = r;
          return (
            <div key={r.part_id || r.product_id} className="d-flex justify-content-between">
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {isAssembly ? (
                  <>
                    <span
                      className="badge bg-success me-1"
                      style={{ fontSize: '0.65em', verticalAlign: 'middle' }}
                      title={`Assembly — primary part ${r.product_id || `#${r.part_id}`}, ${ppq}× per assembly`}
                    >
                      ASSY
                    </span>
                    <span className="fw-semibold">{r.assembly_name}</span>
                  </>
                ) : (
                  <ProductIdLink productId={r.product_id} partId={r.part_id} onInspect={onInspectPart} />
                )}
              </span>
              <span className="text-muted ms-1">
                {displayActual}/{displayPlanned}{r.zones > 1 ? ` (${r.zones} zones)` : ''}
              </span>
            </div>
          );
        })}
        {purchased.map((it) => {
          const crossSo = it.sales_order_id && homeSalesOrderId && it.sales_order_id !== homeSalesOrderId;
          return (
            <div key={`purchased-${it.item_id}`} className="d-flex justify-content-between">
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={`Purchased item — ${it.description || ''} (${it.order_number}${it.customer_po ? ` · PO ${it.customer_po}` : ''}). Bought finished and resold; no production.`}>
                <i className="bi bi-cart-fill me-1 text-info"></i>
                <span className="fw-semibold">{it.product_id}</span>
                {crossSo && (
                  <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.65em', verticalAlign: 'middle' }}>
                    {it.order_number}
                  </span>
                )}
              </span>
              <span className="text-muted ms-1">{it.quantity}/{it.quantity}</span>
            </div>
          );
        })}
      </div>
      {insideBoxes.length > 0 && (
        <div className="small mt-1 pt-1 border-top" data-testid={`so-box-inside-${box.box_id}`}>
          <div className="text-muted mb-1">
            <i className="bi bi-box-arrow-in-down me-1"></i>Inside this carton:
          </div>
          {insideBoxes.map((nb) => {
            const nbName = nb.short_id || boxLabel(nb);
            return (
              <div key={nb.box_id} className="border-start ps-2 mb-1" data-testid={`so-nested-box-${nb.box_id}`}>
                <div className="d-flex justify-content-between align-items-center gap-1">
                  <span>
                    {nb.riding_from_so_number && (
                      <span className="badge bg-warning text-dark me-1" style={{ fontSize: '0.65em', verticalAlign: 'middle' }}>
                        {nb.riding_from_so_number}
                      </span>
                    )}
                    <span className="fw-semibold" style={{ fontFamily: 'monospace' }}>{nbName}</span>
                    <span className="text-muted ms-1">{nb.effective_status || nb.status}</span>
                  </span>
                  {canTakeOut && (
                    <button
                      className="btn btn-sm btn-outline-warning py-0 px-1"
                      disabled={!!busy}
                      onClick={() => onUnnestBox(nb)}
                      title={`Take ${nbName} out of ${headerLabel}: it stays on this pallet as a box of its own.`}
                    >
                      {busy === `unnest_${nb.box_id}`
                        ? <span className="spinner-border spinner-border-sm"></span>
                        : <><i className="bi bi-box-arrow-up me-1"></i>Take out</>}
                    </button>
                  )}
                </div>
                {boxContents(nb).map(r => (
                  <div key={`nz-${nb.box_id}-${r.part_id || r.product_id}`} className="d-flex justify-content-between">
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.isAssembly
                        ? <span className="fw-semibold">{r.assembly_name}</span>
                        : <ProductIdLink productId={r.product_id} partId={r.part_id} onInspect={onInspectPart} />}
                    </span>
                    <span className="text-muted ms-1">{r.displayActual}/{r.displayPlanned}</span>
                  </div>
                ))}
                {(nb.purchased_items || []).map(it => (
                  <div key={`np-${it.item_id}`} className="d-flex justify-content-between">
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <i className="bi bi-cart-fill me-1 text-info"></i>
                      <span className="fw-semibold">{it.product_id}</span>
                      {it.order_number && (
                        <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.65em', verticalAlign: 'middle' }}>
                          {it.order_number}
                        </span>
                      )}
                    </span>
                    <span className="text-muted ms-1">{it.quantity}/{it.quantity}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {box.carton_weight_lbs != null && Number(box.carton_weight_lbs) > 0 && (
        <div className="small text-muted mt-1" data-testid={`so-box-carton-weight-${box.box_id}`}>
          Carton ~{Math.round(Number(box.carton_weight_lbs))} lb{insideBoxes.length ? ' (with the boxes inside)' : ''}
        </div>
      )}
      {/* Cross-SO attribution: which jobs filled this box, and from which SO.
          Jobs from a different SO than the plan's home SO are highlighted
          so the operator can see when cross-SO surplus-allocation parts
          landed here. */}
      {(box.placement_sources || []).length > 0 && (
        <div className="small mt-1 pt-1 border-top">
          <div className="text-muted mb-1">
            <i className="bi bi-arrow-down-circle me-1"></i>Filled by:
          </div>
          {box.placement_sources.map((src, i) => {
            const isCrossSo = src.source_sales_order_id && homeSalesOrderId
              && src.source_sales_order_id !== homeSalesOrderId;
            return (
              <div key={i} className="d-flex justify-content-between">
                <span>
                  Job {src.master_job_num}
                  {src.source_so_number && (
                    <span className={`ms-1 ${isCrossSo ? 'badge bg-warning text-dark' : 'text-muted'}`}
                          style={!isCrossSo ? { fontSize: '0.9em' } : null}>
                      {isCrossSo ? '↪ ' : ''}{src.source_so_number}
                    </span>
                  )}
                </span>
                <span className="text-muted">{fmtBoxQty(src.qty)} {boxQtyUnit}</span>
              </div>
            );
          })}
        </div>
      )}
      {editMode && box.status !== 'Sealed' && palletStatus === 'Building' && (
        <div className="mt-2 pt-2 border-top small bg-warning bg-opacity-10 p-2 rounded">
          <div className="fw-bold mb-1">
            <i className="bi bi-pencil-square me-1"></i>
            Edit {isDirect ? `stack ${headerLabel}` : `box ${headerLabel}`}
          </div>
          {zones.length === 0 && purchased.length === 0 && insideBoxes.length === 0 && (
            <div className="text-muted mb-1"><em>Empty — safe to delete.</em></div>
          )}
          {purchased.map((it) => (
            <div key={`purchased-edit-${it.item_id}`} className="d-flex align-items-center gap-1 mb-1">
              <i className="bi bi-cart-fill text-info"></i>
              <span className="flex-grow-1" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.quantity}× {it.product_id} <span className="text-muted">({it.order_number})</span>
              </span>
              {typeof onDeletePurchasedItem === 'function' && (
                <button
                  className="btn btn-sm btn-outline-danger py-0 px-1"
                  disabled={!!busy}
                  onClick={() => onDeletePurchasedItem(it.item_id)}
                  title="Remove this purchased item from the box (the pieces go back to not-in-any-box)."
                >
                  {busy === `purchased_del_${it.item_id}`
                    ? <span className="spinner-border spinner-border-sm"></span>
                    : <i className="bi bi-trash"></i>}
                </button>
              )}
            </div>
          ))}
          {!isDirect && typeof onAddPurchasedItem === 'function' && (purchasedOptions || []).length > 0 && (
            <SOAddPurchasedItemForm box={box} options={purchasedOptions} busy={busy} onAdd={onAddPurchasedItem} />
          )}
          {zones.map((z) => (
            <ZoneEditRow
              key={z.zone_id}
              zone={z}
              box={box}
              busy={busy}
              eligibleDestBoxes={eligibleDestBoxes}
              onMoveZone={onMoveZone}
              onEditZoneQty={onEditZoneQty}
              onDeleteZone={onDeleteZone}
              onInspectPart={onInspectPart}
            />
          ))}
          {zones.length === 0 && purchased.length === 0 && insideBoxes.length === 0 && typeof onDeleteEmptyBox === 'function' && (
            <button
              className="btn btn-sm btn-outline-danger w-100 mt-1"
              disabled={!!busy}
              onClick={() => onDeleteEmptyBox(box.box_id)}
            >
              {busy === `del_box_${box.box_id}`
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Deleting...</>
                : <><i className="bi bi-trash me-1"></i>Delete empty {isDirect ? 'stack' : 'box'}</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Per-row ride-along controls — assigns a non-laser/oversized part to a
// SAME-CUSTOMER pallet on ANOTHER SO. Box's plan_id stays here (so this
// SO retains billing/yield/qty_shipped credit) but its shipped_pallet_id
// points at the host pallet. Backend handles the same-customer guard,
// the dest weight cap, and (when dest is Ready) the immediate
// shipment_pallet_items / shipment_pallet_box_items writes.
const RideAlongAssignRow = ({ part, maxQty, crossSoTargets, busy, onSubmit, planMaxPalletWeightLbs }) => {
  const [qty, setQty] = useState(String(maxQty));
  const [dest, setDest] = useState('');
  const [asPalletDirect, setAsPalletDirect] = useState(true); // default: stack (most non-laser parts)
  const [boxSku, setBoxSku] = useState('');
  const [override, setOverride] = useState(false);
  const [localErr, setLocalErr] = useState('');

  const key = `ridealong_assign_${part.part_id}`;
  const targets = crossSoTargets || [];

  const partWeight = (part.weight_lbs || 0) * (parseInt(qty, 10) || 0);
  const tgt = targets.find(t => String(t.pallet_id) === dest);
  const palletCap = Number(tgt?.max_pallet_weight_lbs) || Number(planMaxPalletWeightLbs) || 0;
  const palletCur = Number(tgt?.current_weight_lbs) || 0;
  const projected = palletCur + partWeight;
  const overCap = palletCap > 0 && projected > palletCap;

  const handleClick = async () => {
    if (!dest) return;
    setLocalErr('');
    const palletId = parseInt(dest, 10);
    const body = {
      part_id: part.part_id,
      quantity: Math.min(Math.max(parseInt(qty, 10) || 0, 1), maxQty),
      dest_pallet_id: palletId,
      as_pallet_direct: !!asPalletDirect,
      override_weight_warning: !!override,
    };
    if (!asPalletDirect) {
      if (!boxSku) {
        setLocalErr('Pick a box SKU first.');
        return;
      }
      body.box_sku = boxSku;
    }
    try {
      const result = await onSubmit(body);
      // Reset form on success.
      if (result !== false) {
        setDest('');
        setBoxSku('');
        setOverride(false);
        setQty(String(maxQty));
      }
    } catch (err) {
      setLocalErr(err?.message || String(err));
    }
  };

  if (!targets.length) {
    return (
      <div className="text-muted small mt-1 fst-italic">
        No same-customer pallets on other open plans — ride-along not available for this part.
      </div>
    );
  }

  return (
    <div className="border-start border-3 border-info ps-2 mt-1">
      <div className="d-flex align-items-center gap-1 flex-wrap small">
        <span className="badge bg-info text-dark me-1">
          <i className="bi bi-arrow-up-right-circle me-1"></i>Ride-along
        </span>
        <input
          type="number"
          className="form-control form-control-sm"
          style={{ maxWidth: 70 }}
          min={1}
          max={maxQty}
          value={qty}
          disabled={!!busy}
          onChange={(e) => setQty(e.target.value)}
          title={`Quantity to ride-along (max ${maxQty})`}
        />
        <span className="text-muted">→</span>
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 360 }}
          value={dest}
          disabled={!!busy}
          onChange={(e) => setDest(e.target.value)}
        >
          <option value="">Pick another SO's pallet…</option>
          {targets.map(t => {
            const cap = Math.round(t.max_pallet_weight_lbs || 0);
            const cur = Math.round(t.current_weight_lbs || 0);
            const room = cap > 0 ? `${cur} / ${cap} lb` : `~${cur} lb`;
            return (
              <option key={t.pallet_id} value={t.pallet_id}>
                {t.order_number} · {t.short_id || `P${t.pallet_num}`} · {t.box_count} box(es) · {room} · {t.status}
              </option>
            );
          })}
        </select>
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 130 }}
          value={asPalletDirect ? 'stack' : 'box'}
          disabled={!!busy}
          onChange={(e) => setAsPalletDirect(e.target.value === 'stack')}
        >
          <option value="stack">As stack (loose)</option>
          <option value="box">As box (carton)</option>
        </select>
        {!asPalletDirect && (
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: 100 }}
            placeholder="Box SKU"
            value={boxSku}
            disabled={!!busy}
            onChange={(e) => setBoxSku(e.target.value)}
            title="e.g. S-4827"
          />
        )}
        <button
          className="btn btn-sm btn-info"
          disabled={!!busy || !dest || !qty || parseInt(qty, 10) < 1 || (!asPalletDirect && !boxSku)}
          onClick={handleClick}
        >
          {busy === key
            ? <><span className="spinner-border spinner-border-sm me-1"></span>Sending…</>
            : <><i className="bi bi-send me-1"></i>Send</>}
        </button>
      </div>
      {dest && palletCap > 0 && (
        <div className={`small mt-1 ${overCap ? 'text-warning' : 'text-muted'}`}>
          Projected dest weight: <strong>{Math.round(projected)} lb</strong> / cap {Math.round(palletCap)} lb
          {overCap && (
            <>
              <span className="ms-2 badge bg-warning text-dark">
                exceeds cap by {Math.round(projected - palletCap)} lb
              </span>
              <div className="form-check form-check-inline ms-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`ra_override_${part.part_id}`}
                  checked={override}
                  onChange={(e) => setOverride(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor={`ra_override_${part.part_id}`}>
                  Override
                </label>
              </div>
            </>
          )}
        </div>
      )}
      {localErr && <div className="text-danger small mt-1">{localErr}</div>}
    </div>
  );
};

// Per-row assign controls for a manual-handling part: qty input, destination
// picker, and a commit button. Local state so each row's typing doesn't
// force a re-render of the whole parent list.
const ManualAssignRow = ({ part, maxQty, destOptions, busy, onSubmit }) => {
  const [qty, setQty] = useState(String(maxQty));
  const [dest, setDest] = useState('');
  const key = `assign_${part.part_id}`;
  const handleClick = async () => {
    if (!dest) return;
    await onSubmit(part, dest, qty);
    setDest('');
    setQty(String(maxQty));
  };
  return (
    <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
      <input
        type="number"
        className="form-control form-control-sm"
        style={{ maxWidth: 70 }}
        min={1}
        max={maxQty}
        value={qty}
        disabled={!!busy}
        onChange={(e) => setQty(e.target.value)}
        title={`Quantity to assign (max ${maxQty})`}
      />
      <span className="text-muted small">→</span>
      <select
        className="form-select form-select-sm"
        style={{ maxWidth: 240 }}
        value={dest}
        disabled={!!busy || !destOptions.length}
        onChange={(e) => setDest(e.target.value)}
      >
        <option value="">Destination…</option>
        {destOptions.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <button
        className="btn btn-sm btn-success"
        disabled={!!busy || !dest || !qty || parseInt(qty, 10) < 1}
        onClick={handleClick}
      >
        {busy === key
          ? <><span className="spinner-border spinner-border-sm me-1"></span>Assigning...</>
          : <><i className="bi bi-plus-circle me-1"></i>Assign</>}
      </button>
    </div>
  );
};

// Staging queue — secondary-process parts (Laser→Brake/Tap/Fab) that carry no
// pallet. The laser slip tells the operator to STAGE FOR <station>; once the
// parts are back from the process, the office assigns each to a pallet here (as
// a new pallet-direct stack, an existing box/stack, or a brand-new pallet).
// Backed by `plan.staged` (load_plan recomputes it and drops parts off as they
// get assigned). Always actionable — no edit-mode gate — per "quick and easy".
const SOPlanStagingQueue = ({ plan, busy = '', onManualAssign, onCreatePallet, onInspectPart, onReopenPlan, onClearWip }) => {
  const NEW_PALLET = '__new_pallet__';
  const staged = plan.staged || [];
  const byStation = React.useMemo(() => {
    const m = new Map();
    for (const it of staged) {
      const st = it.next_station || 'Next station';
      if (!m.has(st)) m.set(st, []);
      m.get(st).push(it);
    }
    return m;
  }, [staged]);
  const destOptions = React.useMemo(() => {
    const opts = [];
    for (const p of (plan.pallets || [])) {
      if (p.status !== 'Building' || p.is_wip) continue;
      const pLabel = p.short_id || `P${p.pallet_num}`;
      opts.push({ value: `newstack:${p.pallet_id}`, pallet_id: p.pallet_id, as_pallet_direct: true, label: `${pLabel} — new stack` });
      for (const b of (p.boxes || [])) {
        if (b.status === 'Sealed' || isNested(b)) continue;
        const kind = b.is_pallet_direct ? 'S' : 'B';
        const displayNum = b.display_box_num ?? b.box_num;
        const suffix = b.is_pallet_direct ? ' (stack)' : (b.sku ? ` (${b.sku})` : '');
        opts.push({ value: `box:${b.box_id}`, box_id: b.box_id, label: `${pLabel} / ${b.short_id || `${kind}${displayNum}`}${suffix}` });
      }
    }
    return opts;
  }, [plan.pallets]);
  if (!staged.length) return null;
  const isComplete = plan.status === 'Complete';
  const totalQty = staged.reduce((a, it) => a + ((it.remaining_qty ?? it.quantity) || 0), 0);
  return (
    <div className="border border-warning rounded p-2 mb-3" style={{ background: '#fffaf0' }}>
      <div className="fw-bold mb-1" style={{ color: '#9a6700' }}>
        <i className="bi bi-hourglass-split me-1"></i>
        Staging — assign to a pallet
        <span className="badge bg-warning text-dark ms-2">{staged.length} part{staged.length === 1 ? '' : 's'} · {totalQty} pcs</span>
      </div>
      <div className="small text-muted mb-2">
        Laser-cut parts that go to a secondary process next. The laser slip prints
        <strong> STAGE FOR &lt;station&gt;</strong> for each: on the laser task's WIP pallet when it made
        one, otherwise with no pallet. Once they're back, pick a pallet below — no edit mode needed. Assigned parts drop off this list; partially-packed
        parts show only what's left to assign.
        {staged.some(it => (it.on_wip || []).length > 0) && (
          <> Parts on a laser task's WIP pallet show it under their row: assigning them takes the pieces
          already cut off that pallet (pieces still being cut stay claimed for it), and an emptied WIP pallet
          ends (pull its beacon).</>
        )}
      </div>
      {isComplete && (
        <div className="alert alert-warning py-2 small mb-2 d-flex align-items-center gap-2 flex-wrap">
          <i className="bi bi-lock-fill"></i>
          <span>
            This plan is marked <strong>Complete</strong>, so assignments are locked.
            Reopen it to palletize these parts — it re-completes automatically once
            the new pallet(s) are staged and picked up / shipped.
          </span>
          {onReopenPlan && (
            <button className="btn btn-sm btn-warning" disabled={!!busy} onClick={onReopenPlan}>
              {busy === 'reopen_plan'
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Reopening…</>
                : <><i className="bi bi-unlock me-1"></i>Reopen plan</>}
            </button>
          )}
        </div>
      )}
      {Array.from(byStation.entries()).map(([station, items]) => (
        <div key={station} className="mb-2">
          <div className="small fw-semibold mb-1">
            <span className="badge bg-warning text-dark me-1">→ {String(station).toUpperCase()}</span>
            {items.length} part{items.length === 1 ? '' : 's'}
          </div>
          {items.map((it) => (
            isComplete
              ? (
                <div key={it.part_id} className="d-flex align-items-center gap-2 mt-1 small text-muted flex-wrap">
                  <span style={{ minWidth: 120 }}>
                    <ProductIdLink productId={it.product_id} partId={it.part_id} onInspect={onInspectPart} />
                  </span>
                  <span className="text-truncate" style={{ maxWidth: 260 }}>{it.description || ''}</span>
                  <span>× {(it.remaining_qty ?? it.quantity) || 0}</span>
                  <SOStagingOnWip onWip={it.on_wip || []} busy={busy} onClearWip={onClearWip} />
                </div>
              )
              : (
                <SOStagingAssignRow
                  key={it.part_id}
                  part={it}
                  destOptions={destOptions}
                  newPalletValue={NEW_PALLET}
                  busy={busy}
                  onManualAssign={onManualAssign}
                  onCreatePallet={onCreatePallet}
                  onInspectPart={onInspectPart}
                  onClearWip={onClearWip}
                />
              )
          ))}
        </div>
      ))}
    </div>
  );
};

const SOPlanManualHandling = ({ plan, editMode = false, busy = '', onManualAssign, onRideAlongAssign, crossSoTargets = [], boxCatalog, onInspectPart }) => {
  // Pallet-direct parts are now on the pallet map (as hatched/dashed
  // "DIRECT" items) and in the box list as dashed sub-cards, so we no
  // longer duplicate them here. This section shows only items the plan
  // cannot route automatically — and within THAT set, only items the
  // operator still needs to assign (we subtract any qty that's already
  // been moved into a box/stack so the list gets shorter as work
  // progresses instead of staying static and confusing).
  const ovRaw = plan.oversized || [];
  const nlRaw = plan.non_laser || [];
  if (!ovRaw.length && !nlRaw.length) return null;

  // Sum planned_qty across every zone in the plan, keyed by part_id.
  // Used to compute "remaining = ordered - assigned" for each row AND
  // to hide rows where remaining is zero. Includes outgoing ride-along
  // boxes — those still belong to this plan (plan_id matches) but their
  // physical pallet is on another plan, so they're excluded from
  // plan.pallets. Without this, parts hand-assigned via ride-along
  // would show inflated remaining qty and could be over-committed.
  const assignedByPart = new Map();
  for (const p of (plan.pallets || [])) {
    for (const b of (p.boxes || [])) {
      for (const z of (b.zones || [])) {
        if (!z.part_id) continue;
        assignedByPart.set(z.part_id, (assignedByPart.get(z.part_id) || 0) + (z.planned_qty || 0));
      }
    }
  }
  for (const b of (plan.outgoing_ride_alongs || [])) {
    for (const z of (b.zones || [])) {
      if (!z.part_id) continue;
      assignedByPart.set(z.part_id, (assignedByPart.get(z.part_id) || 0) + (z.planned_qty || 0));
    }
  }
  const enrich = (it) => {
    const assigned = assignedByPart.get(it.part_id) || 0;
    const ordered = it.quantity || 0;
    return {
      ...it,
      assigned_qty: assigned,
      ordered_qty: ordered,
      remaining_qty: Math.max(0, ordered - assigned),
    };
  };
  const ov = ovRaw.map(enrich).filter(it => it.remaining_qty > 0);
  const nl = nlRaw.map(enrich).filter(it => it.remaining_qty > 0);
  if (!ov.length && !nl.length) return null;
  // Destinations for manual-assign: every existing Building-pallet box/stack
  // (append a new zone), plus "New stack on P#" / "New box on P#" virtual
  // options per Building pallet. The row renderer turns the virtual options
  // into a two-step call (create empty stack/box, then manual-assign into it).
  // For v1 we only expose "existing destinations" so there's one API call
  // per action. Operators can use the "+ New box / stack" picker on a pallet
  // first, then assign into the newly-created destination.
  const destOptions = [];
  for (const p of (plan.pallets || [])) {
    if (p.status !== 'Building') continue;
    for (const b of (p.boxes || [])) {
      if (b.status === 'Sealed' || isNested(b)) continue;
      const kind = b.is_pallet_direct ? 'S' : 'B';
      const suffix = b.is_pallet_direct
        ? ' (stack)'
        : (b.sku ? ` (${b.sku})` : '');
      const sidTail = b.short_id ? ` · ${b.short_id}` : '';
      const displayNum = b.display_box_num ?? b.box_num;
      destOptions.push({
        value: `box:${b.box_id}`,
        box_id: b.box_id,
        pallet_id: p.pallet_id,
        as_pallet_direct: !!b.is_pallet_direct,
        label: `${p.short_id || `P${p.pallet_num}`} / ${b.short_id || `${kind}${displayNum}`}${suffix}`,
      });
    }
    destOptions.push({
      value: `new_direct:${p.pallet_id}`,
      box_id: null,
      pallet_id: p.pallet_id,
      as_pallet_direct: true,
      label: `${p.short_id || `P${p.pallet_num}`} · New pallet-direct stack`,
    });
  }
  const handleAssign = async (it, destValue, qty) => {
    if (!destValue) return;
    const opt = destOptions.find(o => o.value === destValue);
    if (!opt) return;
    // Cap at what's still unassigned, not at the full order qty.
    // remaining_qty is set by SOPlanManualHandling's enrich().
    const maxQty = it.remaining_qty ?? it.quantity ?? 0;
    const body = {
      part_id: it.part_id,
      quantity: Math.min(Math.max(parseInt(qty, 10) || 0, 1), maxQty),
    };
    if (opt.box_id) {
      body.dest_box_id = opt.box_id;
    } else {
      body.dest_pallet_id = opt.pallet_id;
      body.as_pallet_direct = opt.as_pallet_direct;
    }
    await onManualAssign(body);
  };
  const renderAssignControls = (it, idx, keyPrefix) => {
    if (!editMode || !it.part_id) return null;
    // Cap the qty input at whatever's left to assign — not the original
    // ordered qty — so an operator can't over-commit in a single click
    // after a partial prior assignment. The backend over-ship guard is
    // the backstop if this ever drifts.
    const maxQty = it.remaining_qty || 0;
    if (maxQty <= 0) return null;
    const hasCrossSoTargets = !!(onRideAlongAssign && crossSoTargets.length);
    return (
      <React.Fragment key={`${keyPrefix}_${idx}_ctrl`}>
        <ManualAssignRow
          part={it}
          maxQty={maxQty}
          destOptions={destOptions}
          busy={busy}
          onSubmit={handleAssign}
        />
        {/* Ride-along is a SECOND option per row: send the same part to
            another SO's pallet (same customer) instead of putting it on
            this SO's plan. Surfaces only when there are valid cross-SO
            targets so we don't show a dead-end "no destinations" row. */}
        {hasCrossSoTargets && (
          <RideAlongAssignRow
            part={it}
            maxQty={maxQty}
            crossSoTargets={crossSoTargets}
            busy={busy}
            onSubmit={onRideAlongAssign}
            planMaxPalletWeightLbs={plan?.max_pallet_weight_lbs}
          />
        )}
      </React.Fragment>
    );
  };
  return (
    <div className="mt-3">
      <div className="fw-bold mb-2"><i className="bi bi-hand-index me-1"></i>Manual handling</div>
      {editMode && !destOptions.length && (
        <div className="alert alert-info py-2 small">
          No Building-pallet destinations available. Add a pallet or an empty
          box / stack first, then assign parts to it.
        </div>
      )}
      {ov.length > 0 && (
        <div className="mb-2">
          <div className="small fw-bold text-danger">
            <i className="bi bi-exclamation-triangle me-1"></i>Oversized
          </div>
          <ul className="small mb-0 list-unstyled">
            {ov.map((it, i) => (
              <li key={`ov_${i}`} className="py-1">
                <div>
                  <ProductIdLink productId={it.product_id} partId={it.part_id} onInspect={onInspectPart} />
                  {' — qty '}<strong>{it.remaining_qty}</strong>
                  {it.assigned_qty > 0 && (
                    <span className="text-muted ms-1">({it.assigned_qty} of {it.ordered_qty} already assigned)</span>
                  )}
                  {' — '}{it.length}″ × {it.width}″
                  {it.reason ? <em className="text-muted"> — {it.reason}</em> : null}
                </div>
                {renderAssignControls(it, i, 'ov')}
              </li>
            ))}
          </ul>
        </div>
      )}
      {nl.length > 0 && (
        <div className="mb-2">
          <div className="small fw-bold text-warning">
            <i className="bi bi-tools me-1"></i>Non-laser parts — manual packing
          </div>
          <ul className="small mb-0 list-unstyled">
            {nl.map((it, i) => (
              <li key={`nl_${i}`} className="py-1">
                <div>
                  <ProductIdLink productId={it.product_id} partId={it.part_id} onInspect={onInspectPart} />
                  {' — qty '}<strong>{it.remaining_qty}</strong>
                  {it.assigned_qty > 0 && (
                    <span className="text-muted ms-1">({it.assigned_qty} of {it.ordered_qty} already assigned)</span>
                  )}
                  {' — process: '}{it.process_steps || 'unknown'}
                </div>
                {renderAssignControls(it, i, 'nl')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export { SOPackagingPlanCard as PackageGuideCard };
export default SOPackagingPlanCard;
